
export const translations = {
  en: {
    ".": ".",
    appName: "OneClickSell",
    trustAppName: "OneClickSell Trust",

    // Navigation
    nav: {
      features: "Features",
      pricing: "Pricing",
      faq: "FAQ",
      login: "Log In",
      startFree: "Start Free"
    },
    common: {
      ok: "OK",
      cancel: "Cancel",
      confirm: "Confirm",
      notice: "Notice",
      backToWorkspace: "Back to Workspace",
      backHome: "Back Home"
    },
    appError: {
      title: "Unexpected error",
      subtitle: "You can refresh the page or return to Task Center to continue.",
      backTaskCenter: "Back to Task Center",
      refresh: "Refresh",
      copy: "Copy error"
    },

    theme: {
      light: "Light",
      dark: "Dark",
      switchToLight: "Switch to light mode",
      switchToDark: "Switch to dark mode"
    },

    appNav: {
      more: "More",
      loginHint: "Log in to access this feature.",
      unlockHint: "Complete an import to unlock.",
      comingSoonBadge: "Coming soon",
      extensionConnected: "Extension connected",
      extensionMissing: "Extension not detected",
      extensionOffline: "Extension offline",
      extensionUpgradeRequired: "Extension update required",
      extensionUpgradeDetail: "Current {current}, required {required}",
      extensionVersionUnknown: "Unknown",
      extensionTakeoverStatusLabel: "Automatic takeover status",
      extensionAwaitingSync: "Awaiting site takeover",
      extensionAwaitingSyncDetail: "Installed successfully. Keep this page open and the extension will sync automatically.",
      extensionStatusChecking: "Checking extension status",
      extensionStatusCheckingDetail: "The extension is installed. We are confirming takeover and backend readiness now.",
      extensionBackendUnreachable: "Backend unreachable",
      extensionBackendUnreachableDetail: "The extension is installed, but it cannot reach the current backend.",
      extensionReadyPolling: "Auto polling ready",
      extensionReadyPollingDetail: "Site takeover completed. Waiting for backend tasks to start collecting automatically.",
      extensionCollecting: "Auto collecting",
      extensionCollectingDetail: "The extension is collecting product data in the background.",
      extensionConnecting: "Connecting extension",
      extensionConnectingDetail: "Site takeover completed. Establishing extension connection now.",
      extensionDownload: "Download extension",
      extensionUpdate: "Update extension",
      extensionDownloadGuide: "Opening extension download...",
      assetsComingSoon: "Asset Library is under development: manage generated/uploaded images, copy, and listing records with search, reuse, and cross-platform sync.",
      copyrightComingSoon: "Copyright Assets is under development: watermark/PSD fingerprinting, evidence bundles, and dispute templates to protect your creative assets."
    },
    extensionGuide: {
      badge: "Beginner-friendly setup",
      title: "Let's install the Chrome extension step by step",
      subtitle: "No technical background needed. Follow each step and we'll verify automatically.",
      resumeHint: "We restored your previous progress. You can continue from here.",
      progress: "Step {current} / {total}",
      sources: {
        header: "Opened from top navigation",
        workspace: "Opened from workspace",
        import: "Opened from import page"
      },
      steps: {
        intro: {
          title: "Welcome! This takes about 2 minutes",
          desc: "The extension helps us capture product data safely from supplier pages. You only need to install it once.",
          tip: "Tip: keep this guide open and follow each button below."
        },
        download: {
          title: "Step 1: Download the extension ZIP package",
          desc: "Click the download button below. After downloading, keep the ZIP file in an easy-to-find folder like Desktop.",
          tip: "If your browser asks where to save, choose a folder you can find quickly."
        },
        unzip: {
          title: "Step 2: Unzip the downloaded file",
          desc: "Find the ZIP file, right-click it, and choose extract/unzip. You should get a normal folder after this step.",
          tip: "Do not select the ZIP file later. Chrome needs the extracted folder."
        },
        openExtensions: {
          title: "Step 3: Open Chrome extensions page",
          desc: "Click the button below to open chrome://extensions/. Keep this guide open in the original tab.",
          tip: "If Chrome blocks opening the page, copy and paste chrome://extensions/ into the address bar."
        },
        loadUnpacked: {
          title: "Step 4: Enable Developer mode and load folder",
          desc: "On the extensions page, turn on Developer mode, click “Load unpacked”, then choose the extracted folder from Step 2.",
          tip: "After loading, you should see this extension card in the list."
        },
        verify: {
          title: "Step 5: We are checking installation status",
          desc: "Stay on this page. We will detect your extension automatically. If needed, you can retry with one click.",
          tip: "If detection times out, go back to Step 4 and check whether the selected folder is correct."
        },
        done: {
          title: "Done! Extension is ready",
          desc: "Great job. The extension is installed and connected.",
          tip: "You can now continue importing products."
        }
      },
      help: {
        title: "Need help?",
        show: "Show tips",
        hide: "Hide tips",
        intro1: "Do not worry if this is your first time. Every step has a button guide.",
        intro2: "If you close the guide by mistake, click download/update again to reopen it.",
        download1: "If download does not start, check whether your browser blocked popups.",
        download2: "You can click “I already downloaded it” if your file is ready.",
        unzip1: "macOS: usually double-click the ZIP file to unzip.",
        unzip2: "Windows: right-click ZIP → Extract All.",
        openExtensions1: "Chrome address must be exactly chrome://extensions/.",
        openExtensions2: "You can keep this guide in one tab and extension page in another tab.",
        loadUnpacked1: "Developer mode switch is usually at top-right of chrome://extensions/.",
        loadUnpacked2: "If Chrome shows an error, choose the extracted folder again, not the parent folder.",
        verify1: "Detection runs every 1.5 seconds automatically.",
        verify2: "If your version is too old, click “Download latest package” and reinstall.",
        done1: "If import still fails later, click the extension status icon in the header to recheck.",
        done2: "You can reopen this guide anytime from Workspace or Import page."
      },
      actions: {
        close: "Close",
        prev: "Back",
        start: "Start now",
        next: "Next",
        downloadZip: "Download ZIP package now",
        downloadDone: "I already downloaded it",
        openChromeExtensions: "Open chrome://extensions/",
        backToExtensionsPage: "Back to extension page",
        retryDetect: "Retry detection",
        downloadUpdate: "Download latest package"
      },
      status: {
        ready: "Extension connected successfully",
        needUpgrade: "Current version {current}. Please update to {required} or newer.",
        unknownVersion: "unknown",
        authRequired: "Extension installed. Waiting for site takeover.",
        backendUnreachable: "Extension installed, but backend is unreachable.",
        readyPolling: "Extension is ready and waiting for backend tasks.",
        collecting: "Extension is collecting automatically.",
        connecting: "Extension is connecting to the backend...",
        connectedWaiting: "Extension found. Finalizing checks...",
        offline: "Extension appears offline. Please refresh Chrome and retry.",
        verifyTimeout: "Detection timed out. Please check Step 4 and retry.",
        detecting: "Checking extension status..."
      },
      verify: {
        successHint: "Everything looks good. Moving to completion.",
        upgradeHint: "You are connected, but this version is too old for current features.",
        authRequiredHint: "Installation is complete. Once this site finishes automatic takeover, the extension will start polling tasks on its own.",
        backendUnreachableHint: "Please confirm the current backend is running and reachable, then retry detection.",
        readyPollingHint: "Automatic takeover is complete. The extension is waiting for backend tasks.",
        collectingHint: "Great. The extension is already collecting a task in the background.",
        connectingHint: "The extension has received site credentials and is establishing its background connection.",
        timeoutHint: "No worries. Click retry, or go back one step and load the folder again.",
        detectingHint: "Auto-detection in progress (attempt {attempts})..."
      },
      doneDesc: "Installed version: {version} · Minimum required: {required}. You can continue now.",
      doneActions: {
        workspace: "Go to import products",
        import: "Start importing now",
        header: "Continue current task"
      }
    },

    admin: {
      title: "Admin Console",
      subtitle: "Operations & Controls",
      backToWorkspace: "Back to Workspace",
      login: {
        title: "Admin Login",
        subtitle: "Use your admin credentials to continue.",
        checking: "Checking admin session...",
        username: "Username",
        usernamePlaceholder: "Enter admin username",
        password: "Password",
        passwordPlaceholder: "Enter admin password",  // pragma: allowlist secret
        action: "Log In",
        loggingIn: "Logging in...",
        required: "Username and password are required.",
        failed: "Login failed.",
        denied: "Access denied.",
        logout: "Log Out"
      },
      nav: {
        overview: "Overview",
        aiRuntime: "AI Runtime",
        aiChannels: "AI Channels",
        aiMonitor: "Channel Monitor",
        aiUsage: "AI Usage",
        marketing: "Marketing Analytics",
        billing: "Billing & Credits",
        consumption: "Credit Ledger",
        users: "Users",
        logs: "Admin Logs",
        settings: "System Settings"
      },
      marketing: {
        title: "Marketing Analytics",
        desc: "Track PV, engagement, CTA funnel, and UTM attribution performance.",
        loading: "Loading marketing analytics...",
        error: "Failed to load marketing analytics.",
        empty: "No marketing data for the selected filters.",
        apply: "Apply",
        reset: "Reset",
        updatedAt: "Updated at {time}",
        cards: {
          pv: "Total PV",
          visitors: "Unique Visitors",
          engagement: "Avg Engagement",
          ctaClicks: "CTA Clicks",
          conversions: "Conversions",
          conversionRate: "Conversion Rate"
        },
        filters: {
          days: "Time Range",
          utmSource: "UTM Source",
          utmMedium: "UTM Medium",
          utmCampaign: "UTM Campaign",
          experimentId: "Experiment ID",
          experimentVariant: "Experiment Variant",
          pagePath: "Page Path"
        },
        business: {
          cards: {
            paidOrders: "Paid Orders",
            revenue: "Revenue",
            adCost: "Ad Cost",
            grossProfit: "Gross Profit",
            roas: "ROAS",
            roi: "ROI",
            aov: "Avg Order Value",
            cac: "Cost / Paid Order",
            cpa: "Cost / Conversion"
          }
        },
        trend: {
          title: "Daily PV/UV/Conversion Trend",
          engagementTitle: "Daily Avg Engagement (seconds)",
          series: {
            pv: "PV",
            uv: "UV",
            conversions: "Conversions",
            engagement: "Avg Engagement (s)"
          }
        },
        funnel: {
          title: "CTA Funnel",
          view: "View",
          ctaClick: "CTA Click",
          conversion: "Conversion"
        },
        utm: {
          title: "UTM Attribution"
        },
        experiment: {
          title: "Experiment Attribution",
          empty: "No experiment attribution data under current filters.",
          columns: {
            id: "Experiment",
            variant: "Variant",
            pv: "PV",
            uv: "UV",
            conversions: "Conv",
            paidOrders: "Paid Orders",
            revenue: "Revenue",
            adCost: "Ad Cost",
            roas: "ROAS",
            roi: "ROI",
            cvr: "CVR"
          }
        },
        pages: {
          title: "Page Breakdown",
          empty: "No page-level data under current filters.",
          summary: {
            highPriorityPages: "High Priority Pages",
            mediumPriorityPages: "Medium Priority Pages",
            lowPriorityPages: "Low Priority Pages",
            avgScore: "Avg Optimization Score"
          },
          priority: {
            high: "High",
            medium: "Medium",
            low: "Low"
          },
          signals: {
            HIGH_BOUNCE_RATE: "High bounce rate",
            LOW_CTA_RATE: "Low CTA click-through rate",
            LOW_CTA_TO_CONVERSION: "Low conversion after CTA click",
            HIGH_ENGAGEMENT_LOW_CONVERSION: "High engagement but low conversion",
            NO_ENGAGEMENT_EVENTS: "No engagement events tracked"
          },
          actions: {
            OPTIMIZE_ABOVE_THE_FOLD: "Optimize headline/value proposition above the fold",
            IMPROVE_CTA_VISIBILITY: "Increase CTA visibility and copy clarity",
            REDUCE_FORM_FRICTION: "Reduce form and checkout friction",
            ALIGN_CONTENT_AND_OFFER: "Align page content with offer and intent",
            VERIFY_ENGAGEMENT_TRACKING: "Verify engagement tracking instrumentation",
            ENRICH_TRAFFIC_QUALITY: "Improve traffic quality and targeting",
            SCALE_WINNING_PAGE: "Scale traffic to this winning page"
          },
          columns: {
            rank: "Rank",
            priority: "Priority",
            score: "Score",
            page: "Page",
            events: "Events",
            pv: "PV",
            uv: "UV (All)",
            uvAll: "UV (All)",
            uvPageView: "UV (PV)",
            entry: "Entry",
            exit: "Exit",
            bounce: "Bounce",
            engagement: "Avg Engagement",
            engagementEvents: "Engagement Events",
            ctaClicks: "CTA Clicks",
            ctaRate: "CTA Rate",
            conversions: "Conv",
            cvr: "CVR",
            cvrPv: "CVR (PV)",
            cvrFromCta: "CVR (CTA)",
            paidOrders: "Paid Orders",
            revenue: "Revenue",
            adCost: "Ad Cost",
            roas: "ROAS",
            roi: "ROI",
            aov: "AOV",
            cac: "Cost / Paid Order",
            cpa: "Cost / Conversion",
            signals: "Signals",
            actions: "Actions",
            topEvents: "Top Events"
          }
        },
        table: {
          source: "Source",
          medium: "Medium",
          campaign: "Campaign",
          pv: "PV",
          uv: "UV",
          conversions: "Conv",
          paidOrders: "Paid Orders",
          revenue: "Revenue",
          adCost: "Ad Cost",
          roas: "ROAS",
          cvr: "CVR"
        }
      },
      aiUsage: {
        title: "AI Usage Analytics",
        desc: "Track token usage and distribution by provider, call type and model",
        loading: "Loading AI usage data...",
        error: "Failed to load AI usage data.",
        empty: "No usage records found for the selected filters.",
        apply: "Apply",
        reset: "Reset",
        summary: "Period: {start} ~ {end}",
        cards: {
          totalCalls: "Total Calls",
          totalTokens: "Total Tokens",
          totalInputTokens: "Input Tokens",
          totalOutputTokens: "Output Tokens",
          imageCalls: "Image Calls",
          nonImageCalls: "Non-Image Calls",
          avgTokens: "Avg Tokens / Call",
          quantity: "Quantity"
        },
        distribution: {
          provider: "Provider Distribution",
          callType: "Call Type Distribution",
          model: "Model Distribution"
        },
        trend: {
          title: "Usage Trend"
        },
        cost: {
          title: "Cost Estimation",
          description: "Adjust the unit prices below to estimate external AI costs.",
          reset: "Reset defaults",
          inputPrice: "Input price (USD / 1M tokens)",
          outputPrice: "Output price (USD / 1M tokens)",
          imageCost: "Image cost (RMB / image)",
          exchangeRate: "USD/CNY",
          totalTokenCost: "Token cost",
          imageCostTitle: "Image cost",
          totalCost: "Estimated total cost",
          avgPerTask: "Avg cost / call",
          tokenPerNonImageCall: "Avg token cost / non-image call"
        },
        filters: {
          periodStart: "Start Time",
          periodEnd: "End Time",
          interval: "Trend Interval",
          all: "All",
          image: "Image Calls Only",
          yes: "Yes",
          no: "No",
          userId: "User ID",
          provider: "Provider",
          model: "Model",
          callType: "Call Type",
          action: "Action",
          pipelineStage: "Pipeline Stage",
          stage: "Stage",
          runId: "Run ID",
          callId: "Call ID"
        },
        intervals: {
          hour: "Hour",
          day: "Day",
          week: "Week",
          month: "Month"
        },
        table: {
          time: "Time",
          user: "User",
          runId: "Run ID",
          callId: "Call ID",
          action: "Action",
          provider: "Provider",
          model: "Model",
          callType: "Call Type",
          tokens: "Tokens",
          estimatedCost: "Est. Cost",
          image: "Image",
          metadata: "Metadata"
        },
        records: "Usage Records",
        pagination: "Page {current} / {total}",
        prev: "Previous",
        next: "Next"
      },
      navGroups: {
        overview: "Overview",
        ai: "AI Console",
        operations: "Operations",
        system: "System"
      },
      access: {
        checking: "Verifying admin access...",
        loginTitle: "Please log in to continue",
        loginDesc: "Admin console requires authentication.",
        loginAction: "Log In",
        deniedTitle: "Access denied",
        deniedDesc: "Your account does not have admin permissions."
      },
      listStates: {
        loading: {
          title: "Syncing list data",
          description: "Please wait while we fetch the latest records."
        },
        error: {
          title: "Unable to load list",
          description: "Retry to get the latest list data.",
          detailPrefix: "Error detail: "
        },
        empty: {
          title: "No data to display",
          description: "Try adjusting filters or refresh and check again."
        },
        actions: {
          retry: "Retry",
          retrying: "Retrying...",
          refresh: "Refresh data",
          backToOverview: "Back to overview"
        }
      },
      overview: {
        title: "Dashboard",
        desc: "Key metrics and recent trends",
        loading: "Loading overview data...",
        error: "Failed to load overview data.",
        empty: "No data yet.",
        userTrend: "New users in last 7 days",
        revenueTrend: "Revenue in last 7 days",
        cards: {
          totalUsers: "Total Users",
          activeToday: "Active Today",
          newToday: "New Today",
          totalRevenue: "Total Revenue",
          revenueMonth: "Revenue This Month",
          totalConsumption: "Total Consumption"
        },
        units: {
          users: "Users",
          usd: "USD",
          credits: "Credits"
        }
      },
      aiChannels: {
        title: "AI Channel Management",
        desc: "Manage platform AI providers and routing",
        error: "Failed to load channels.",
        testResult: {
          success: "Test passed",
          failed: "Test failed",
          successMessage: "Channel test succeeded.",
          failureUnknown: "Test failed. Please check error details below.",
          failureReason: "Failure reason",
          checkedAt: "Checked at {time}"
        },
        validationName: "Channel name is required.",
        validationKey: "API Key is required.",
        validationBaseUrl: "Base URL is required for non-official channels.",
        validationExpiresAtRequired: "Please set a valid expiry time or choose permanent validity.",
        validationExpiresAtInvalid: "Expiry time format is invalid.",
        validationVertexProjectLocation: "Vertex AI channels require both Project and Location.",
        validationVertexProjectLocationPair: "GCP Project and Location must both be filled or both left empty.",
        enableConfirm: "Enable channel {name}?",
        disableConfirm: "Disable channel {name}?",
        testConfirm: "Test channel {name}?",
        updated: "Channel updated.",
        created: "Channel created.",
        deleted: "Channel deleted.",
        tested: "Test completed.",
        deleteConfirm: "Delete channel {name}?",
        officialBaseUrl: "Official Base URL: {url}",
        vertexBaseUrlHint: "Vertex AI provider does not require Base URL.",
        vertexAuthHint: "Vertex AI supports API Key mode directly. Project/Location are optional and must be entered together.",
        hints: {
          neverExpires: "Current channel is permanently valid."
        },
        refresh: "Refresh",
        updatedAt: "Updated at {time}",
        edit: "Edit Channel",
        create: "Create Channel",
        newChannel: "New Channel",
        cancel: "Cancel",
        formHint: "Keep API Key empty when editing to preserve current key.",
        searchPlaceholder: "Search by name / base URL",
        filters: {
          all: "All channels",
          enabled: "Enabled only",
          disabled: "Disabled only",
          official: "Official only",
          custom: "Custom only",
          degraded: "Degraded only"
        },
        summary: {
          total: "Total Channels",
          enabled: "Enabled",
          degraded: "Degraded",
          requests: "24h Requests",
          failureRate: "24h Failure Rate"
        },
        providers: {
          developerAPI: "Gemini Developer API",
          vertexAI: "Vertex AI",
          googleOfficial: "Google Official",
          customProxy: "Custom Proxy"
        },
        fields: {
          name: "Channel Name",
          apiKey: "API Key",  // pragma: allowlist secret
          provider: "Provider",
          baseUrl: "Base URL",
          gcpProject: "GCP Project",
          gcpLocation: "GCP Location",
          tier: "Tier",
          priority: "Priority",
          creditsPerImage: "Credits per image",
          expiresAt: "Expires At",
          maxFail: "Max Fail Count",
          recovery: "Recovery Time (s)",
          description: "Description",
          enabled: "Enabled",
          isOfficial: "Official",
          neverExpires: "Permanent"
        },
        placeholders: {
          name: "e.g. Gemini-Primary",
          apiKey: "Paste API key",  // pragma: allowlist secret
          apiKeyEdit: "Leave blank to keep current key",  // pragma: allowlist secret
          baseUrl: "https://api.example.com",
          gcpProject: "e.g. my-gcp-project",
          gcpLocation: "e.g. us-central1",
          creditsPerImage: "e.g. 5 (optional)",
          description: "Usage notes"
        },
        save: "Save",
        saving: "Saving...",
        listTitle: "Channel List",
        loading: "Loading channels...",
        empty: "No channels configured.",
        statusUnknown: "Unknown",
        table: {
          name: "Channel",
          status: "Status",
          priority: "Priority",
          creditsPerImage: "Credits/Image",
          baseUrl: "Base URL",
          requests: "24h Requests",
          failRate: "24h Failure Rate",
          updatedAt: "Updated At",
          avgLatency: "Avg Latency",
          validUntil: "Valid Until",
          permanent: "Permanent",
          expired: "Expired",
          actions: "Actions",
          scrollHint: "Scroll horizontally to view all columns."
        },
        actions: {
          edit: "Edit",
          disable: "Disable",
          enable: "Enable",
          enabling: "Enabling...",
          disabling: "Disabling...",
          test: "Test",
          testing: "Testing...",
          delete: "Delete",
          deleting: "Deleting..."
        }
      },
      aiRuntime: {
        title: "AI Runtime Configuration",
        desc: "Configure model + thinking + channel strategy per AI module",
        quickPolicyTitle: "Quick Routing Policy",
        quickPolicyDesc: "One switch to force all analysis modules to official channels. Image generation still follows user mode.",
        manageChannels: "Manage channels",
        analysisOfficialOnlyLabel: "Force official channels for analysis modules",
        analysisOfficialOnlyHintOn:
          "Enabled: all analysis modules are forced to official channels; only image generation can use Value mode.",
        analysisOfficialOnlyHintOff:
          "Disabled: analysis modules follow the global/module channel strategy configured below.",
        enabledOfficialChannels: "Enabled official channels: {count}",
        enabledThirdPartyChannels: "Enabled third-party channels: {count}",
        loading: "Loading runtime config...",
        error: "Failed to load runtime config.",
        saved: "Runtime config updated.",
        save: "Save Configuration",
        saving: "Saving...",
        reset: "Reset unsaved changes",
        saveConfirm: "You are about to save runtime configuration and apply to current environment. Continue?",
        summaryTitle: "Runtime Overview",
        statusDirty: "Unsaved changes",
        statusSaved: "Saved",
        global: "Global Default",
        globalHint: "Defaults are inherited by all modules unless overridden.",
        modules: "Module Overrides",
        channelsHint: "When strategy is specific channels, failover stays in selected channels.",
        emptyChannels: "No channels available. Configure channels first.",
        effectiveConfig: "Effective Runtime Policy",
        copyGlobal: "Copy global settings",
        searchResultCount: "{count} modules shown",
        groups: {
          all: "All",
          extract: "Extract",
          content: "Content",
          image: "Image"
        },
        summary: {
          total: "Total Modules",
          enabled: "Enabled",
          override: "With Override",
          specific: "Specific Channels"
        },
        fields: {
          model: "Model",
          thinking: "Thinking",
          strategy: "Channel Strategy",
          channels: "Channels",
          enabled: "Enabled"
        },
        hints: {
          globalModel: "Model inherited by modules when no module-level override is set.",
          globalThinking: "Default thinking level used as fallback for all modules.",
          globalStrategy: "Set default channel strategy when module override is empty.",
          globalChannels: "Only takes effect when channel strategy is set to specific channels.",
          moduleModel: "Set module-level model override; leave empty to inherit global default.",
          moduleThinking: "Set module-level thinking level; leave empty to inherit global default.",
          moduleStrategy: "Module-specific strategy for request routing behavior.",
          moduleChannels: "When strategy is specific channels, only selected channels are used.",
          moduleEnabled: "Enable or disable this module from taking requests."
        },
        strategy: {
          auto: "Auto",
          official_only: "Official Only",
          specific_channels: "Specific Channels"
        },
        inherit: "Inherit global",
        moduleSearch: "Search modules",
        moduleCount: "{count} modules",
        selectedModule: "Selected module"
      },
      aiMonitor: {
        title: "AI Channel Monitor",
        desc: "Runtime status and routing health",
        loading: "Loading runtime stats...",
        error: "Failed to load channel stats.",
        refreshing: "Refreshing...",
        refresh: "Refresh",
        autoRefreshOn: "Auto refresh: On",
        autoRefreshOff: "Auto refresh: Off",
        autoRefresh: "Auto Refresh",
        updatedAt: "Updated at {time}",
        current: "Current Channel",
        total: "Total Channels",
        healthy: "Healthy Channels",
        requests: "24h Requests",
        failureRate: "Failure Rate",
        avgLatency: "Avg Latency",
        requestUnit: "req",
        percentUnit: "%",
        msUnit: "ms",
        trend: "Traffic Trend",
        last24h: "Last 24h",
        noTrend: "No trend data",
        errors: "Recent Errors",
        errorsDesc: "Latest failures in last 24h",
        noErrors: "No recent errors",
        noErrorMessage: "No message",
        expand: "Expand",
        collapse: "Collapse",
        none: "None",
        empty: "No runtime data.",
        statusUnknown: "Unknown",
        yes: "Yes",
        no: "No",
        table: {
          name: "Channel",
          status: "Status",
          priority: "Priority",
          enabled: "Enabled",
          available: "Available",
          successRate: "Success Rate",
          failCount: "Fail Count",
          avgLatency: "Avg Latency"
        }
      },
      billing: {
        title: "Credits & Billing",
        desc: "Adjust credits and run manual recharges",
        error: "Failed to load credit users.",
        creditsBreakdown: "Sub {subscription} / Recharge {recharge} / Legacy {legacy}",
        creditUnit: "credits",
        actionTitle: "Manual credit action",
        validationUser: "User ID is required.",
        defaultReason: "Admin adjustment",
        rechargeReason: "Admin recharge",
        actionSuccess: "Action completed.",
        submitting: "Submitting...",
        submit: "Submit",
        listTitle: "User Credits",
        search: "Search",
        loading: "Loading credit users...",
        empty: "No users found.",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        fields: {
          userId: "User ID",
          actionType: "Action",
          reason: "Reason",
          credits: "Credits",
          amount: "Amount",
          bonus: "Bonus"
        },
        placeholders: {
          userId: "Select from list or paste ID",
          reason: "Optional reason",
          search: "Search user"
        },
        actions: {
          adjust: "Adjust Credits",
          recharge: "Recharge",
          select: "Select"
        },
        chart: {
          title: "Billing order trend",
          desc: "Daily paid/pending/issue order comparison",
          loading: "Loading billing trend...",
          error: "Failed to load billing trend.",
          empty: "No billing trend data yet.",
          orderUnit: "orders",
          series: {
            paid: "Paid",
            pending: "Pending",
            issue: "Issues"
          }
        },
        table: {
          user: "User",
          plan: "Plan",
          credits: "Credits",
          trial: "Trial",
          actions: "Actions"
        }
      },
      consumption: {
        title: "Credit Ledger",
        desc: "Audit recharges, consumption, promotions, refunds, and admin adjustments",
        loading: "Loading credit ledger...",
        error: "Failed to load credit ledger.",
        empty: "No records found.",
        creditUnit: "credits",
        search: "Search",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholders: {
          userId: "Filter by user ID"
        },
        chart: {
          title: "Consumption trend",
          desc: "Daily credits consumption vs recharge",
          loading: "Loading consumption trend...",
          error: "Failed to load consumption trend.",
          empty: "No consumption trend data yet.",
          series: {
            consumed: "Consumed credits",
            recharged: "Recharged credits"
          }
        },
        table: {
          user: "User",
          createdAt: "Created At"
        }
      },
      ledger: {
        placeholders: {
          search: "Search by phone / email / username / description"
        },
        filters: {
          allTypes: "All types",
          allBuckets: "All buckets"
        },
        types: {
          recharge: "Recharge",
          consumption: "Consumption",
          subscription: "Subscription Grant",
          adjustment: "Admin Adjustment",
          promo: "Promotion",
          refund: "Refund",
          trialGrant: "Trial Grant"
        },
        buckets: {
          subscription: "Subscription",
          recharge: "Recharge",
          legacy: "Legacy"
        },
        table: {
          type: "Type",
          bucket: "Bucket",
          change: "Change",
          balance: "Balance",
          reference: "Reference",
          money: "Amount/Cost"
        }
      },
      users: {
        title: "User Management",
        desc: "Update status, plans, and admin flags",
        error: "Failed to load users.",
        filters: "Filters",
        search: "Search",
        filtersAllStatus: "All status",
        filtersAllAdmin: "All users",
        filtersAdmin: "Admins only",
        filtersNonAdmin: "Non-admins",
        editorTitle: "Edit user",
        detailTitle: "User details",
        closeEditor: "Close",
        adminToggle: "Admin access",
        saved: "Changes saved.",
        saving: "Saving...",
        save: "Save changes",
        discardChanges: "Discard",
        discardSuccess: "Changes discarded.",
        listTitle: "Users",
        loading: "Loading users...",
        empty: "No users found.",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholderEmpty: "-",
        compareTitle: "Pending changes",
        noChanges: "No pending changes.",
        riskConfirmTitle: "High-risk changes detected",
        riskConfirm: "The following high-risk changes require confirmation:",
        saveError: "Save failed: {error}",
        unknownError: "Unknown error",
        adminStateEnabled: "Enabled",
        adminStateDisabled: "Not admin",
        fields: {
          status: "Status",
          plan: "Plan",
          admin: "Admin",
          notes: "Notes"
        },
        detailLabels: {
          userId: "User ID",
          phone: "Phone",
          email: "Email",
          plan: "Plan",
          status: "Status",
          credits: "Credits",
          trial: "Trial",
          createdAt: "Created At",
          lastLogin: "Last Login"
        },
        placeholders: {
          search: "Search by name / phone / email",
          notes: "Admin notes"
        },
        status: {
          active: "Active",
          inactive: "Inactive",
          banned: "Banned"
        },
        plan: {
          free: "Free",
          earlyBird: "Early Bird",
          standard: "Standard",
          enterprise: "Enterprise"
        },
        table: {
          user: "User",
          plan: "Plan",
          status: "Status",
          credits: "Credits",
          actions: "Actions"
        },
        actions: {
          manage: "Manage"
        }
      },
      logs: {
        title: "Admin Logs",
        desc: "Audit trail of admin operations",
        loading: "Loading logs...",
        error: "Failed to load logs.",
        empty: "No logs found.",
        search: "Search",
        reset: "Reset",
        pagination: "Page {current} / {total}",
        prev: "Prev",
        next: "Next",
        placeholders: {
          action: "Filter by action",
          admin: "Filter by admin ID",
          filter: "Filter by keyword",
          module: "Filter by module",
          startDate: "Start date",
          endDate: "End date"
        },
        labels: {
          level: "Log level",
          module: "Module",
          keyword: "Keyword",
          startDate: "Start date",
          endDate: "End date"
        },
        table: {
          status: "Status",
          level: "Level",
          action: "Action",
          target: "Target",
          admin: "Admin",
          details: "Details",
          ip: "IP",
          createdAt: "Created At"
        },
        levels: {
          all: "All",
          error: "Error",
          warn: "Warn",
          info: "Info"
        },
        copy: "Copy details",
        copying: "Copying...",
        copied: "Copied"
      },
      pagination: {
        jumpTo: "Jump to",
        jump: "Jump",
        page: "Page"
      },
      settings: {
        title: "System Settings",
        desc: "Adjust runtime configuration",
        error: "Failed to load settings.",
        loading: "Loading settings...",
        empty: "No settings available.",
        search: "Search",
        searchPlaceholder: "Search by key, hint, or group",
        onlyChanged: "Only changed",
        expandAll: "Expand all",
        collapseAll: "Collapse all",
        saved: "Setting saved.",
        saving: "Saving...",
        saveConfirm: "Saving this setting will overwrite the existing value. Continue?",
        save: "Save",
        boolean: "Enabled",
        items: "items",
        noMatch: "No settings matched your filter.",
        unsavedChanges: {
          leaveTitle: "Unsaved changes",
          leaveConfirm: "You have unsaved settings changes. Leave this page and discard them?",
          leaveNow: "Leave page",
          stay: "Stay on page"
        },
        updatedAt: "Updated at {time}",
        typeHints: {
          secret: "Sensitive credential. Keep this value hidden and avoid sharing it in screenshots.",  // pragma: allowlist secret
          number: "Numeric value. Leave blank only when supported by system fallback.",
          boolean: "Toggle the flag to enable/disable this feature.",
          text: "Text value used by current function module, please fill carefully."
        },
        groups: {
          billing: "Billing",
          pricing: "Pricing",
          ai_channel: "AI Channel",
          platform: "Platform",
          business: "Business",
          feature: "Feature",
          other: "Other"
        },
        showMore: "Show {count} more",
        showLess: "Show less",
        ozonPublic: {
          title: "Ozon Public Query Credentials",
          desc: "Used for public Ozon catalog queries (category/attributes). New users can complete generation without binding a store. Publishing still requires binding their own store.",
          clientId: "Client ID",
          apiKey: "API Key",  // pragma: allowlist secret
          configured: "Configured",
          notConfigured: "Not configured",
          clearTitle: "Clear Ozon public credentials",
          saved: "Ozon public credentials saved.",
          saveConfirm: "You are about to save changed Ozon public credentials. This will apply to all new user queries. Continue?",
          fillRequired: "Client ID and API Key are both required before testing.",
          clientIdHint: "Enter the Ozon public API client ID from Ozon dashboard.",
          apiKeyHint: "Enter the Ozon public API key from Ozon dashboard. This value is masked after save.",  // pragma: allowlist secret
          test: "Test Connection",
          testing: "Testing...",
          testSuccess: "Ozon connection is healthy.",
          clear: "Clear",
          clearConfirm: "Clear Ozon public query credentials? This may break category/attribute queries for new users.",
          cleared: "Ozon public credentials cleared.",
          secretHint: "API Key only shows last 4 chars. It won't be revealed after saving."
        }
      }
    },

    // Hero Section
    hero: {
      badge: "AI-Powered E-commerce",
      title: "Transform Your Products",
      titleHighlight: "Into Global Listings",
      subtitle:
        "Paste a link, let AI generate images, copy, SEO and platform-ready attributes, then publish in one click. Turn 30–60 mins of manual listing work into minutes.",
      placeholder: "Paste your 1688 or AliExpress link here...",
      button: "Start AI Optimization"
    },

    // Feature 1 - Product Showcase
    feature1: {
      title: "See AI Magic in Action",
      subtitle: "Watch how we transform raw supplier data into premium marketplace listings.",
      tab1: "Smart Watch",
      tab2: "Headphones",
      tab3: "Sneakers",
      imgBtn1: "AI Enhanced",
      imgBtn2: "Original",
      imgBtn3: "Compare",
      aiBadge: "AI Optimized",
      marketingTitle: "Marketing Assets",
      viewAll: "View All",
      listingTitleTag: "Listing Title",
      translated: "Translated",
      bulletTitle: "Key Features",
      seoTitle: "SEO Keywords",
      pushBtn: "Push to Marketplace",
      syncText: "Synced to Ozon",
      // Watch product
      prodTitle_watch: "Premium Smart Watch Pro Max",
      bullet1Title_watch: "Health Monitoring",
      bullet1Desc_watch: "24/7 heart rate, SpO2, sleep tracking",
      bullet2Title_watch: "Long Battery",
      bullet2Desc_watch: "Up to 14 days on single charge",
      key1_watch: "smartwatch",
      key2_watch: "fitness tracker",
      key3_watch: "health monitor",
      hotspot1: "AMOLED Display",
      hotspot2: "Titanium Case",
      // Headphones product
      prodTitle_headphones: "Wireless ANC Headphones Studio",
      bullet1Title_headphones: "Active Noise Cancellation",
      bullet1Desc_headphones: "Block out 99% of ambient noise",
      bullet2Title_headphones: "Hi-Res Audio",
      bullet2Desc_headphones: "40mm drivers with LDAC support",
      key1_headphones: "wireless headphones",
      key2_headphones: "noise cancelling",
      key3_headphones: "bluetooth",
      hotspot_headphones_1: "Memory Foam Pads",
      hotspot_headphones_2: "Touch Controls",
      // Sneakers product
      prodTitle_sneakers: "Ultra Boost Running Shoes X9",
      bullet1Title_sneakers: "Cloud Cushioning",
      bullet1Desc_sneakers: "Revolutionary foam technology",
      bullet2Title_sneakers: "Breathable Mesh",
      bullet2Desc_sneakers: "360° ventilation system",
      key1_sneakers: "running shoes",
      key2_sneakers: "athletic footwear",
      key3_sneakers: "sports sneakers",
      hotspot_sneakers_1: "Carbon Plate",
      hotspot_sneakers_2: "Grip Sole"
    },

    // Feature 2 - How It Works
    feature2: {
      title: "How It Works",
      subtitle: "Three steps to create marketplace listings in minutes.",
      card1Title: "Import & Analyze",
      card1Desc: "Paste any supplier link. Our AI extracts product data, images, and specifications automatically.",
      card2Title: "AI Optimization",
      card2Desc: "Generate SEO titles, translate descriptions, enhance images, and create A+ content in minutes."
    },

    // Feature 3 - Security
    feature3: {
      badge: "Enterprise Security",
      title: "Protect Your Brand Assets",
      subtitle: "Military-grade protection for your product images and listings.",
      verified: "Verified",
      watermark: "Neural Watermark",
      hash: "SHA-256 Hash",
      cert: "Certified",
      card1Title: "Invisible Watermarks",
      card1Desc: "AI-embedded markers that survive compression and cropping.",
      card2Title: "Legal Evidence Pack",
      card2Desc: "Auto-generated DMCA takedown documents with blockchain proof.",
      card3Title: "Instant Detection",
      card3Desc: "Get alerts when your images appear on unauthorized listings.",
      downloadBtn: "Download Whitepaper",
      downloadSub: "Security Architecture Overview",
      learnMore: "Learn More About Protection"
    },

    // Pricing Section
    pricing: {
      title: "Simple, Transparent Pricing",
      subtitle: "Pay only for image generation — {creditsPerImage} credits/image. Est. {imagesPerListing} images per listing (for reference).",
      roiTitle: "Do the math",
      roiDesc:
        "Manual listing creation often takes {minutesMin}–{minutesMax} minutes. Scaling usually requires dedicated staff (≈{laborMin}–{laborMax} CNY/month). Let AI handle images, copy, SEO and platform formatting—review and publish in one click.",
      timeSaveHint: "Estimated time saved: ~{hoursMin}-{hoursMax} hours (for reference).",
      timeSaveHintMonthly: "Estimated time saved: ~{hoursMin}-{hoursMax} hours/mo (for reference).",
      monthly: "Monthly",
      yearly: "Yearly",
      save: "2 months free",
      bestForGuests: "Best for Guests",
      onDemand: "Pay as you go",
      credits: "Credits",
      creditsLabel: "Credits",
      imagesLabel: "Images",
      perMonth: "/mo",
      perYear: "/yr",
      packBasic: "Basic Pack",
      packGrowth: "Growth Pack",
      packScale: "Scale Pack",
      bestValue: "Best Value",
      featExpires: "Top-up credits never expire",
      featAi: "AI image generation",
      featPsd: "HD image exports",
      featPriority: "Priority support",
      purchase: "Purchase",
      planStarter: "Starter",
      planPro: "Pro",
      planBusiness: "Business",
      featStore: "Monthly credit bundle",
      featBatch: "Batch image generation",
      featSupport: "Priority support",
      featBatchStarter: "Basic batch generation",
      featBatchPro: "Advanced batch generation",
      featBatchBusiness: "High-volume batch generation",
      featSupportStarter: "Standard support (48h)",
      featSupportPro: "Priority support (12h)",
      featSupportBusiness: "Priority support + success manager",
      featExportsStarter: "HD image downloads",
      featExportsPro: "HD + 4K image downloads",
      featExportsBusiness: "Brand asset kit exports",
      loadFailed: "Unable to load pricing. Please retry or contact support.",
      retryAction: "Reload"
    },

    // FAQ Section
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our platform.",
      contact: "Contact Support",
      q1: "Will AI change my product information?",
      a1: "No. We optimize from your source data and you can review or edit every field before publishing.",
      q2: "Do I need to install a plugin or connect my store?",
      a2: "No. Paste a product link to try it. Store authorization is only required when publishing or syncing.",
      q3: "Is my data and images secure?",
      a3: "Enterprise-grade encryption and isolation keep your data safe. Generated images include invisible watermarks and forensic proof.",
      q4: "How does the free trial and pricing work?",
      a4: "New users get one full task with only one hero image. After that, choose a subscription or credits with transparent pricing."
    },

    // CTA Section
    cta: {
      title: "Ready to Scale",
      titleBreak: "Your E-commerce Business?",
      subtitle: "Join thousands of sellers already using AI to dominate global marketplaces.",
      button: "Get Started Free"
    },

    // Footer
    footer: {
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      support: "Support",
      rights: "© {year} OneClickSell. All rights reserved."
    },

    // Trust page
    trust: {
      forensicData: "Forensic Metadata",
      badge: "Enterprise Security",
      title: "Protect Your",
      titleHighlight: "Digital Assets",
      subtitle: "Military-grade security for your product images and brand identity.",
      dmcaBadge: "DMCA Protected",
      forensicBadge: "Forensic Ready",
      encryptedBadge: "256-bit Encrypted",
      nav: {
        technology: "Technology",
        legal: "Legal Framework",
        whitepaper: "Resources"
      },
      resources: {
        copyrightTitle: "Copyright Whitepaper",
        copyrightDesc: "Full technical breakdown of our watermark technology.",
        legalTitle: "Legal Certificates",
        legalDesc: "Official templates for marketplace verification."
      },
      tech1Title: "Neural Watermarking",
      tech1Desc: "Invisible markers that survive any transformation.",
      tech2Title: "Blockchain Timestamp",
      tech2Desc: "Immutable proof of creation on distributed ledger.",
      tech3Title: "AI Detection",
      tech3Desc: "Real-time monitoring across all marketplaces.",
      compareTitle: "See the Difference",
      compareSub: "Move the slider to reveal hidden watermarks.",
      rawOutput: "Raw Output",
      status: "Status",
      ctaBtn: "Start Protection",
      protectedLayer: "Protected Layer",
      frameworkTitle: "Legal Framework",
      frameworkSub: "Compliance-ready documentation and verified evidence trails.",
      licenseTitle: "License Agreement",
      licenseDesc: "Instantly generate licensing documentation with immutable proof.",
      dmcaPackageTitle: "DMCA Package",
      dmcaPackageDesc: "Auto-build takedown evidence packages for marketplaces.",
      complianceTitle: "Compliance Ready",
      complianceDesc: "Structured evidence that meets platform enforcement requirements.",
      platformLabel: "Platform",
      priority: "Priority",
      refLabel: "Reference ID",
      evidenceLabel: "Evidence",
      evidence1: "Original listing screenshots",
      evidence2: "Watermark verification hash",
      generatorTitle: "Automated DMCA Generator",
      generatorSub: "Generate enforcement documents in minutes.",
      generatorNote: "Compatible with Amazon, Shopify, Etsy, and 30+ platforms.",
      letterAutomation: "Auto-generated DMCA Notice",
      letterBody1: "This notice confirms ownership and infringement evidence.",
      letterBody2: "All metadata and timestamps are verified on-chain.",
      letterBody3: "Supporting evidence is attached in the case file.",
      letterBody4: "Please remove the infringing content immediately.",
      letterNotice: "This notice is generated for immediate submission.",
      letterSign: "Authorized Representative",
      ctaTitle: "Ready to Protect Your Brand?",
      ctaTitleBreak: "Start Enforcement Today",
      ctaSub: "Launch compliance-grade protection in under 5 minutes.",
      downloadBtn: "Download Evidence Pack",
      verifiedHash: "Verified Hash",
      urlLabel: "Infringing URL",
      selectPlatform: "Select Platform",
      toLabel: "To",
      rights: "All rights reserved"
    },

    // Auth translations
    welcome: "Welcome Back",
    loginSubtitle: "Please enter your details to sign in to your professional account.",
    smsMethod: "SMS Code",
    passMethod: "Password",
    phoneLabel: "Phone Number",
    phonePlaceholder: "138 0000 0000",
    mobilePlaceholder: "Mobile number",
    phoneRequired: "Please enter your phone number.",
    phoneInvalid: "Invalid phone number.",
    sendCode: "Send Verification Code",
    sendCodeFailed: "Failed to send the code. Please try again.",
    debugCodeHint: "Development code: {code}",
    verifyLabel: "Verification Code",
    resendIn: "Resend in {time}s",
    resendNow: "Resend Code",
    invalidCode: "Invalid verification code. Try 123456.",  // pragma: allowlist secret
    loginFailed: "Login failed. Please try again.",
    authErrorTermsRequired: "Please accept the Terms and Privacy Policy, then try again.",
    authErrorInvalidToken: "Your session token is invalid. Please sign in again.",
    authErrorTokenExpired: "Your session expired. Please sign in again.",
    authErrorTokenRevoked: "This session was revoked. Please sign in again.",
    authErrorUnauthorized: "You are not signed in. Please log in and retry.",
    authErrorForbidden: "Your account does not have permission for this action. Contact support if needed.",
    authErrorUserNotFound: "We couldn't find this account. Check your phone number or sign up first.",
    authErrorUserDisabled: "This account is disabled. Contact support to restore access.",
    authErrorInvalidCredentials: "Phone number or password is incorrect. Please try again.",
    authErrorPhoneInvalid: "Phone format is invalid. Please enter an 11-digit mobile number.",
    authErrorSmsRateLimited: "Too many verification requests. Please wait 1 minute and try again.",
    authErrorSmsDailyLimit: "Today's SMS limit is reached. Please try again tomorrow.",
    authErrorSmsServiceUnavailable: "SMS service is temporarily unavailable. Please try again later.",
    passwordLabel: "Password",
    forgotPass: "Forgot Password?",
    passPlaceholder: "Enter your password",
    passwordRequired: "Please enter your password.",  // pragma: allowlist secret
    passwordTooShort: "Password must be at least 8 characters.",  // pragma: allowlist secret
    signIn: "Sign In",
    newToApp: "New to OneClickSell?",
    signUp: "Sign up",
    createAccount: "Create Your Account",
    signupSubtitle: "Join 5,000+ sellers optimizing their listings.",
    codeVerified: "Code Verified",
    setPassPlaceholder: "Set your password",
    agree: "I agree to the",
    userAgreement: "User Agreement",
    and: "and",
    privacyPolicy: "Privacy Policy",
    termsRequired: "Please read and agree to the Terms of Service and Privacy Policy first.",
    autoRegisterHint: "If this phone number is not registered, we'll create an account automatically after verification.",
    getStarted: "Get Started for Free",
    haveAccount: "Already have an account?",
    login: "Log in",
    resetTitle: "Reset Your Password",
    resetSubtitle: "Enter your mobile number to receive a verification code.",
    sendReset: "Send Reset Code",
    resetStep1: "Verify Phone",
    resetStep2: "Enter Code",
    resetStep3: "Set New Password",
    resetCodeSent: "Verification code sent. Please check your phone.",
    resetCodeHint: "Enter the 6-digit code",
    resetVerifyAction: "Verify & Continue",
    resetBackStep: "Back to Previous Step",
    resetMismatch: "Passwords do not match.",
    resetRequirement: "Use at least 8 characters with letters and numbers.",
    resetSuccess: "Password updated successfully.",
    resetSuccessHint: "Returning to sign in...",
    resetContextMissing: "Reset session expired. Please request a new code.",
    resetFailed: "Password reset failed. Please try again.",
    resetStrengthLabel: "Strength: {level}",
    resetStrengthWeak: "Weak",
    resetStrengthMedium: "Medium",
    resetStrengthStrong: "Strong",
    backLogin: "Back to Login",
    phoneVerified: "Phone Verified",
    setNewPass: "Set New Password",
    newPassSubtitle: "Please create a strong password to secure your account.",
    newPassLabel: "New Password",
    newPassPlaceholder: "Create a new password",
    strength: "Strength: Strong",
    confirmPassLabel: "Confirm New Password",
    confirmPassPlaceholder: "Repeat new password",
    updatePass: "Update Password",
    backSignIn: "Back to Sign In",
    captchaLabel: "Human Verification",
    captchaHint: "Complete the verification to continue.",
    captchaLoading: "Loading verification...",
    captchaRequired: "Please complete the verification first.",
    captchaUnavailable: "Verification is unavailable. Please try again later.",
    captchaLoadFailed: "Verification failed to load. Refresh and try again.",
    captchaError: "Verification failed. Please retry.",

    // Profile Menu
    profile: {
      proPlan: "Pro Plan",
      freePlan: "Free Plan",
      guest: "Guest",
      coreNav: "Core Navigation",
      workspace: "Workspace",
      assetMgmt: "Asset Management",
      copyright: "Copyright Assets",
      pricing: "Pricing Strategy",
      storeAuth: "Store Authorization",
      marketIntel: "Market Intelligence",
      beta: "Beta",
      marketIntelComingSoon: "Market Intelligence is coming soon. Contact support to request early access.",
      account: "Account",
      settings: "Account Settings",
      billing: "Billing & Credits",
      help: "Help Center",
      signOut: "Sign Out"
    },

    // Settings
    settings: {
      general: "General Info",
      security: "Security",
      preferences: "Platform Preferences",
      billing: "Billing & Credits",
      notifications: "Notifications",
      feedback: "My Feedback",
      integrations: "Integrations",
      storeAuth: "Store Authorization"
    },

    // Store Binding
    storeBind: {
      title: "Store Authorization",
      subtitle: "Link your marketplace stores to enable direct AI publishing.",
      guideTitle: "Setup Guide",
      step1Title: "Login to Ozon",
      step1Desc: "Access your account at the Ozon Seller Center portal to begin the integration.",
      step1Btn: "Go to Ozon Seller Center",
      step2Title: "API Settings",
      step2Desc: "Navigate to Settings > API Keys in the top menu.",
      step2Btn: "Open API Settings",
      step3Title: "Generate Admin Key",
      step3Desc: "Select \"Admin\" key type, generate it, and copy the Client ID and Key for use in the form.",

      // Store Switcher
      activeSwitcher: "Active Store Switcher",
      manageAll: "Manage All Stores",
      currentlyActive: "Currently Active",
      synced: "Synced",
      connected: "Connected",
      disabled: "Disabled",
      syncingShort: "Syncing",
      syncFailedShort: "Sync Failed",
      uploads7d: "7d Uploads",
      connectNew: "Connect New Store",

      // Platform Selection
      fullApiIntegration: "Full API Integration",
      readyConnect: "Ready to connect",
      comingSoon: "Coming Soon",
      active: "Active",

      // Connection Form
      connectOzon: "Connect Ozon Store",
      statusLabel: "Status",
      authorized: "Authorized & Ready",
      noStoresLinked: "No stores linked",
      howToGetKey: "How to get API Key?",
      storeName: "Store Name",
      storeNamePlaceholder: "e.g., My Ozon Shop",
      clientId: "Client ID",
      clientIdPlaceholder: "Enter Client ID",
      apiKey: "API Key",  // pragma: allowlist secret
      addStore: "Add Ozon Store",
      testConnection: "Test Connection",
      testing: "Testing...",

      // Syncing
      syncingData: "Syncing Store Data...",
      syncingDesc: "Importing categories, shipping methods, and warehouse info from Ozon. This will only take a moment.",
      syncComplete: "Synchronization Complete",
      syncCompleteDesc: "All categories and warehouse data have been successfully synced. Your store is now ready for AI listing generation.",
      success: "Success",

      // Success State
      startListing: "Start Listing Generation",
      connectionSuccess: "Connection Successful",

      // Security
      securityTitle: "API Connection Security",
      securityEncryption: "Your credentials are stored using AES-256 military-grade encryption.",
      securityNoPassword: "OneClickSell will never ask for or store your marketplace login password.",  // pragma: allowlist secret

      // Table
      authorizedStores: "Authorized Stores",
      platform: "Platform",
      actions: "Actions",
      syncSettings: "Sync Settings",
      items: "items",

      // Settings Modal
      settingsTitle: "Store Settings",
      settingsSubtitle: "Manage store automation and security options.",
      loading: "Loading...",
      settingActive: "Store Active",
      settingActiveDesc: "Disable to pause publishing and syncing for this store.",
      settingAutoSync: "Auto Sync",
      settingAutoSyncDesc: "Sync products, stocks, and orders automatically.",
      settingPsd: "PSD Protection",
      settingPsdDesc: "Enable copyright embedding and evidence generation.",
      settingSyncPsd: "Sync PSD Processing",
      settingSyncPsdDesc: "Embed PSD copyright before image upload.",
      settingRotateKey: "Update API Key",
      settingRotateKeyHint: "Leave empty to keep your current API Key.",
      deleteStore: "Delete Store",
      setDefault: "Set as Default",
      saving: "Saving...",
      saveChanges: "Save Changes",

      // Sync Modal
      syncModalTitle: "Sync Store Data",
      syncModalSubtitle: "Sync data for {name}.",
      syncModalDesc: "Select which data types to sync for this store.",
      syncProducts: "Products",
      syncProductsDesc: "Refresh product list from Ozon.",
      syncStocks: "Stocks",
      syncStocksDesc: "Refresh inventory levels by warehouse.",
      syncOrders: "Orders",
      syncOrdersDesc: "Refresh recent orders and statuses.",
      syncNow: "Start Sync",

      // Confirm
      deleteTitle: "Delete Store",
      confirmDelete: "Are you sure you want to delete this store? This action cannot be undone.",
      deleteConfirm: "Delete",

      // Footer
      storageSystem: "Storage System: Localized AES Encryption",
      securityGuaranteed: "Security Guaranteed",
      encryptedBridge: "Encrypted API Bridge",
      noPasswordStorage: "No Password Storage",  // pragma: allowlist secret
      cancel: "Cancel",
      saveAuth: "Save Authorization",

      // Toast
      toastTitle: "Ozon Store Ready",
      toastDesc: "All categories and warehouse data have been successfully synced.",
      toasts: {
        defaultUpdated: "Default store updated.",
        noChanges: "No changes to save.",
        storeUpdated: "Store settings updated.",
        storeDeleted: "Store deleted.",
        syncStarted: "Sync started. Data will refresh shortly."
      },

      continuePublish: "Continue Publishing",
      noStores: "No authorized stores yet.",
      testSuccess: "API credentials verified.",
      errors: {
        missingFields: "Please fill in store name, Client ID, and API Key.",
        testFailed: "Connection test failed. Please verify your credentials.",
        bindFailed: "Store binding failed (status: {status}).",
        statusFailed: "Failed to load store status (status: {status}).",
        syncFailed: "Synchronization failed. You can retry from Sync Settings.",
        defaultFailed: "Failed to update default store.",
        storeDetailFailed: "Failed to load store details (status: {status}).",
        storeUpdateFailed: "Failed to update store (status: {status}).",
        storeNameRequired: "Store name is required.",
        deleteFailed: "Failed to delete store (status: {status}).",
        syncTypesRequired: "Please select at least one sync type.",
        syncTriggerFailed: "Failed to trigger sync (status: {status})."
      }
    },

    publish: {
      title: "Final Launch Confirmation",
      subtitle: "Currently optimized for Ozon platform publishing.",
      healthScore: "Listing Health Score",
      health: {
        excellent: "Excellent Ready",
        good: "Great Potential",
        warning: "Needs Review",
        poor: "Needs Improvement"
      },
      targetPlatforms: "1. Target Platforms",
      storeLabel: "Store",
      manageStores: "Manage stores",
      storeModeSingle: "Single",
      storeModeBatch: "Batch",
      batchPrimary: "Primary",
      batchHint: "Batch mode will create one publish task per selected store.",
      batchSelectedCount: "{count} stores selected",
      selectAllStores: "Select all",
      clearStores: "Clear",
      creatingTasks: "Creating publish tasks ({current}/{total})...",
      modeNoticeUpdate: "Detected existing listing (offer_id: {offerId}). This action will update the existing product instead of creating a duplicate.",
      modeNoticeCreate: "No existing listing found. This action will create a new product on Ozon.",
      platformReady: "Global Seller",
      platformUnavailable: "Not Available",
      comingSoon: "Coming Soon",
      pricingConfirmation: "2. Pricing Confirmation",
      aiOptimized: "AI Optimized Prices",
      tablePlatform: "Platform",
      tableSuggested: "Suggested Price",
      tableAdjusted: "Adjusted Price",
      tableProfit: "Est. Profit",
      scheduleTitle: "3. Publishing Schedule",
      publishNow: "Publish Now",
      publishNowDesc: "Push listing to Ozon immediately.",
      scheduleLater: "Schedule for Later",
      scheduleLaterDesc: "Choose a peak traffic time to launch.",
      releaseDate: "Release Date",
      releaseTime: "Release Time",
      backToEdit: "Back to Edit",
      confirmLaunch: "Confirm & Launch",
      confirmLaunchBatch: "Confirm & Launch ({count})",
      launching: "Launching...",
      loading: "Loading...",
      progressTitle: "Ozon Publishing Progress Monitor",
      progressSubtitle: "OneClickSell • Ozon API Integration",
      publishing: "Publishing",
      batchPublishing: "Batch publishing",
      viewResult: "View result",
      estimatedRemaining: "Estimated remaining",
      stepCompleted: "Completed",
      stepInProgress: "In Progress...",
      stepFailed: "Failed",
      stepPending: "Pending",
      stepLabels: {
        validate: "Validating Data",
        uploadImages: "Uploading Product Images",
        createListing: "Creating Ozon Listing",
        syncStock: "Syncing Price & Stock",
        updateListing: "Updating Listing Data",
        updateAttributes: "Syncing Product Attributes",
        updatePrices: "Syncing Product Prices",
        updateStock: "Syncing Product Stock",
        updateImages: "Updating Product Images",
        verifyUpdate: "Verifying Update Result"
      },
      doNotClose: "Please do not close this page.",
      doNotCloseDesc: "Your listing is being securely transmitted to Ozon API.",
      successTitle: "Product Published Successfully",
      successSubtitle: "Your listing has been verified and is now live on the marketplace.",
      successWarningTitle: "Product Created (Action Needed)",
      successWarningSubtitle: "The product card was created, but it is not currently visible in Ozon backend.",
      liveNow: "Live Now",
      needsAction: "Action Needed",
      openInOzon: "Open in Ozon Store",
      openInOzonDisabledHint: "The publish link will appear once the listing is live.",
      returnDashboard: "Return to Dashboard",
      goToAssets: "Go to Asset Library",
      publishAnother: "Publish Another Product",
      resultFooter: "© {year} AI Listing Optimizer. Listing Batch ID: {ref}",
      failureTitle: "Unable to Publish to Ozon",
      failureSubtitle: "Your listing could not be processed by the marketplace API.",
      errorDetails: "Error Details",
      referenceId: "Reference ID: {ref}",
      retryPublishing: "Retry Publishing",
      retrying: "Retrying...",
      contactSupport: "Contact Support",
      batchFailuresTitle: "{count} store tasks failed to create",
      manualReview: {
        title: "Manual Review Recommended",
        desc: "Ozon may return errors while the item is still partially created. Please verify in Ozon Seller backend.",
        partialSuccessHint: "If the product already appears in Ozon Seller backend, this usually means the upload was partially successful and only specific fields need correction.",
        checkOzon: "Log in to Ozon Seller and check the exact validation errors for this product.",
        fixInOzon: "If the product card exists, you can manually correct fields and resubmit in Ozon.",
        aiNotPerfect: "AI-generated listing data improves efficiency but is not 100% accurate. Manual review is still required for some items."
      },
      recovery: {
        title: "Recovery Plan",
        loading: "Analyzing failure...",
        reasonFallback: "No detailed reason returned from Ozon. You can retry or contact support.",
        reasonCode: "Rule",
        newOfferId: "New SKU",
        newOfferIdPlaceholder: "Enter a new SKU (offer_id)",
        newOfferIdHint: "Use letters/numbers/_/- only, max length 50.",
        retryWithNewSku: "Relaunch with New SKU",
        retrySamePayload: "Retry with Same Payload",
        executing: "Submitting...",
        goBindStore: "Check Store Authorization",
        offerIdRequired: "Please enter a new SKU first.",
        loadFailed: "Failed to load recovery plan.",
        executeFailed: "Failed to relaunch. Please try again.",
        actions: {
          retry_with_new_offer_id: "Suggested: relaunch with a new SKU",
          retry_same_payload: "Suggested: retry with same payload",
          fix_content_then_retry: "Suggested: fix content and retry",
          store_auth_required: "Suggested: check store authorization",
          wait_and_poll: "Suggested: wait and refresh status",
          contact_support: "Suggested: contact support"
        }
      },
      result: {
        untitled: "Untitled Listing"
      },
      errors: {
        missingCollection: "Missing collection data. Please return and try again.",
        summaryFailed: "Failed to load publish summary (status: {status}).",
        storeStatusFailed: "Failed to load store status (status: {status}).",
        missingFields: "Required fields are missing. Please complete them before publishing.",
        noUpdateFields: "No actual changes detected. Price/image/content are unchanged, so update was not submitted.",
        richContentInvalid: "Rich content format is invalid. Please regenerate before publishing.",
        missingFieldsHint: "Missing fields: {fields}",
        storeRequired: "Please select at least one store.",
        missingSchedule: "Please select a publish date and time.",
        publishFailed: "Publishing failed (status: {status}).",
        statusFailed: "Failed to fetch publish status (status: {status}).",
        retryFailed: "Retry failed. Please try again later.",
        batchPartialFailed: "{count} stores failed to create tasks. Others are publishing.",
        socksProxyDependencyMissing:
          "Publishing request failed because SOCKS proxy dependency is missing. We switched to direct connection. Please retry.",
        viewTechnicalDetails: "View technical details",
        hideTechnicalDetails: "Hide technical details"
      }
    },

    // Capability Settings
    capability: {
      title: "Capability Settings",
      subtitle: "Set default 6-agent workflow once. Ozon supports full automation; other platforms export listing packages.",
      targetPlatform: "Target Platform Selection",
      supported: "Fully Supported",
      exportOnly: "Export Only",
      currentPlatform: "Current:",
      systemNotice: "System Notice",
      systemNoticeBody: "The 6-agent pipeline is platform-agnostic. Ozon supports direct publish; other platforms export listing packages for manual upload.",
      platformActive: "Full Automation",
      platformOzonDesc: "Full workflow with direct publish and compliance",
      platformAmazonDesc: "Global marketplace listing export package",
      platformShopifyDesc: "Storefront listing export package",
      platformAliDesc: "Cross-border listing export package",
      platformEbayDesc: "Marketplace listing export package",
      languageLockedHint: "Language is locked to Russian for Ozon.",
      targetMarket: "Target Market",
      russiaDefault: "Russia (Default for Ozon)",
      outputLanguage: "Output Language",
      russianDefault: "Russian (Standard Cyrillic)",
      contentStyleTitle: "Content Style",
      seoLevelTitle: "SEO Depth",
      customPromptTitle: "Custom Prompt",
      customPromptPlaceholder: "Write your custom prompt here...",
      customPromptHint: "Only used when Custom style is selected.",
      brandGuardTitle: "Brand & Compliance Guard",
      brandGuardDesc: "Apply brand protection and compliance safeguards across content and images.",
      brandGuardHint: "Advanced protection remains managed by the platform.",
      summaryTitle: "Execution Summary",
      summaryHint: "What will be applied",
      summaryImages: "Generate {count} images (main + scenario + feature).",
      summarySeo: "SEO depth: {level}.",
      summaryRich: "Rich content theme: {theme}.",
      summaryBrandOn: "Brand guard enabled.",
      summaryBrandOff: "Brand guard disabled.",
      summaryExportOnly: "{platform} currently supports listing export only.",
      saveDefault: "Save as Default",
      useDefault: "Use Default",
      saving: "Saving...",
      saved: "Saved",
      skipAutoOpen: "Save as default and do not auto-open again",
      skipAutoOpenHint: "You can reopen from the navigation menu at any time.",
      aiContentGen: "AI Content Generation",
      aiContentDesc: "Images, titles, and localized specifications",
      active: "ACTIVE",
      directPublish: "One-Click Direct Publish",
      directPublishDesc: "Bypass manual CSV export/import",
      ozonExclusive: "OZON EXCLUSIVE",
      launchBtn: "Launch AI Optimization",
      status: "System status: Ready for Ozon high-speed synchronization",
      secure: "Secure Protocol",
      neural: "Neural Engine v4.2",
      apiSync: "API Sync Active",

      // Smart setup modes
      quickModeSubtitle: "Quick edit your existing configuration",
      expertModeSubtitle: "Full control over all settings",
      guidedModeSubtitle: "Complete the setup to get started",
      currentConfig: "Current Configuration",
      quickModeDesc: "Edit key settings or launch AI with current config",
      expertMode: "Expert Mode",
      quickMode: "Quick Mode",
      showAdvanced: "Show advanced settings",
      saveChanges: "Save Changes",
      launchAI: "Launch AI",
      perProduct: "per product",
      platformLabel: "Platform",
      marketLabel: "Market",
      styleLabel: "Style",
      imagesLabel: "Images",
      marketRuShort: "Russia",
      marketUsShort: "United States",
      marketCnShort: "China",
      marketGlobalShort: "Global",
      styleProfessionalDesc: "Business formal tone",
      styleCreativeDesc: "Emotional storytelling",
      styleConciseDesc: "Direct & punchy",
      styleCustomDesc: "Custom prompt configuration",
      stepPlatformHint: "Select one target platform for this workflow.",
      stepMarketHint: "Confirm the core market where this listing will be published.",
      stepLanguageHint: "Choose the language used for generated listing content.",
      stepStyleHint: "Pick the content tone that best matches your brand.",
      stepCustomPromptHint: "Write your custom writing instructions for the AI.",
      stepImageCountHint: "Set how many AI images to generate per product.",
      stepSkipAutoOpenHint: "Choose whether this setup should open automatically next time.",
      stepSaved: "Current step has been saved.",
      loginRequired: "Please log in to save settings",
      saveSuccess: "Settings saved successfully",
      saveFailed: "Failed to save settings",
      remoteLoadFallbackHint: "Cloud sync failed. Showing local settings, you can retry.",
      strategyTierTitle: "Strategy Tier",
      strategyTierHint: "Choose the automation depth and quality level.",
      tierEconomy: "Economy",
      tierEconomyDesc: "Lower image volume and faster output for rapid launches.",
      tierBalanced: "Balanced",
      tierBalancedDesc: "Recommended default for quality and cost balance.",
      tierPremium: "Premium",
      tierPremiumDesc: "Higher image volume and stronger model quality.",
      guidedStepLabel: "Step {current} of {total}",
      guidedBack: "Back",
      guidedNext: "Next",
      guidedSaveNext: "Save & Next",
      guidedLaunch: "Launch Optimization"
    },

    aiConfig: {
      badge: "AI Configuration",
      pageTitle: "AI Optimization Settings",
      pageSubtitle: "Review and fine-tune the AI parameters before launching your optimization task.",
      statusHint: "AI suggestions ready for review",
      targetPlatform: "Target Platform",
      platformActive: "Ozon Active",
      platformOzonDesc: "Optimized for CIS market & regional logistics",
      platformAmazonDesc: "Global marketplace optimization pipeline",
      platformShopifyDesc: "Listing prep for Shopify storefronts",
      platformAliDesc: "Cross-border listing preparation",
      comingSoon: "Coming Soon",
      targetLanguage: "Target Language",
      languageRu: "Russian",
      languageEn: "English",
      languageDe: "German",
      languageFr: "French",
      languageIt: "Italian",
      languageEs: "Spanish",
      languageJa: "Japanese",
      languagePtBr: "Portuguese (Brazil)",
      languagePtPt: "Portuguese (Portugal)",
      languageNl: "Dutch",
      languageSv: "Swedish",
      languagePl: "Polish",
      languageTr: "Turkish",
      languageAr: "Arabic",
      languageZhHans: "Chinese (Simplified)",
      languageZhHant: "Chinese (Traditional)",
      languageCs: "Czech",
      languageDa: "Danish",
      languageFi: "Finnish",
      languageKo: "Korean",
      languageNb: "Norwegian (Bokmål)",
      languageTh: "Thai",
      contentStyle: "Content Style",
      styleProfessional: "Professional",
      styleCreative: "Creative",
      styleConcise: "Concise",
      styleCustom: "Custom",
      stylePrompt: "Style Prompt Logic",
      currentStyle: "Current Style:",
      customActive: "Custom Logic Active",
      promptProfessional: "Prompt: Focus on technical specifications, durability, and business-formal tone.",
      promptCreative: "Prompt: Use evocative language, emotional storytelling, and lifestyle benefits.",
      promptConcise: "Prompt: Direct, punchy sentences focusing on key value propositions.",
      promptCustom: "Custom prompt loaded. Review advanced settings for final adjustments.",
      customPrompt: "Custom Prompt Editor",
      customPromptPlaceholder: "Write your custom prompt here...",
      customPromptHint: "Advanced variables: {product_name}, {category}, {specs}",
      customHint: "Open advanced editor to refine custom prompt",
      imageGeneration: "Enable AI Image Generation",
      imageGenerationDesc: "Removes watermarks and creates custom lifestyle backgrounds.",
      imageGenerationOnce: "Applies to this run only.",
      imageCredits: "Image generation will consume image credits per product.",
      imageCount: "Images to generate",
      imageCountHint: "Generate 1–10 images. Starts from the main image and continues in order.",
      trialImageBadge: "Trial: 1 main image only",
      trialImageHint: "Trial mode generates 1 main image. Upgrade to unlock full image sets.",
      trialUpgradeCta: "View plans",
      seoLevel: "SEO Optimization Level",
      seoStandard: "Standard",
      seoStandardDesc: "Basic keyword injection and meta-tag optimization",
      seoDeep: "Deep Analysis",
      seoDeepDesc: "Semantic clustering, competitor analysis, and LSI keyword integration",
      seoLockedHint: "Deep analysis is the default and cannot be changed.",
      review: "Review & Launch",
      recommended: "Recommended",
      launch: "Launch AI Optimization",

      modalTitle: "AI Optimization Configuration",
      modalSubtitle: "Set your final preferences before launching AI agents.",
      reuseTitle: "Reuse Previous Results?",
      reuseSubtitle: "Historical optimization outputs are available for this product.",
      reuseDesc: "You can reuse the latest downstream results or regenerate with new preferences.",
      reuseNotice: "Reuse will display results directly without regenerating.",
      reuseTimeLabel: "Last run: {time}",
      reuseStageLabel: "Reusable stages: {stages}",
      reuseSourceLabel: "Source collection: {id}",
      reuseViewHistory: "View history",
      reuseRegenerate: "Regenerate",
      reuseUse: "Reuse Results",
      reuseApplying: "Applying...",
      useDefault: "Use Default Configuration",
      advanced: "Advanced Settings",
      cancel: "Cancel",
      saveDraft: "Save Draft",
      saving: "Saving...",
      saved: "Saved",
      footerHint: "AI agents will follow your selected strategy and credits plan."
    },

    // AI Workspace / Review
    ai: {
      workspaceTitle: "Active Listing Optimization",
      syncProgress: "Sync Progress",
      sourceRef: "Source Reference",
      rawMedia: "Raw Media Assets",
      brandProt: "Brand Protection",
      agentOrch: "Agent Orchestration",
      neuralCenter: "Neural Insight Center",

      // Modules
      sourceAnalysis: "Source Analysis & Structure",
      byImageAnalyzer: "By Image Analyzer",
      dataStructurer: "Data Structurer",
      rawImages: "Raw Supplier Images",
      scanned: "Scanned",
      unstructured: "Unstructured Text → Structured Attributes",
      processed: "Processed",

      seoArch: "SEO Keyword Graph",
      bySeo: "By SEO Architect",
      optTitle: "Optimized Title",
      score: "Score",
      bullets: "Bullet Points",
      keywordStrat: "Keyword Strategy",
      core: "Core",
      hot: "Hot",
      longTail: "Long-Tail",

      visualPipeline: "Visual Asset Pipeline",
      byImgSel: "By Image Selector",
      visualPlanner: "Visual Planner",
      refSel: "Reference Selection",
      sourcesLocked: "Sources Locked",
      visualPlan: "Visual Plan (Generative Prompts)",
      readyArtist: "Ready for Artist",

      optGallery: "Listing Writer",
      byArtist: "By Visual Artist",

      marketInt: "Platform Adapter",
      byRichBuilder: "By Rich Content Builder",
      contextMaster: "Context Master",
      aplusPreview: "Rich Content Preview",
      fieldMap: "Marketplace Field Mapping",

      motionProd: "Export & Delivery",
      byVideoCreator: "By Video Creator",
      rendering: "VEO 3.1 Rendering Engine",
      scriptGen: "Script Generated",

      // Agents
      analyzer: "Product Understanding",
      structurer: "Product Understanding",
      architect: "SEO Keyword Agent",
      planner: "Visual Strategy",
      selector: "Visual Strategy",
      richContent: "Platform Adapter",
      videoCreator: "Export Agent",
      visualArtist: "Listing Writer",

      // Status
      complete: "COMPLETE",
      thinking: "THINKING",
      queued: "QUEUED",
      waiting: "WAITING",
      idle: "IDLE",
      active: "ACTIVE (RENDERING)",
      enableVideo: "Enable Video Generation",

      // Footer
      reviewChanges: "Review Changes",
      reviewAfterGenerate: "Review after generation",
      oneClick: "One-Click Publish",
      neuralActive: "Neural Insight Center active. Pipeline targeting {platform} {market} ({language})."
    },

    markets: {
      ru: "Russia",
      us: "United States",
      cn: "China",
      global: "Global"
    },

    studio: {
      control: {
        title: "Generation & Debug Control",
        simpleTitle: "Generation Control",
        collection: "Collection:",
        collectionEmpty: "Not selected",
        setupRequiredTitle: "Complete AI settings first",
        setupRequiredDesc: "Please confirm target market/language and image settings before starting generation.",
        setupRequiredAction: "Go to AI settings",
        store: "Store",
        manageStores: "Manage stores",
        marketStatus: "Market Status",
        skipImages: "Skip images",
        onceOnly: "One-time only",
        costHint: "Estimated cost: {count} images · {credits} credits",
        confirmCostTitle: "Confirm deduction",
        confirmCostMessage: "This run will generate {count} images and deduct {credits} credits.\nContinue?",
        confirmCostConfirm: "Continue",
        start: "Start generation",
        regenerate: "Regenerate",
        resume: "Resume",
        starting: "Starting...",
        cancelGenerate: "Cancel",
        cancelling: "Cancelling...",
        runningStages: "Running stages",
        parallelRunningHint: "Parallel execution in progress",
        stageDebug: "Single-stage debug",
        runStage: "Run stage",
        executing: "Running...",
        infoStructureHint: "Running product understanding only updates this stage."
      },
      credits: {
        balanceHint: "Balance: {credits} credits",
        totalBalanceHint: "Total: {credits} credits",
        bucketSubscription: "Subscription credits",
        bucketRecharge: "Top-up credits",
        bucketLegacy: "Legacy credits",
        availableTooltipPrefix: "Available credits = ",
        availableTooltipSuffixThirdParty: " (Value mode: subscription → top-up → legacy).",
        availableTooltipSuffixOfficial: " (Official mode: top-up first; subscription/legacy depends on settings).",
        channelPlanLabel: "Image Mode",
        channelPlanThirdParty: "Value",
        channelPlanOfficial: "Official",
        perImageHint: "{credits} credits/image",
        channelPlanUnavailable: "This mode is temporarily unavailable. Please contact the admin.",
        channelPlanUnavailableInline: "Unavailable",
        channelPlanCompareHint: "Value {thirdParty}/image · Official {official}/image",
        channelSwitchChecking: "Checking task retry status...",
        channelSwitchBlockedDefault: "You can switch mode only after this task exhausts all automatic retries.",
        switchOfficialTitle: "Switch to Official mode?",
        switchOfficialConfirm: "Official mode costs more credits per image. Continue switching?",
        switchOfficialConfirmWithDiff:
          "Official mode uses {official} credits/image (current {current}/image, +{diff}/image). Continue?",
        switchOfficialConfirmLabel: "Switch",
        switchOfficialCancelLabel: "Cancel",
        insufficientTitle: "Insufficient credits",
        insufficientMessage: "Need {needed} credits, you currently have {current} credits.\nTop up to continue.",
        insufficientMessageGeneric: "Insufficient credits. Top up to continue.",
        goRecharge: "Top up",
        later: "Later",
        skipImages: "Skip images",
        stageHint: "Tip: You can skip images to finish copy/mapping now, then top up and generate images later.",
        generateHint: "This run estimates {count} images. You can also skip images to proceed first.",
        affordableHint: "With your balance, you can generate up to {count} images. You can adjust the image count in AI settings.",
        singleHint: "Image generation costs credits per image. Please top up and try again."
      },
      errors: {
        missingCollection: "Missing collection ID for Studio.",
        previewFailed: "Failed to load preview data.",
        stageStatusFailed: "Failed to load stage status.",
        generateFailed: "Failed to start generation.",
        stageExecuteFailed: "Failed to run stage.",
        agentLogFailed: "Failed to load agent logs.",
        previewRequestFailed: "Preview request failed (status {status}).",
        imagePlanRequestFailed: "Image plan request failed (status {status}).",
        imageGenerationStatusRequestFailed: "Image generation status request failed (status {status}).",
        stageStatusRequestFailed: "Stage status request failed (status {status}).",
        generateRequestFailed: "Generate request failed (status {status}).",
        stageExecuteRequestFailed: "Stage execution request failed (status {status}).",
        agentLogRequestFailed: "Agent logs request failed (status {status}).",
        promptUpdateFailed: "Failed to update prompt.",
        imageGenerateFailed: "Failed to generate image.",
        imageRegenerateFailed: "Failed to regenerate image.",
        imageDecisionFailed: "Failed to apply image decision.",
        imageDeleteFailed: "Failed to delete image.",
        updateChannelFailed: "Failed to update image mode.",
        cancelGenerateFailed: "Failed to cancel generation.",
        queueBusy: "Queue is busy. Please try again shortly.",
        refundHint: "Credits refunded.",
        publishBlocked: "Cannot publish yet. Please resolve the failed stage ({stage}) and regenerate.",
        publishNotReady: "Not ready to publish. Please complete generation first.",
        publishImageOpsPending: "Images are still being updated. Please wait before publishing.",
        publishMissingImages: "No images to upload. Please generate or add images before publishing.",
        publishMissingFields: "Required attributes are missing. Please finish platform mapping before publishing.",
        stageFailed: "Stage failed: {stage}"
      },
      publishGuide: {
        title: "Pre-publish checklist",
        subtitle: "Fix the items below before publishing.",
        close: "Got it",
        itemImages: "Missing images",
        itemPendingImages: "Images are processing",
        itemCategory: "Missing category / type",
        itemTitle: "Missing title",
        itemAttributes: "Missing required attributes",
        itemPrice: "Missing price",
        itemAdapter: "Platform mapping incomplete",
        itemStageFailed: "Stage failed: {stage}",
        itemStageIncomplete: "Stage incomplete: {stage}",
        goFixImages: "Add images",
        goToStage: "Go to {stage}"
      },
      action: {
        generateStarted: "Generation started.",
        generateAdjusted: "Generation started (auto adjusted to {count} images).",
        generateSkipImagesAdjusted: "Insufficient credits. Auto disabled image generation; you can top up and generate images later.",
        generateQueued: "The same generation task is already running. Progress has been restored.",
        cancelRequested: "Cancel request submitted.",
        stageTriggered: "Stage triggered: {stage}",
        stageQueued: "The same stage task is already running: {stage}",
        promptUpdated: "Prompt updated for image {index}.",
        imageGenerateTriggered: "Image generation triggered: {index}",
        imageGenerateQueued: "Image generation already queued: {index}",
        imageRegenerateTriggered: "Image regeneration triggered: {index}",
        imageRegenerateQueued: "Image regeneration already queued: {index}",
        imageCandidateAccepted: "Applied new image for plan {index}.",
        imageCandidateRejected: "Kept old image for plan {index}.",
        imageDeleted: "Image deleted: {index}",
        queueRetrying: "Queue busy, retrying ({attempt}/{max})..."
      },
      imageGeneration: {
        title: "Image generation task",
        statusQueued: "Queued",
        statusRunning: "Running",
        statusRunningBackground: "Background running",
        statusCompleted: "Completed",
        statusFailed: "Failed",
        statusCancelled: "Cancelled",
        progress: "{done}/{total} completed",
        progressWithFailed: "{done}/{total} completed · {failed} failed",
        defaultQueued: "Task is queued and will start soon.",
        defaultRunning: "Image generation is in progress.",
        defaultRunningBackground: "Task is still running in backend. Results will sync automatically.",
        defaultCompleted: "Image generation completed and results are synced.",
        defaultFailed: "Image generation failed. Credits will be refunded automatically.",
        defaultCancelled: "Image generation task has been cancelled.",
        backgroundHint: "You can continue other operations. This task is still running in background.",
        lastUpdated: "Last update: {time}"
      },
      module: {
        expand: "Expand",
        collapse: "Collapse",
        collapsedHint: "Module collapsed. Click Expand to view details."
      },
      stage: {
        productUnderstanding: "Product Understanding",
        seo: "SEO Keyword Graph",
        listing: "Listing Writer",
        selector: "Reference Selector",
        visual: "Visual Strategy",
        imageGeneration: "Image Generation",
        normalized: "Normalized Payload",
        category: "Category Match",
        adapter: "Platform Adapter",
        export: "Export & Delivery"
      },
      market: {
        medium: "Medium Competition",
        high: "High Competition",
        low: "Low Competition"
      },
      source: {
        title: "Product Understanding",
        verified: "Data Verified",
        pending: "Awaiting Data",
        visualDiagnostics: "Asset Diagnostics",
        analyzing: "Analyzing",
        ready: "Ready",
        summary: "Summary",
        expand: "Expand details",
        collapse: "Collapse details",
        thumbnailAlt: "Thumbnail",
        mainImageAlt: "Source Image",
        emptyImages: "No source images",
        emptyTitle: "Waiting for source images",
        emptyDesc: "Connect the extension or import a product to start understanding.",
        imageCount: "Images: {count}",
        noImages: "No images",
        scanning: "ANALYZING...",
        sectionsTitle: "Captured Sections",
        sectionsEmpty: "No section data yet."
      },
      rawText: {
        title: "Source Text",
        length: "{count} chars",
        emptyTitle: "No raw text yet",
        emptyDesc: "Run product understanding to parse source text."
      },
      productCore: {
        title: "Product Core",
        materials: "Materials",
        specs: "Specs",
        useCases: "Use cases",
        audience: "Audience",
        facts: "Facts",
        emptyTitle: "Product core pending",
        emptyDesc: "Run product understanding to generate core facts."
      },
      productSchema: {
        title: "Product Data Schema",
        identity: "Identity",
        titleLabel: "Title",
        brandLabel: "Brand",
        categoryLabel: "Category",
        attributes: "Key Attributes",
        categoryKeywords: "Category Keywords",
        noCategoryKeywords: "No category keywords yet.",
        empty: "No attribute data yet.",
        emptyTitle: "Attributes pending",
        emptyDesc: "Complete structuring to generate product attributes.",
        logistics: "Logistics",
        price: "Price",
        weight: "Weight",
        dimensions: "Dimensions",
        emptyValue: "—",
        sku: "SKU Overview",
        skuCount: "Variants",
        skuUnit: "Items",
        noColors: "No color data",
        sizes: "Sizes",
        noSizes: "No size data",
        skuReadOnlyHint: "SKU selection is managed in Product Preview to keep AI reference filtering consistent.",
        skuReadOnlyAction: "Go to Product Preview"
      },
      metrics: {
        images: "Images",
        skus: "SKUs",
        attributes: "Attributes",
        sections: "Sections",
        rawText: "Raw Text"
      },
      imageText: {
        title: "Image Text Extraction",
        count: "Images {count}",
        empty: "No image text extracted yet.",
        emptyTitle: "No text detected yet",
        emptyDesc: "Run image text extraction to populate results here."
      },
      seo: {
        strength: "Search Strength",
        failed: "SEO generation failed",
        failedDesc: "No SEO result available.",
        optimizedKeywords: "Optimized Keywords",
        emptyKeywords: "No keywords generated yet.",
        emptyTitle: "SEO keywords pending",
        emptyDesc: "Run SEO stage to generate keyword graph.",
        searchVolume: "Search Volume",
        competition: "Intent",
        unknown: "Unknown",
        clusterLabel: "Cluster",
        volumeTier: "Volume",
        volumeHigh: "High",
        volumeMedium: "Medium",
        volumeLow: "Low",
        volumeUnknown: "Unknown",
        searchPipeline: "Keyword Graph Summary",
        pipelinePending: "Generating keyword graph...",
        graphTitle: "Keyword Graph Visualization",
        graphEmpty: "No graph nodes yet.",
        meaningLabel: "Chinese meaning",
        coverageTitle: "Coverage Report",
        missingAspects: "Missing Aspects",
        duplicates: "Duplicate Keywords",
        languageIssues: "Language Issues",
        coverageEmpty: "No issues detected"
      },
      keyword: {
        primary: "Primary",
        secondary: "Secondary",
        longTail: "Long-Tail",
        conversion: "Conversion",
        transactional: "Transactional",
        information: "Information",
        other: "Other"
      },
      agent: {
        productUnderstanding: "Product Understanding",
        seo: "SEO Keyword Agent",
        listing: "Listing Writer",
        visual: "Visual Strategy",
        selector: "Reference Selector",
        normalized: "Normalized Payload",
        category: "Category Match",
        adapter: "Platform Adapter",
        export: "Export & Delivery",
        error: "Stage failed. Check logs."
      },
      visual: {
        title: "Visual Strategy",
        subtitle: "Prompt plans • {count} sequences",
        collapseAll: "Collapse all",
        regeneratePrompts: "Regenerate prompts",
        regeneratePromptsTitle: "Regenerate visual prompts",
        regeneratePromptsDesc: "Regenerate all 10 prompt plans. Auto-generate currently uses the first {count} plans.",
        regeneratePromptsRunning: "Running...",
        regeneratePromptsOnly: "Prompt only",
        regeneratePromptsAndGenerate: "Prompt + generate",
        regeneratePromptsAutoTitle: "Regenerate prompts and images",
        regeneratePromptsAutoAction: "Confirm",
        regeneratePromptsAutoConfirm:
          "This will regenerate all prompts and auto-render the first {count} images. Estimated cost follows image generation pricing ({credits} credits/image). Continue?",
        regeneratePromptsSuccess: "Prompt regeneration completed ({count} plans). Free retries left: {remaining}.",
        regeneratePromptsCharged: "Charged {credits} credits for prompt regeneration.",
        regeneratePromptsAutoQueued: "Auto image rendering queued for {count} plans.",
        regeneratePromptsAutoFailed: "Prompts regenerated, but auto image rendering was not queued.",
        empty: "No visual prompts yet. Run the visual strategy stage first.",
        emptyTitle: "Visual strategy ready to start",
        emptyDesc: "Run the visual strategy stage to generate prompt plans. We'll show editable prompts and image outputs here.",
        emptyStep1: "Step 1: Run visual strategy",
        emptyStep2: "Step 2: Review prompts",
        emptyStep3: "Step 3: Generate images",
        constraintsTitle: "Constraints",
        constraintsEmpty: "No constraints",
        keywordsTitle: "Keyword Focus",
        keywordsEmpty: "No keywords yet",
        referenceTitle: "Reference Images",
        referenceEmpty: "No reference images yet",
        referencePreviewTitle: "Reference Image #{index}",
        referenceSwitchTitle: "Switch Reference",
        referenceSwitchHint: "Use thumbnails or arrows to switch references quickly.",
        referenceSource: {
          sku: "SKU",
          main: "Main",
          collection: "Collection",
          extracted: "Extracted",
          legacy: "Legacy",
          manual: "Manual",
          sku_image_url: "SKU image URL",
          selected_sku_image_indices: "SKU image indices",
          none: "None",
          unknown: "Unknown"
        },
        anchorModeImage: "Image Anchor",
        anchorModeText: "Text Anchor · threshold {threshold}%",
        anchorSourceLabel: "Source: {source}",
        selectorSelectedCount: "Selected {count}",
        selectorSummaryTitle: "Selection Summary",
        selectorSummaryEmpty: "No selection summary yet.",
        selectorRationaleEmpty: "No rationale details yet.",
        selectorPromptTitle: "Selector Prompt",
        selectorPromptEmpty: "Prompt snapshot not available yet.",
        selectorDecisionPrimary: "Primary group",
        selectorDecisionSupplement: "Confidence supplement",
        selectorDecisionFallback: "Fallback fill",
        selectorReasonTitle: "Selection Reason",
        selectorReasonEmpty: "No explicit reason provided.",
        selectorConfidence: "Confidence {value}%",
        selectorSkuConfidence: "SKU confidence {value}%",
        singleSkuDecisionTitle: "Single SKU Decision",
        singleSkuDecisionEmpty: "No single SKU decision output yet.",
        singleSkuUnknown: "Unknown SKU",
        singleSkuBadge: "SKU · {sku}",
        singleSkuReasonsTitle: "Why this SKU",
        singleSkuReasonsEmpty: "No explicit SKU rationale.",
        singleSkuExcludedTitle: "Excluded SKU Notes",
        singleSkuExcludedEmpty: "No excluded SKU notes.",
        singleSkuRiskPrefix: "Risk: {risk}",
        singleSkuCandidatesTitle: "SKU Candidate Scores",
        singleSkuCandidatesEmpty: "No SKU candidate scoring data.",
        singleSkuCandidateScore: "Score {value}",
        singleSkuCandidateImageCount: "Images {count}",
        singleSkuCandidateReasonEmpty: "No candidate reason provided.",
        manualAnchorAction: "Go to Import Preview and lock an anchor image",
        referenceSkuTitle: "Mapped SKU",
        referenceSkuEmpty: "No SKU mapping",
        referenceSkuReasonTitle: "SKU Mapping Reason",
        referenceSkuReasonEmpty: "No SKU mapping reason.",
        overlayLabel: "Overlay Text",
        promptCopied: "Prompt copied",
        sceneLabel: "Scene",
        placeholderMainTitle: "Hero shot",
        placeholderMainDesc: "Primary listing image prompt",
        placeholderSceneTitle: "Scenario shot",
        placeholderSceneDesc: "Lifestyle and usage context",
        placeholderDetailTitle: "Detail shot",
        placeholderDetailDesc: "Material and feature close-ups",
        trackPrimary: "Main/Carousel Images",
        trackSecondary: "Detail Images",
        expand: "Expand",
        collapse: "Collapse",
        morePlansHint: "{count} additional plans are ready with prompts.",
        expandExtraPlans: "Show {count} more plans",
        collapseExtraPlans: "Hide extra plans",
        viewMore: "View {count} more",
        statusWaiting: "Status: Waiting",
        statusGenerated: "Status: {current}/{total} Generated",
        statusPrompted: "Status: {count} Planned",
        galleryTitle: "Generated Images",
        generated: "Generated",
        openImage: "Open",
        selectingReference: "Selecting reference images...",
        savePrompt: "Save Prompt",
        savePromptSaving: "Saving...",
        generateImage: "Generate Image",
        generating: "Generating...",
        regenerate: "Regenerate",
        regenerating: "Regenerating...",
        pendingCandidateLabel: "New image pending review",
        pendingCandidateAt: "Generated at",
        pendingCandidateExists: "A pending new image already exists. Please choose keep old or use new first.",
        compareOpen: "Compare",
        compareUseNew: "Use new",
        compareKeepOld: "Keep old",
        compareKeepOldTitle: "Keep current image",
        compareKeepOldConfirm: "Keep the current image for plan #{index} and discard the new candidate?",
        compareOld: "Current image",
        compareNew: "New image",
        compareModeSlider: "Slider compare",
        compareModeSideBySide: "Side by side",
        compareModalTitle: "Compare image plan #{index}",
        compareModalDesc: "Review both versions and choose the one to keep.",
        deleteImage: "Delete image",
        deleteConfirmTitle: "Delete generated image",
        deleteConfirmMessage: "Delete the generated image for plan #{index}? You can regenerate later.",
        deleteConfirm: "Delete",
        costHint: "Estimated: {count} image · {credits} credits",
        planBadge: "{index} {type}",
        planDefaultType: "MAIN",
        planTypeTitle: "{type} Plan",
        planTitle: "Image Plan {index}",
        planAspect: "Aspect {ratio}",
        planSubtitle: "Prompt plan",
        historyTitle: "Version History",
        historyEmpty: "No history yet",
        historyAccepted: "Accepted",
        historyRejected: "Rejected",
        historyViewImage: "View",
        historyRestore: "Restore",
        historyRestored: "Historical version restored",
        historyReplaced: "Replaced",
        historyCompare: "Compare",
        historyAt: "at {time}",
        stylePreset: {
          dramatic: "Dramatic",
          minimal: "Minimal",
          lifestyle: "Lifestyle",
          tech: "Tech",
          warm: "Warm",
          industrial: "Industrial",
          nature: "Nature",
          luxury: "Luxury"
        }
      },
      listing: {
        title: "Listing Writer",
        writing: "Writing...",
        keywordInput: "SEO input keywords",
        keywordEmpty: "No keyword hints yet.",
        emptyTitle: "Listing pending",
        emptyDesc: "Run listing stage to generate the listing output.",
        fieldTitle: "Title",
        shortDesc: "Short description",
        longDesc: "Long description",
        bullets: "Bullet points",
        emptyBullets: "No bullet points yet.",
        faq: "FAQ",
        emptyFaq: "No FAQ yet."
      },
      normalized: {
        title: "Normalized Payload",
        platformUnknown: "Platform",
        summary: "Normalized Summary",
        platform: "Platform",
        market: "Market",
        language: "Language",
        keywordCount: "Keywords",
        imageCount: "Images",
        skuCount: "SKUs",
        factCount: "Facts",
        payload: "Normalized Output",
        emptyPayload: "No normalized payload.",
        emptyTitle: "Normalized payload pending",
        emptyDesc: "Run normalized stage to generate."
      },
      adapter: {
        title: "Platform Adapter",
        rerun: "Rerun adapter",
        platformUnknown: "Platform",
        summary: "Adapter Summary",
        platform: "Platform",
        format: "Format",
        fieldCount: "Fields",
        category: "Category",
        typeId: "Type ID",
        price: "Price",
        images: "Images",
        attributesCount: "Attributes",
        richContent: "Rich content",
        richContentMissingHint: "Rich content missing; rerun adapter stage to regenerate.",
        missingFields: "Missing fields",
        missingFieldsEmpty: "No missing fields",
        payload: "Adapter Output",
        emptyPayload: "No adapter output.",
        fieldMap: "Field Mapping",
        emptyMappingTitle: "No mapped fields yet",
        emptyMappingDesc: "Run adapter stage to generate platform payload.",
        mappingField: "Field",
        mappingSource: "Normalized source",
        mappingStatus: "Status",
        mappingSuggestion: "Suggestion",
        mappingStatusMapped: "Mapped",
        mappingStatusInferred: "Can infer",
        mappingStatusMissing: "Missing",
        mappingSuggestUseSource: "Fill from normalized payload",
        mappingSuggestInfer: "Need inference / manual fill",
        mappingSuggestCategory: "Needs category match",
        mappingPlatformHint: "Current output is normalized; run Ozon adapter for field mapping.",
        schemaTitle: "Upload Parameters",
        schemaFetchedAt: "Schema fetched at {time}",
        schemaEmpty: "No schema data yet.",
        schemaField: "Field",
        schemaFieldId: "ID",
        schemaRequired: "Required",
        schemaType: "Type",
        schemaValue: "Value",
        schemaDictionary: "Dictionary values ({count})",
        schemaStatus: "Status",
        schemaStatusFilled: "Filled",
        schemaStatusMissing: "Missing",
        schemaRequiredYes: "Required",
        schemaRequiredNo: "Optional"
      },
      export: {
        title: "Export & Delivery",
        exporting: "Exporting...",
        open: "Export details",
        reexport: "Re-export",
        platform: "Platform",
        category: "Category",
        columns: "Columns",
        rows: "Rows",
        templateKey: "Template key",
        format: "Format",
        version: "Template Version",
        validation: "Validation",
        validationPass: "Passed",
        validationPending: "Pending / Issues",
        fileName: "File Name",
        delivery: "Delivery",
        ready: "Download ready.",
        pending: "Waiting for export data.",
        download: "Download",
        waiting: "No download link yet.",
        validationErrors: "Validation errors"
      },
      preview: {
        title: "Generated Preview",
        back: "Back",
        retry: "Retry",
        images: "Preview Images",
        adapter: "Adapter Payload",
        upload: "Upload Payload",
        export: "Export Payload",
        noImages: "No preview images.",
        imageCountSuffix: "images",
        mainImageAlt: "Main preview image",
        listingTitle: "Listing Title",
        shortDesc: "Short Description",
        bulletPoints: "Key Benefits",
        longDesc: "Long Description",
        faq: "FAQ",
        attributes: "Product Attributes",
        highlights: "Key Highlights",
        seoKeywords: "SEO Keywords",
        metricsTitle: "Content Overview",
        metricImages: "Images",
        metricSkus: "SKUs",
        metricAttributes: "Attributes",
        metricSections: "Sections",
        metricRawText: "Raw Text",
        priceSummary: "Product Summary",
        price: "Price",
        skuCount: "SKU Count",
        weight: "Weight",
        size: "Size",
        empty: "No data available.",
        statusReady: "Ready for review",
        statusUploaded: "Uploaded, ready to update",
        reviewBtn: "Review changes",
        publishBtn: "Publish now",
        updateBtn: "Update on Ozon",
        updateUnavailable: "This task is not uploaded yet, cannot update.",
        updateTriggered: "Update request submitted.",
        saveDraft: "Save draft",
        savingDraft: "Saving...",
        draftSaved: "Draft saved.",
        draftNoChanges: "No draft changes to save.",
        draftSaveFailed: "Failed to save draft.",
        draftUnsaved: "Draft changed, not saved",
        openPricingEngine: "Open Pricing Engine",
        setPrimaryImage: "Set as primary image",
        primaryImageCurrent: "Current primary image",
        readonlyTitle: "Read-only fields",
        tagsHint: "Enter tags separated by spaces or commas",
        oldPrice: "Old price",
        invalidPrice: "Price must be a positive number.",
        invalidOldPrice: "Old price must be a positive number.",
        invalidPriceRelation: "Old price cannot be lower than price.",
        invalidWidth: "Width must be a positive number.",
        invalidHeight: "Height must be a positive number.",
        invalidDepth: "Depth must be a positive number.",
        invalidWeight: "Weight must be a positive number.",
        invalidImages: "Keep at least one image.",
        invalidPrimaryImage: "Primary image must exist in image list.",
        ozonOnly: "Only Ozon preview is supported right now.",
        ozonPayloadMissing: "No Ozon upload payload found. Run the adapter stage first.",
        ozonListing: "Ozon Listing",
        ozonOfferId: "Ozon SKU",
        ozonCurrency: "Currency",
        ozonSpecs: "Specifications",
        ozonDescription: "Ozon Description",
        ozonTags: "Ozon Tags",
        ozonAttributes: "Ozon Attributes",
        pricingFinalized: "FINALIZED PRICING",
        pricingEngineTitle: "Pricing Engine Logic",
        pricingProfitability: "Profitability",
        pricingMargin: "Margin",
        pricingFinalPrice: "Final Listing Price",
        pricingViewBreakdown: "View Breakdown",
        pricingHideBreakdown: "Hide Breakdown",
        pricingDiscountExplainTitle: "Fixed 40% discount policy",
        pricingDiscountExplainLine: "Listing price is intentionally higher. Discount {discount}, pay factor {pay_factor}. Fill {listing_price} in Ozon backend, buyer pays about {target_price}.",
        pricingBreakdownMissing: "Pricing breakdown is not available.",
        pricingBreakdown: {
          baseCost: "Base Cost",
          platformFee: "Platform Fee",
          logistics: "Logistics",
          packaging: "Pkg & Labor",
          otherFees: "Other Fees"
        }
      },
      gallery: {
        main: "MAIN",
        empty: "No generated images",
        slot: "SLOT {index}",
        diffusing: "DIFFUSING...",
        loading: "Loading...",
        mainPlaceholder: "Hero slot",
        emptySlot: "Slot {index}",
        emptyHint: "Awaiting render",
        waiting: "Generating..."
      },
      marketplace: {
        emptyRich: "No rich content assets yet",
        widgets: "Widgets {count}",
        images: "Images {count}",
        theme: "Theme {theme}",
        emptyMapping: "No mapped attributes yet."
      },
      richContent: {
        title: "Rich Content Structure",
        summary: "{count} widgets",
        empty: "No rich content widgets yet.",
        emptyTitle: "Rich content is ready to render",
        emptyDesc: "Once the rich content stage finishes, widgets and images will appear here.",
        emptyPreview: "No rich content preview yet.",
        images: "Rich content images",
        emptyImages: "No rich content images available.",
        version: "Version",
        generatedAt: "Generated",
        blocks: "{count} blocks",
        rows: "{count} rows",
        columns: "{count} columns",
        untitled: "Untitled widget",
        noDescription: "No text content yet.",
        moreWidgets: "More widgets: {count}",
        emptyMappingTitle: "No field mapping yet",
        emptyMappingDesc: "Finish attribute generation to map fields automatically.",
        detailHint: "Structure ready",
        viewDetail: "View details",
        collapse: "Collapse",
        detailTitle: "Widget details",
        detailDesc: "Structured output from the rich content agent.",
        jsonTitle: "Widget JSON",
        blocksTitle: "Block preview",
        emptyBlocks: "No blocks generated yet.",
        galleryTitle: "Rich content image stream",
        emptyGallery: "No rich content images yet.",
        emptyGalleryDesc: "Generate rich content to populate the image stream.",
        widget: {
          showcase: "Showcase",
          text: "Text Block",
          table: "Table",
          video: "Video",
          list: "List",
          unknown: "Widget"
        }
      },
      video: {
        empty: "Video generation is not enabled yet.",
        hint: "Enable the video stage to produce motion assets.",
        recording: "RECORDING..."
      },
      status: {
        complete: "COMPLETE",
        thinking: "THINKING...",
        processing: "PROCESSING...",
        backgroundProcessing: "BACKGROUND...",
        error: "ERROR",
        waiting: "WAITING"
      },
      agentLogs: {
        title: "Agent Live Feed",
        subtitle: "Agent Stream Console",
        copyDiagnostics: "Copy diagnostics",
        copied: "Copied",
        downloadLogs: "Download logs",
        chainTitle: "AGENT LOG STREAM",
        streaming: "Streaming",
        emptyTitle: "No agent logs yet",
        emptyDesc: "Run a stage to see live agent events here.",
        logCount: "{count} logs",
        lastUpdated: "Last updated {time}",
        duration: "Duration {duration}",
        expandLog: "Expand",
        collapseLog: "Collapse",
        status: {
          running: "RUNNING",
          completed: "DONE",
          failed: "FAILED",
          idle: "IDLE"
        },
        event: {
          start: "START",
          thinking: "THINK",
          result: "RESULT",
          error: "ERROR",
          cacheHit: "CACHE",
          skip: "SKIP"
        }
      }
    },

    // Pricing Engine
    pricingEngine: {
      title: "OneClickSell Dynamic Pricing Engine",
      subtitle: "Multi-platform profit calculator with platform-specific fee logic for global marketplaces.",
      returnToPreview: "Back to preview",
      calcMode: "Calculation Mode",
      manualEntry: "Manual Entry",
      myListings: "My Listings",
      destPlatform: "Destination Platform",
      globalChannels: "{count} Global Platforms Available",
      ready: "Ready",
      soon: "Soon",
      listingsOzonOnly: "Listings sync is currently available for Ozon only.",
      inputMode: "Input Mode",
      compactMode: "Compact",
      fullMode: "Advanced",
      singlePlatformHint: "Most sellers operate on a single platform.",
      rateSource: "Live FX source",
      rateLoading: "Updating...",
      calculationLoadingTitle: "Recalculating pricing...",
      calculationLoadingDesc: "Latest Ozon fee and profit simulation is running. Please wait before retrying.",
      calculationFailed: "Pricing calculation failed",
      retryCalculation: "Retry calculation",
      rateUpdated: "Updated {date}",
      rateError: "Rate error",
      rateErrorDesc: "Failed to fetch the latest exchange rate. Please retry.",
      networkErrorDesc: "Network connection failed. Please retry.",
      requestTimeoutDesc: "Request timed out. Please retry.",
      autoRateOn: "Auto Rate",
      autoRateOff: "Manual",
      refreshRate: "Refresh Rate",
      autoRateHint: "Auto rate enabled (Frankfurter API).",
      manualRateHint: "Manual override enabled.",
      tiktokRegion: "Selling Region",
      regionUs: "United States",
      regionUk: "United Kingdom",
      regionSea: "Southeast Asia",
      tiktokRegionHint: "Switching region will update commission and transaction presets. Edit any field if your actual rate differs.",
      selectProduct: "Select Product from Workspace",
      viewDetails: "View Details",
      dataSynced: "Data Synced",
      pricingStrategy: "Pricing Strategy",
      targetMargin: "Target Margin (%)",
      targetProfit: "Target Profit (¥)",
      tieredProfit: "Tiered Profit",
      tieredProfitHint: "Configure profit by purchase-cost bands. Effective target = max(Fixed Profit, Cost × Profit Rate).",
      currentTier: "Active Tier",
      tierFixedProfit: "Fixed Profit",
      tierProfitRate: "Profit Rate",
      tieredTargetHint: "Current tier target profit: {value}",
      tierLabel: "Tier",
      targetValue: "Target Value",
      quickAdjustTag: "Live Tuning",
      quickAdjustTitle: "Adjust strategy right here",
      quickAdjustHint: "Drag slider or type value",
      quickAdjustClickHint: "Click to switch to margin editing",
      autoCalculated: "Auto-calculated from current inputs",
      breakEven: "Break-even",
      suggestedPrice: "Suggested Listing Price",
      ozonTargetPriceLabel: "Buyer Pays",
      rate: "Rate: 1 CNY = {rate} {currency} (Input)",
      breakEvenPrice: "Break-even Price: {price}",
      optimizedMsg: "Optimized for {value}% Margin",
      optimizedProfit: "Optimized for {value} Profit",
      netProfit: "Net Profit",
      profitMargin: "Profit Margin",
      targetMet: "Target Met",
      targetNotReached: "Target not reached (cap applied)",
      roi: "ROI",
      highMargin: "High Margin",
      updateListing: "Update Price in Listing",
      syncDesc: "Instantly sync this price to your Ozon listing in the workspace.",
      syncSuccess: "Price Sync Complete",
      syncSuccessDesc: "Successfully sent {price} to {platform} listing.",
      feeBreakdown: "Platform Fee Breakdown",
      catLogic: "Category Logic Applied",
      commission: "Marketplace Commission",
      lastMile: "Last-mile Delivery",
      handling: "Handling & Warehousing",
      totalOpCost: "Total Operational Cost",
      costBreakdown: "Cost Breakdown",
      exportReport: "Export Report",
      shareImage: "Generate Share Image",
      shareReady: "Share Image Ready",
      shareReadyDesc: "Share image for {platform} saved to your device.",
      shareError: "Share Image Failed",
      shareErrorDesc: "Unable to render share image. Please retry.",
      saveOzonConfig: "Save Ozon Strategy",
      savingConfig: "Saving...",
      applyOzonConfig: "Apply to Ozon Flow",
      saveAndApplyOzonConfig: "Save & Apply to Ozon Flow",
      applyingConfig: "Applying...",
      saveConfigSuccess: "Ozon strategy saved",
      saveConfigSuccessDesc: "Your custom Ozon pricing strategy has been saved.",
      saveConfig: "Save Config",
      saveConfigFailed: "Save failed",
      saveConfigFailedDesc: "Unable to save Ozon pricing strategy. Please retry.",
      applyConfig: "Apply Config",
      applyConfigSuccess: "Strategy applied",
      applyConfigSuccessDesc: "The strategy is now active for subsequent Ozon generation.",
      applyConfigTriggeredDesc: "Applied successfully and started backfilling pricing for the current product.",
      applyConfigNoCollectionDesc: "Applied successfully for subsequent generation. Open Studio product to backfill history.",
      applyConfigAutoSyncSuccessDesc: "Applied and auto-synced to preview draft (40% discount listing logic included).",
      applyConfigAutoSyncFailedDesc: "Applied successfully, but auto-sync to preview failed. Please click Sync to preview manually.",
      nextStepTitle: "Next Actions",
      nextStepSubtitle: "Apply the latest pricing, export a handoff snapshot, or continue to publish.",
      nextStepApplyCurrent: "Apply to Current Product",
      nextStepApplyCurrentDesc: "Push this pricing strategy to the selected product context.",
      nextStepExportPlan: "Export Plan",
      nextStepExportPlanDesc: "Download current pricing inputs and calculated results as JSON.",
      nextStepGoPublish: "Go to Publish",
      nextStepGoPublishDesc: "Continue with publishing workflow for the selected product.",
      nextStepDisabledCalculating: "Pricing is still recalculating. Please wait a moment.",
      nextStepDisabledInvalidPrice: "Suggested price is invalid. Adjust inputs before continuing.",
      nextStepApplyDisabledPlatform: "Apply to current product is only available for Ozon.",
      nextStepApplyDisabledAuth: "Sign in first to apply pricing to the current product.",
      nextStepApplyDisabledContext: "Select a product context before applying.",
      nextStepApplyDisabledApplying: "Applying strategy in progress. Please wait.",
      nextStepApplyDisabledSaving: "Saving strategy in progress. Please wait.",
      nextStepGoPublishDisabledAuth: "Sign in first to continue to publish.",
      nextStepGoPublishDisabledContext: "Select a product context before entering publish.",
      nextStepExportSuccess: "Pricing plan exported successfully.",
      nextStepExportFailed: "Failed to export pricing plan. Please retry.",
      applyConfigFailed: "Apply failed",
      applyConfigFailedDesc: "Unable to apply Ozon strategy. Please retry.",
      loadConfigFailed: "Load failed",
      loadConfigFailedDesc: "Unable to load your saved Ozon strategy.",
      loginRequiredTitle: "Login required",
      loginRequiredDesc: "Please sign in first to save and apply Ozon strategy.",
      loadingOzonConfig: "Loading your Ozon strategy...",
      updatedAt: "Updated: {date}",
      ozonOnlyHint: "Ozon strategy apply is available on Ozon only.",
      ozonFlowStrategyTitle: "Ozon flow strategy",
      ozonFlowStrategyDesc: "Use current calculator target directly in Ozon generation flow.",
      collectionContextTitle: "Current product context",
      contextLoading: "Loading current product context...",
      contextEmpty: "No collection context found. You can pick one from the quick list below.",
      contextPriceHint: "Current suggested CNY price: {price}",
      contextLoadFailedDesc: "Failed to load pricing context for this product.",
      collectionUnnamed: "Unnamed product",
      quickSourceTitle: "Quick product picker",
      sourceAssets: "Asset Library",
      sourceTasks: "Task List",
      sourceSearchPlaceholder: "Search by title or URL",
      sourcesLoading: "Loading products...",
      sourcesEmpty: "No matching products found.",
      sourcesLoadFailedDesc: "Failed to load quick picker list.",
      sourceUploaded: "Uploaded",
      sourcePending: "Not uploaded",
      sourceNotReadyTitle: "Unavailable",
      sourceNotReadyDesc: "This task has no collection yet, and cannot be used for pricing.",
      syncToPreview: "Sync price to preview",
      syncingPreview: "Syncing...",
      syncPreviewHint: "Push current calculated result to Studio preview draft: listing price = buyer price ÷ 0.6 (fixed 40% discount).",
      syncPreviewMissingCollection: "Missing collection context. Open from Studio preview or pick one above first.",
      syncPreviewInvalidRate: "Exchange rate invalid, unable to convert RUB to CNY.",
      syncPreviewInvalidPrice: "Suggested price invalid, unable to sync.",
      syncPreviewFailed: "Sync failed",
      syncPreviewFailedDesc: "Unable to sync latest pricing details to Studio preview.",
      syncPreviewSuccess: "Synced to preview",
      syncPreviewSuccessDesc: "Latest pricing details have been written to Studio preview draft.",
      ozonMinProfitSyncHint: "Current Ozon minimum profit: ¥{value}",
      ozonTieredProfitSyncHint: "Current Ozon tier target: ¥{value} ({range})",
      ozonStrategyTitle: "Ozon Generation Strategy",
      ozonStrategyDesc: "These settings are persisted and used by Ozon generation price stage.",
      ozonProfitRate: "Target Profit Rate",
      ozonMinProfit: "Minimum Profit",
      ozonPromotionReserve: "Promotion Reserve",
      ozonOperationMode: "Operation Mode",
      ozonAdvertisingStrategy: "Advertising Strategy",
      ozonAdvertisingNone: "None",
      ozonAdvertisingCpoCpc: "CPO / CPC",
      ozonAdvertisingAggressive: "Aggressive",
      ozonCrossBorder: "Seller Type",
      ozonCrossBorderOn: "Cross-border",
      ozonCrossBorderOff: "Domestic",
      ozonDefaultCategory: "Default Category Code",
      ozonExchangeRate: "Custom FX (optional)",
      ozonDomesticShipping: "Domestic Shipping",
      ozonLabelingFee: "Labeling Fee",
      ozonReturnRate: "Return Rate",
      ozonWithdrawalRate: "Withdrawal Rate",
      ozonOrderProcessingRate: "Order Processing Rate",
      ozonAcquiringRate: "Acquiring Rate",
      ozonAgentRate: "Ozon Agent Rate",
      ozonPartnerDeliveryRate: "Partner Delivery Rate",
      logisticsProvider: "Logistics Provider",
      logisticsProviderMode: "Provider Selection",
      logisticsProviderModeAuto: "Auto (Recommended)",
      logisticsProviderModeManual: "Manual",
      logisticsProviderUni: "UNI (Legacy)",
      logisticsProviderUral: "Ural (2026-03 Quote)",
      autoModeLabel: "Auto",
      ozonDiscountExplainTitle: "Fixed 40% discount policy",
      ozonDiscountExplainLine: "Buyer price is calculated first, then converted to listing price. Discount: {discount}, pay factor: {pay_factor}. Buyer pays {target_rub}, set listing price to {listing_rub}.",
      ozonDiscountExplainFormula: "Formula: buyer pays = listing × 0.6. So listing = {target_cny} ÷ 0.6 = {listing_cny}.",
      saveScenario: "Save Scenario",
      prodMetadata: "Product Metadata (Locked)",
      prodCategory: "Product Category",
      purchaseCost: "Purchase Cost (CNY)",
      weight: "Weight (kg)",
      dimensions: "Dimensions (L*W*H cm)",
      logistics: "Logistics",
      air: "Air",
      sea: "Sea",
      saveWorkspace: "Save this calculation to your workspace",
      saveWorkspaceDesc: "Sync directly to Ozon and track your margin history by creating a free account.",
      createAccount: "Create Free Account",
      basicCosts: "Base Costs",
      costCurrency: "Cost currency: CNY → {currency}",
      purchaseCostLabel: "Purchase Cost",
      shippingCostLabel: "Shipping Cost",
      shippingEst: "Estimated International Shipping",
      estimatedShipping: "Estimated Shipping",
      estimatedShippingHint: "Estimated from current weight, dimensions, and shipping mode.",
      packagingCostLabel: "Packaging / Prep Cost",
      otherCostLabel: "Other Cost",
      estimatedTag: "Estimated",
      shippingCharged: "Shipping Charged to Buyer",
      exchangeRateLabel: "Exchange Rate (1 CNY → {currency})",
      inputGuardrailInvalid: "Please enter a valid number ({range}). Auto-corrected to {value}.",
      inputGuardrailAdjusted: "Accepted range: {range}. Auto-corrected to {value}.",
      inputRangeBetween: "{min} - {max}",
      inputRangeMinimum: "≥ {min}",
      platformFees: "Platform Fee Inputs",
      marginCapHint: "Current practical margin cap: {value}%",
      categoryLabel: "Category / Segment",
      referralFeeLabel: "Referral Fee",
      referralRateLabel: "Referral / Commission Rate",
      commissionRate: "Commission Rate",
      referralMinFeeLabel: "Minimum Referral Fee",
      sellingPlan: "Selling Plan",
      sellingPlanIndividual: "Individual",
      sellingPlanProfessional: "Professional",
      sellingPlanFee: "Per-item Selling Fee",
      closingFee: "Closing Fee",
      closingFeeLabel: "Closing Fee",
      fulfillmentMode: "Fulfillment Mode",
      fulfillmentFba: "FBA",
      fulfillmentFbm: "FBM",
      fulfillmentFee: "Fulfillment Fee",
      storageFee: "Storage Fee",
      inboundShippingFee: "Inbound Shipping Fee",
      fbmHandlingFee: "FBM Handling / Pick Fee",
      platformDiscount: "Platform Discount",
      tax: "Tax / VAT",
      sellerDiscount: "Seller Discount",
      affiliateRate: "Affiliate Fee Rate",
      affiliatePartnerRate: "Affiliate Partner Rate",
      adsRate: "Ads / Promotion Rate",
      marketingCost: "Marketing Cost",
      shippingType: "Shipping Option",
      shippingPlatform: "Platform Shipping",
      shippingSeller: "Seller Shipping",
      shippingFbt: "FBT (Fulfilled by TikTok)",
      fbtFulfillmentFee: "FBT Fulfillment Fee",
      fbtStorageFee: "FBT Storage Fee",
      transactionFeeRate: "Transaction Fee Rate",
      transactionFeeFixed: "Transaction Fixed Fee",
      serviceFeeRate: "Service Fee Rate",
      serviceFeeFixed: "Service Fixed Fee",
      orderServiceFee: "Order Service Fee",
      platformSupportFee: "Platform Support Fee",
      platformSupportFeeHint: "Some markets charge a fixed support fee per order (e.g., MY).",
      shippingSubsidy: "Shipping Subsidy / Program Cost",
      ozonPlacementFee: "Placement / Service Fee",
      ebayFinalValueRate: "Final Value Fee Rate",
      ebayPerOrderFee: "Per-order Fixed Fee",
      ebayInsertionFee: "Insertion Fee",
      ebayInternationalRate: "International Fee Rate",
      etsyListingFee: "Listing Fee",
      etsyTransactionFee: "Transaction Fee Rate",
      etsyPaymentRate: "Payment Processing Rate",
      paymentFeeRate: "Payment Fee Rate",
      etsyPaymentFixed: "Payment Fixed Fee",
      etsyOffsiteAdsRate: "Offsite Ads Rate",
      shippingSupportRate: "Shipping Support Rate",
      totalCost: "Total Cost",
      totalBaseCost: "Total Base Cost",
      platformFeeTotal: "Platform Fees",
      baseCostTotal: "Base Cost",
      platformInsights: "Platform Insights",
      marketingCosts: "Marketing & Creator Costs",
      temuProgram: "Temu Incentives",
      aliexpressProgram: "AliExpress Campaigns",
      shopeeProgram: "Shopee Programs",
      temuHeroTitle: "Temu Low Commission Stack",
      temuHeroDesc: "Commission is often 0%. Track service fees, fulfillment, and subsidies separately.",
      temuHeroTag1: "Zero Commission",
      temuHeroTag2: "Service Fee Adjustable",
      aliexpressHeroTitle: "AliExpress Category Commission",
      aliexpressHeroDesc: "Commission varies by category. Use this panel to model payment fees and campaigns.",
      aliexpressHeroTag1: "5-8% Commission",
      aliexpressHeroTag2: "No Annual Fee",
      shopeeHeroTitle: "Shopee Fee Stack",
      shopeeHeroDesc: "Combine commission, transaction, and optional program fees.",
      shopeeHeroTag1: "Multi-fee",
      shopeeHeroTag2: "Program Optional",
      shopeeHeroTag3: "Support Fee",
      amazonHighlightTitle: "Amazon Plan & Fulfillment Snapshot",
      amazonHighlightDesc: "Model referral fees, plan charges, and FBA/FBM costs together to find the right floor price.",
      ebayHighlightTitle: "eBay Final Value Fee Focus",
      ebayHighlightDesc: "Final value fees apply to item + shipping + tax; add per-order and international fees if applicable.",
      walmartHighlightTitle: "Walmart Referral Fee Focus",
      walmartHighlightDesc: "Referral fee is category-based; WFS and other fulfillment costs can be added separately.",
      etsyHighlightTitle: "Etsy Listing Cycle Snapshot",
      etsyHighlightDesc: "Listing fee, transaction fee, payment processing, and offsite ads can be toggled for each item.",
      platformNotesAmazon: "Includes referral fee (category-based), per-item fee (Individual plan), and fulfillment fees (FBA/FBM).",
      platformNotesTikTok: "Fees may include commission (category-based), transaction fee, affiliate commission, and optional FBT fulfillment costs.",
      platformNotesOzon: "Commission depends on category/mode; shipping, withdrawal and return reserve are estimated items.",
      platformNotesEbay: "Final value fees apply to item + shipping + tax; per-order fee is added separately.",
      platformNotesWalmart: "Referral fee varies by category; other fulfillment costs are excluded in this quick calculator.",
      platformNotesEtsy: "Includes listing fee, transaction fee, payment processing fee, and optional offsite ads.",
      platformNotesTemu: "Temu commission can be 0%, while a small service fee or fulfillment cost may apply depending on program.",
      platformNotesAliExpress: "AliExpress commission varies by category; payment or order service fees may apply by region.",
      platformNotesShopee: "Shopee fees typically include commission, transaction fee, and optional program/support fees.",

      // Strategy presets (new)
      selectStrategy: "Select Pricing Strategy",
      strategyHint: "Choose based on your business goals",
      strategyVolume: "Quick Volume",
      strategyVolumeDesc: "Lower margin (15-20%) to quickly gain market share",
      strategyBalanced: "Balanced Growth",
      strategyBalancedDesc: "Moderate margin (25%) balancing sales and profit",
      strategyProfit: "Profit First",
      strategyProfitDesc: "Higher margin (35%) maximizing per-unit profit",
      strategyCustom: "Custom Strategy",
      strategyCustomDesc: "Full manual control over all parameters",
      strategyScenarioLabel: "Use Case",
      strategyRiskLabel: "Risk",
      strategyVolumeScenario: "Suitable for new launches or campaigns needing fast order momentum.",
      strategyVolumeRisk: "Lower unit margin leaves less room for ad volatility and returns.",
      strategyBalancedScenario: "Suitable for steady growth phases that need both conversion and margin.",
      strategyBalancedRisk: "Needs disciplined discount control to avoid gradual margin erosion.",
      strategyProfitScenario: "Suitable for differentiated products with stable demand and pricing power.",
      strategyProfitRisk: "Higher list price may reduce conversion if competitive pressure rises.",
      strategyCustomScenario: "Suitable when you already have validated cost and channel benchmarks.",
      strategyCustomRisk: "Manual tuning requires continuous monitoring to avoid out-of-range targets.",
      strategyImpactTitle: "Strategy Impact",
      strategyImpactSubtitle: "Key effects after switching this preset",
      strategyVolumeImpact1: "Suggested price baseline drops to improve click-through and traffic intake.",
      strategyVolumeImpact2: "Inventory turnover speed increases, but each order contributes less net profit.",
      strategyVolumeImpact3: "Ad budget should be capped tightly to prevent margin compression.",
      strategyBalancedImpact1: "Keeps margin and conversion in a middle band for stable weekly performance.",
      strategyBalancedImpact2: "Supports iterative testing without large swings in listing price.",
      strategyBalancedImpact3: "Provides enough profit buffer for moderate promotions.",
      strategyProfitImpact1: "Raises unit contribution margin to prioritize net profit per sale.",
      strategyProfitImpact2: "Requires stronger differentiation in content, reviews, and fulfillment promise.",
      strategyProfitImpact3: "Best paired with controlled traffic quality instead of broad traffic buys.",
      strategyCustomImpact1: "Current target inputs become the main pricing driver.",
      strategyCustomImpact2: "You can iterate quickly for special campaigns or category experiments.",
      strategyCustomImpact3: "Please verify range limits frequently to keep calculation outputs stable.",
      recommended: "Recommended",
      estimatedProfit: "Est. Profit",
      estimatedSales: "Est. Monthly Sales",
      profitTarget: "Profit Target",
      profitTargetHint: "Choose margin, fixed profit, or tiered profit mode.",
      enabled: "Enabled",

      // Slider
      adjusting: "Adjusting...",

      // Actions
      saving: "Saving...",
      applying: "Applying...",

      // Cost breakdown
      clickForDetails: "Click segments for details",

      // Logistics
      logisticsHint: "Estimate shipping impact based on weight and dimensions.",

      platforms: {
        amazon: "Amazon",
        tiktok: "TikTok Shop",
        ozon: "Ozon",
        temu: "Temu",
        aliexpress: "AliExpress",
        shopee: "Shopee",
        ebay: "eBay",
        walmart: "Walmart",
        etsy: "Etsy"
      }
    },

    // Copyright
    copyright: {
      nav: "Copyright Assets",
      title: "Copyright & Legal Assets",
      subtitle: "One-stop evidence bundles for instant IP enforcement and copyright protection.",
      statusActive: "Legal Security Status: Active",
      protected: "Protected Listings",
      certified: "Certified Bundles",
      onChain: "On-Chain Proofs",
      ready: "Enforcement Ready",
      filter: "Filter",
      searchPlaceholder: "Search by product name or SKU...",
      table: {
        preview: "Asset Preview",
        product: "Product / Platform",
        status: "Certification Status",
        bundle: "Legal Evidence Bundle",
        actions: "Actions",
        download: "Download Full Legal Bundle",
        previewBtn: "Preview",
        synced: "Synced: ",
        certified: "Certified",
        docs: "High-Res Docs"
      },
      bundleNav: {
        back: "Back",
        details: "Bundle Details",
        downloadCurrent: "Download Current",
        downloadAll: "Download Full Evidence Pack (.zip)",
        contents: "Bundle Contents",
        letter: "Official Authorization Letter",
        psd: "Forensic PSD Source",
        watermark: "Neural Watermark Analysis",
        blockchain: "Blockchain Proof-of-Creation",
        dispute: "IP Dispute Template",
        securityHash: "Security Hash"
      },
      tabs: {
        docView: "DOCUMENT VIEW",
        metaDash: "METADATA DASHBOARD",
        xray: "X-RAY ANALYSIS"
      },
      letter: {
        title: "Platform Authorization Letter",
        seal: "OFFICIAL SEAL APPLIED",
        certTitle: "Certificate of Authenticity",
        certSub: "Official Legal Evidence Record",
        docId: "Document ID",
        date: "Date of Issuance",
        subject: "Subject Matter Identification",
        prodName: "Product Name",
        sku: "Asset SKU",
        jurisdiction: "Platform Jurisdiction",
        grade: "Evidence Grade",
        gradeValue: "Forensic Certified",
        body1: "This document constitutes formal legal evidence that the asset described above has been verified and registered within the OneClickSell Intellectual Property Protection Vault.",
        body2: "Verification includes forensic analysis of source PSD files, embedding of neural watermarks persistent through transformation, and timestamping on the public blockchain ledger.",
        officer: "Legal Compliance Officer",
        signature: "Digitally signed: ",
        footer1: "Secure Forensic Chain: #991-AUTH-Z",
        footer2: "Proprietary Evidence Format v4.2"
      },
      psd: {
        reportTitle: "Forensic Metadata Report",
        verified: "Metadata Verified",
        fileName: "MasterSource_V4_Final.psd",
        layers: "6 Layers",
        dpi: "300 DPI",
        colorProfile: "Adobe RGB",
        colProp: "Technical Property",
        colValue: "Forensic Value",
        created: "Creation Timestamp",
        createdDesc: "File system origin date",
        fingerprint: "Neural Fingerprint (SHA-256)",
        fingerprintDesc: "Unique mathematical identity",
        author: "Author ID",
        authorDesc: "Verified creator signature",
        software: "Software Signature",
        softwareDesc: "Engine and build version",
        seed: "Embedded Watermark Seed",
        seedDesc: "Persistent neural tracer",
        engine: "Analysis Engine",
        integrity: "Integrity Check",
        secure: "100% SECURE"
      },
      watermark: {
        title: "Neural Watermark X-Ray",
        scanning: "Scanning Neural Layers...",
        status: "Status",
        detected: "NEURAL WATERMARK DETECTED",
        score: "Resilience Score",
        stable: "CRYPTO-STABLE",
        encodingTitle: "Neural Encoding",
        encodingDesc: "This watermark is embedded at the neural level during generative diffusion. Unlike traditional pixel overlays, this signature is part of the image's weights, making it resistant to AI-based erasure, cropping, or noise injection.",
        latentSeed: "Latent Seed",
        engineSig: "Engine Signature",
        inference: "Inference Time",
        confidence: "Scan Confidence"
      },
      blockchain: {
        title: "Blockchain Verification Certificate",
        subtitle: "Immutable Proof of Asset Origination",
        verified: "Verified On-Chain",
        network: "Network:",
        networkVal: "Arbitrum One / Ethereum Mainnet",
        txHash: "Transaction Hash (TxID)",
        signature: "Digital Signature (EdDSA)",
        block: "Block Height",
        timestamp: "Timestamp (UTC)",
        legalNoteTitle: "Legal Admissibility Note",
        legalNoteDesc: "This certificate provides a cryptographically secure timestamp and authorship record. The record is permanently anchored to the Arbitrum L2 network, inheriting the full security of the Ethereum settlement layer.",
        scan: "Scan to verify",
        verifStatus: "Verification Status",
        protocol: "OneClick-ChainLink Oracle v2",
        confirmed: "CONFIRMED",
        confirmations: "Node Confirmations",
        integrity: "Data Integrity",
        secure: "100% SECURE"
      },
      dispute: {
        preview: "PREVIEW DOCUMENT",
        settings: "TEMPLATE SETTINGS",
        language: "Language:",
        title: "IP Infringement Takedown Notice",
        subtitle: "Automated Legal Compliance Document",
        to: "To: Legal Compliance Department",
        ref: "Ref:",
        date: "Date:",
        s1Title: "1. Statement of Ownership",
        s1Body: "I, the undersigned, hereby declare under penalty of perjury that I am the authorized representative of the copyright owner for the intellectual property described herein. OneClickSell Inc. maintains exclusive rights to the proprietary digital assets associated with this claim.",
        s2Title: "2. Description of Infringement",
        s2Body: "We have identified unauthorized use of our copyrighted visual assets at the following location:",
        s2Url: "URL:",
        s2Desc: "This listing utilizes proprietary product imagery and creative layouts that are identical or substantially similar to our original works.",
        s3Title: "3. Evidence of Originality",
        s3Body: "The originality and creation date of the disputed assets are verified through the following cryptographic methods included in the attached Evidence Pack:",
        s3Item1: "Neural Watermark:",
        s3Item1Desc: " Invisible forensic markers embedded within the image data matching our master files.",
        s3Item2: "Blockchain Proof:",
        s3Item2Desc: " Immutable proof-of-creation recorded on the Arbitrum Mainnet.",
        s4Title: "4. Legal Declaration",
        s4Body: "I have a good faith belief that the use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law. The information in this notification is accurate. We request the immediate removal of the infringing material.",
        signature: "Authorized Signature",
        division: "IP Protection Division, OneClickSell Inc.",

        sidebar: {
          verifStatus: "Verification Status",
          link: "Infringing Link",
          linkDesc: "URL provided",
          psd: "Forensic PSD",
          psdDesc: "Layer data verified",
          evidence: "Additional Evidence",
          missing: "2 modules missing",
          prob: "Enforcement Probability",
          lowConf: "Low Confidence",
          guide: "Evidence Completion Guide",
          scan: "Neural Watermark Scan",
          boost30: "Missing: +30% boost",
          complete: "Complete Now",
          proof: "Blockchain Proof",
          boost22: "Missing: +22% boost",
          autoFiling: "Automated Filing",
          filingNote: "Filing not recommended until confidence is above 80%.",
          autoSubmit: "Auto-Submit Claim",
          processing: "Processing...",
          submitted: "Claim Submitted"
        }
      }
    },

    // Workspace
    workspace: {
      nav: "Workspace",
      heroTitle: "Create Your First AI Listing",
      heroSub: "Paste a link → AI Optimization → One-click Publishing. Transform product data in seconds.",
      returningLabel: "Welcome back",
      returningTitle: "Pick up where you left off",
      returningSub: "Your latest imports are ready. Continue polishing listings or jump into the task center.",
      startBtn: "Start Importing Products",
      taskCenterEntry: "Open Task Center",
      pasteLink: "Paste Link",
      pasteDesc: "Import from AliExpress, 1688, Amazon and more.",
      uploadMedia: "Upload Media",
      uploadDesc: "Drag & drop images, videos or ZIP files.",
      batchImport: "Batch Import",
      batchDesc: "Import products in bulk via CSV or multiple URLs.",
      recentImports: "Recent Imports",
      recentImportsDesc: "Your latest imports and their current status",
      viewHistory: "View History",
      extRequired: "Chrome Extension Required",
      extNotFound: "Not Found",
      extOutdated: "Outdated",
      extDesc: "Required for secure data collection from major platforms.",
      extDownload: "Download Extension",
      extUpdate: "Update Extension",
      extRecheck: "I've Updated",
      extUpgradeRequired: "Extension update required",
      extUpgradeDesc: "Current version {current}. Minimum required is {required}. Please update before importing.",
      extVersionUnknown: "Unknown",
      watchTutorial: "Watch Video Tutorial",
      readGuide: "Read User Guide",
      status: {
        processing: "Processing",
        ready: "Ready to Publish",
        uploaded: "Uploaded",
        failed: "Failed"
      },
      loadFailed: "Failed to load recent imports.",
      loadHint: "Please retry to fetch your latest tasks.",
      retry: "Retry",
      emptyTitle: "No imports yet",
      emptyDesc: "Start by importing a product link to see progress here.",
      loginTitle: "Log in to view your imports",
      loginDesc: "Sign in to sync your import history and workflow status.",
      loginAction: "Log In",
      unknownSource: "Unknown Source",
      totalImports: "Total Imports",
      completed: "Completed",
      processing: "Processing"
    },

    // Task Center
    taskCenter: {
      nav: "Workbench",
      breadcrumb: {
        workspace: "Workspace",
        taskCenter: "Task Center"
      },
      title: "Unified Workbench",
      subtitle: "Manage tasks and completed assets in one page with less context switching.",
      target: {
        label: "Target:",
        platform: "Amazon",
        region: "United States",
        language: "English"
      },
      sync: {
        label: "Sync Progress",
        value: "85%"
      },
      reviewChanges: "Review Changes",
      noReviewableTasks: "No changes to review yet.",
      createTask: "Create New Task",
      dropdown: {
        singleTitle: "Single Product Task",
        singleDesc: "Import via a single URL",
        batchTitle: "Batch Import Task",
        batchDesc: "Import via multiple URLs or CSV"
      },
      table: {
        source: "Source",
        task: "Task Name / ID",
        status: "Status",
        progress: "AI Progress",
        createdAt: "Created At",
        action: "Action"
      },
      status: {
        active: "Active",
        completed: "Completed",
        waiting: "Waiting",
        idle: "Idle",
        collect_pending: "Waiting for capture",
        assigned: "Capture assigned",
        collecting: "Collecting",
        collect_uploading: "Uploading",
        collect_completed: "Capture complete",
        collect_failed: "Capture failed",
        collect_cancelled: "Capture cancelled",
        collect_expired: "Capture expired",
        pending_generate: "Waiting to generate",
        generating: "Generating",
        publishing: "Publishing",
        ready: "Ready to publish",
        uploaded: "Published",
        publish_failed: "Publish failed",
        generation_failed: "Generation failed"
      },
      batchLabel: "Batch",
      itemsLabel: "Items",
      progress: {
        processing: "Processing",
        copywriting: "AI Copywriting",
        inQueue: "In Queue"
      },
      stage: {
        collect_pending: "Waiting for capture",
        collect_assigned: "Plugin assigned",
        collect_preparing: "Preparing capture",
        collect_scrolling: "Scrolling page",
        collect_collecting: "Collecting data",
        collect_saving: "Saving data",
        collect_uploading: "Uploading assets",
        collect_completed: "Capture complete",
        collect_failed: "Capture failed",
        collect_cancelled: "Capture cancelled",
        collect_expired: "Capture expired",
        generate_pending: "Waiting to generate",
        generate_info_structure: "Product understanding",
        generate_seo: "SEO keyword graph",
        generate_listing: "Listing writing",
        generate_visual: "Visual strategy",
        generate_adapter: "Platform adapter",
        generate_export: "Export delivery",
        publish_pending: "Preparing publish",
        publish_processing: "Publishing",
        publish_scheduled: "Scheduled",
        publish_failed: "Publish failed",
        publish_completed: "Published",
        generate_ready: "Ready to publish",
        generate_uploaded: "Published",
        generate_failed: "Generation failed"
      },
      actions: {
        workspace: "Workspace",
        preview: "Preview",
        generate: "Start AI",
        publish: "Publish",
        viewPublish: "View Publish",
        update: "Update Listing",
        batchOverview: "Batch Overview",
        assets: "Open Asset Surface",
        retry: "Retry",
        retryCollect: "Retry Capture",
        retryGenerate: "Retry Generate",
        forceStopRetry: "Force Stop & Retry",
        pending: "Pending",
        archive: "Archive",
        delete: "Delete"
      },
      tooltips: {
        previewLocked: "Preview is available after collection completes",
        previewUnavailable: "Preview requires a completed capture task",
        previewCollecting: "Capture in progress. Preview will be available soon.",
        previewFailed: "Capture failed. No preview data available.",
        expand: "Expand batch",
        collapse: "Collapse batch",
        errorFallback: "Task failed. Please retry or contact support.",
        workspaceLocked: "Workspace is available once capture and AI processing are complete",
        actionLocked: "Task is processing. Try again later."
      },
      confirmArchive: "Archive this task?",
      confirmDelete: "Delete this task? This action cannot be undone.",
      actionFailed: "Operation failed. Please try again.",
      generateNotReady: "Collection data is still syncing. Please refresh and try again.",
      retryUnavailable: "This task does not have a retryable capture ID. Please import again to start a new task.",
      errorSummaryDefault: "Task failed. Please retry.",
      manualReview: {
        shortHint: "Check Ozon backend. The item may have been partially created.",
        detailHint:
          "Ozon can return failed status while some data is already created. Open Ozon Seller backend to review exact errors and manually fix fields if needed. AI-generated data is not always 100% accurate."
      },
      pagination: {
        showing: "Showing {count} of {total} tasks"
      },
      stats: {
        total: "Total",
        active: "Active",
        completed: "Completed",
        failed: "Failed",
        waiting: "Waiting",
        successRate: "Success Rate",
        avgTime: "Avg. Completion Time",
        performance: "Performance"
      },
      bulk: {
        selected: "{count} selected",
        retry: "Retry",
        archive: "Archive",
        delete: "Delete",
        selectAll: "Select All",
        deselectAll: "Deselect All",
        confirmArchive: "Archive {count} selected tasks?",
        confirmDelete: "Delete {count} selected tasks? This cannot be undone."
      },
      view: {
        cards: "Cards",
        list: "List",
        compact: "Compact"
      },
      batch: "Batch",
      refresh: "Refresh",
      exportCSV: "Export CSV",
      clearFilters: "Clear Filters",
      importComposerToggleExpand: "Show Quick Import",
      importComposerToggleCollapse: "Hide Quick Import",
      surfaceTasks: "Task Queue",
      surfaceAssets: "Asset Results",
      focusTitle: "This view is for scheduling and recovery",
      focusHint: "Use this queue for running or failed jobs. Completed listings belong to the Asset Results surface.",
      focusShowAll: "Show All Tasks",
      focusGoAssets: "Open Asset Surface",
      assetsFocusTitle: "This view is for completed asset operations",
      assetsFocusHint: "Review ready/published results here. For retries and failures, switch back to Task Queue.",
      assetsFilterAll: "All Results",
      assetsFilterReady: "Ready to Publish",
      assetsFilterUploaded: "Published",
      assetsFilterFailed: "Needs Fix",
      filterPanelExpand: "Show Filters",
      filterPanelCollapse: "Hide Filters",
      shortcuts: {
        title: "Keyboard Shortcuts",
        refresh: "Refresh tasks",
        selectAll: "Select all",
        bulkArchive: "Bulk archive",
        bulkDelete: "Bulk delete",
        bulkRetry: "Bulk retry",
        gridView: "Grid view",
        listView: "List view",
        compactView: "Compact view",
        search: "Search",
        newTask: "New task"
      },
      banner: {
        template: "{pipeline}. {brand}. Targeting {target}. Workspace access available upon completion.",
        pipelineMulti: "Multi-agent pipeline active",
        pipelineLite: "Lightweight pipeline active",
        brandOn: "Brand Protection enabled",
        brandOff: "Brand Protection disabled"
      },
      loading: "Loading tasks...",
      emptyTitle: "No tasks yet",
      emptyHint: "Import a product or start a batch to see progress here.",
      emptyAction: "Create Task",
      emptyImportAction: "Import Products",
      emptySampleAction: "View Sample",
      emptyHelpAction: "Help Docs",
      emptyNoActiveTitle: "No active tasks right now",
      emptyNoActiveHint: "Review historical tasks or switch to Asset Results for completed listings.",
      emptyNoActivePrimaryAction: "View All Tasks",
      emptyNoActiveSecondaryAction: "Open Asset Results",
      assetsEmptyTitle: "No completed assets yet",
      assetsEmptyHint: "Create or run tasks first. Completed listings will appear in this asset surface automatically.",
      assetsEmptyPrimaryAction: "Create Task",
      assetsEmptySecondaryAction: "Back to Task Queue",
      loginHint: "Log in to unlock Task Center tracking",
      loginDesc: "Sign in to review collection, AI generation, and upload progress.",
      loginAction: "Log In",
      loadFailed: "Failed to load tasks",
      retryHint: "Please refresh or try again later.",
      retryAction: "Reload",
      modal: {
        title: "Batch Import Task",
        subtitle: "Add multiple products to your catalog simultaneously.",
        productUrls: "Product URLs",
        totalLinks: "Total Unique Links:",
        onePerLine: "One URL per line",
        runModeTitle: "Run Mode",
        runMode: {
          collect: {
            title: "Capture only",
            desc: "Only capture product data. No AI generation or publishing."
          },
          generate: {
            title: "Capture + AI",
            desc: "Capture and run AI generation. Stop at \"Ready to publish\"."
          },
          publish: {
            title: "One-click publish",
            desc: "Capture + AI + automatically publish to selected stores."
          }
        },
        recommended: "Recommended",
        storeTitle: "Publish Stores",
        storeNotBoundTitle: "No Ozon store connected",
        storeNotBoundDesc: "Connect your Ozon API credentials first to enable publishing.",
        storeBindAction: "Connect",
        storeSelected: "{count} selected",
        storeSelectAll: "Select all",
        storeClear: "Clear",
        defaultStore: "Default",
        scheduleTitle: "Publish Time",
        scheduleNow: "Publish now",
        scheduleLater: "Schedule",
        scheduleHint: "Time is based on the system timezone.",
        advancedTitle: "Advanced",
        skipImagesTitle: "Skip image generation",
        skipImagesDesc: "Faster, text-first generation. You can add images later.",
        useCacheTitle: "Reuse cache",
        useCacheDesc: "Reuse previous AI results when available to save time and credits.",
        emptyWarning: "Please add at least one product URL.",
        storeNotBound: "You have not connected an Ozon store yet. Go connect now?",
        storeEmptyWarning: "Please select at least one store to publish.",
        scheduleEmptyWarning: "Please choose the scheduled date and time.",
        fileEmptyWarning: "No valid URLs found in the file.",
        duplicateWarning: "{count} duplicate links detected and automatically removed.",
        uploadTitle: "Upload TXT or CSV file",
        uploadSubtitle: "Max 500 links per batch. One URL per line.",
        uploadHint: "Need a template? Download the example file",
        browseFiles: "Browse Files",
        cancel: "Cancel",
        startBatch: "Start Batch Import",
        startCollect: "Start Capture",
        startGenerate: "Start Capture + AI",
        startPublish: "Start One-click Publish",
        batchPartial: "Batch import partially succeeded",
        batchSuccess: "Batch import submitted."
      },
      batchOverview: {
        title: "Batch Overview",
        subtitle: "See publishing results for every product across stores.",
        back: "Back",
        batchIdLabel: "Batch ID:",
        publishBatch: "Publish to stores",
        publishMore: "Publish to more stores",
        manageSchedule: "Manage schedule",
        refresh: "Refresh",
        exportFailedCsv: "Export failed CSV",
        exportingCsv: "Exporting...",
        exportFailedCsvMessage: "Failed to export. Please try again.",
        loading: "Loading batch overview...",
        loadFailed: "Failed to load batch overview.",
        retryLoad: "Reload",
        empty: "No tasks found in this batch yet.",
        hint: "Tip: Click a store status to open detailed publish progress.",
        searchPlaceholder: "Search title / ID / link...",
        search: "Search",
        clear: "Clear",
        viewLabel: "View",
        view: {
          all: "All",
          failed: "Failed",
          publishing: "Publishing",
          completed: "Completed",
          scheduled: "Scheduled"
        },
        storeColumns: "Store columns",
        storeColumnsHint: "{shown}/{total} shown",
        storeColumnsManage: "Manage",
        pagination: "Showing {start}-{end} of {total}",
        prev: "Prev",
        next: "Next",
        retryAllFailed: "Retry all failed",
        retryStoreFailed: "Retry {count} failed",
        retryItemFailed: "Retry failures",
        retrying: "Retrying...",
        retrySubmitted: "Retry submitted.",
        retryFailed: "Retry failed. Please try again.",
        retryConfirm: "Retry failed publishes in this batch?",
        retryStoreConfirm: "Retry failed publishes for this store?",
        relaunchModal: {
          title: "Smart Recovery Relaunch",
          subtitle: "Scope: {scope}. We analyzed each failed publish and prepared recommended actions.",
          scopeBatch: "Whole batch",
          scopeStore: "Current store",
          scopeSelection: "Selected failures",
          planFailed: "Failed to build recovery plan.",
          noActionable: "No auto-relaunchable failures found in this scope.",
          reasonFallback: "No detailed reason returned.",
          originalSku: "Original SKU",
          newSkuLabel: "New SKU",
          newSkuPlaceholder: "Enter a new SKU",
          newSkuHint: "Only letters, numbers, _ and - are allowed. Max length 50.",
          manualHint: "This failure needs manual handling first (store auth / content fix / support).",
          skippedTitle: "Skipped items ({count})",
          selectedCount: "{count} item(s) selected for relaunch",
          selectAtLeastOne: "Select at least one actionable item.",
          offerIdRequired: "Please complete new SKU before submitting.",
          confirmExecute: "Submit relaunch for {count} item(s)?",
          submit: "Submit Relaunch",
          submitting: "Submitting...",
          executeFailed: "Batch relaunch failed. Please try again.",
          executeSuccess: "Relaunch submitted: {created} created, {skipped} skipped.",
          actions: {
            retry_with_new_offer_id: "New SKU relaunch",
            retry_same_payload: "Retry same payload",
            fix_content_then_retry: "Fix content first",
            store_auth_required: "Store auth required",
            wait_and_poll: "Wait and poll",
            contact_support: "Contact support"
          },
          summary: {
            total: "Analyzed",
            newSku: "New SKU",
            retry: "Same payload",
            manual: "Manual"
          },
          skipped: {
            NOT_IN_BATCH: "Not in this batch",
            NOT_FOUND: "Publish record not found",
            NOT_RETRYABLE_STATUS: "Status is not retryable",
            STORE_FILTERED: "Filtered by store",
            COLLECTION_ID_MISSING: "Missing collection reference",
            STORE_NOT_BOUND: "Store not bound or inactive",
            STRATEGY_NOT_ALLOWED: "Selected strategy does not match failure type",
            INVALID_STRATEGY: "Invalid strategy",
            EMPTY_OFFER_ID: "New SKU is empty",
            INVALID_OFFER_ID_FORMAT: "New SKU format is invalid",
            DUPLICATE_IN_REQUEST: "New SKU duplicated in this request",
            EXISTS_IN_LOCAL: "New SKU already exists in this system",
            EXISTS_IN_OZON: "New SKU already exists in Ozon store",
            CHECK_OZON_FAILED: "Failed to check SKU availability on Ozon",
            OFFER_ID_UNAVAILABLE: "New SKU is unavailable",
            RECOVERY_PLAN_BUILD_FAILED: "Failed to prepare SKU suggestion",
            DUPLICATE_PUBLISH_ID: "Duplicated publish id in request",
            UNKNOWN: "Unknown reason"
          }
        },
        missingBatch: "Missing batch ID.",
        notStarted: "Not started",
        notSelected: "Not selected",
        storeInactive: "Inactive",
        openLink: "Open listing",
        failedCount: "{count} failed",
        storeFilter: {
          title: "Store columns",
          subtitle: "Choose which stores to display in the table.",
          selected: "{count} selected",
          selectAll: "Select all",
          activeOnly: "Active only",
          clear: "Clear",
          save: "Save",
          empty: "Please select at least one store column."
        },
        publishModal: {
          title: "Publish batch",
          subtitle: "Select stores and schedule the publish time.",
          storeTitle: "Stores",
          selectAll: "Select all",
          clear: "Clear",
          noActiveStores: "No active stores available. Please connect or enable a store first.",
          storeEmpty: "Please select at least one store to publish.",
          scheduleTitle: "Publish time",
          scheduleNow: "Publish now",
          scheduleLater: "Schedule",
          scheduleHint: "Time is based on the system timezone.",
          scheduleEmpty: "Please choose the scheduled date and time.",
          scheduleInvalid: "Invalid scheduled time.",
          submit: "Submit",
          submitting: "Submitting...",
          submitted: "Publish submitted.",
          failed: "Failed to submit publish."
        },
        scheduleModal: {
          title: "Manage schedule",
          subtitle: "Cancel or reschedule scheduled publishes for a store.",
          storeTitle: "Store",
          storePlaceholder: "Select a store",
          storeEmpty: "Please select a store.",
          actionTitle: "Action",
          cancel: "Cancel schedule",
          reschedule: "Reschedule",
          timeTitle: "New time",
          scheduleHint: "Time is based on the system timezone.",
          scheduleEmpty: "Please choose the scheduled date and time.",
          scheduleInvalid: "Invalid scheduled time.",
          submit: "Submit",
          submitting: "Submitting...",
          submitted: "Schedule updated.",
          failed: "Failed to update schedule."
        },
        summary: {
          items: "Products",
          completed: "Published",
          failed: "Failed",
          processing: "Publishing",
          scheduled: "Scheduled"
        },
        table: {
          product: "Product",
          actions: "Actions"
        }
      }
    },

    orders: {
      nav: "Order Center",
      title: "Order & Procurement Center",
      subtitle: "Review Ozon orders, track internal fulfillment, and jump back to the original 1688 source for procurement.",
      refresh: "Refresh orders",
      retry: "Retry",
      loading: "Loading orders...",
      loadFailed: "Failed to load orders",
      loadFailedDetail: "We could not load the latest order list right now.",
      detailLoadFailed: "Failed to load order details",
      detailLoadFailedDetail: "We could not load the latest order detail right now.",
      loginTitle: "Log in to review your orders",
      loginDesc: "Sign in first to load Ozon orders and update fulfillment progress.",
      loginAction: "Log In",
      emptyTitle: "No orders yet",
      emptyDesc: "Once real Ozon orders are synced, you can review procurement and fulfillment here.",
      filteredEmptyTitle: "No orders match the current filters",
      filteredEmptyDesc: "Clear one or more filters to review other orders.",
      detailEmptyTitle: "Select an order to continue",
      detailEmptyDesc: "Open one order from the list to view procurement links and fulfillment actions.",
      save: "Save fulfillment",
      saving: "Saving...",
      saveSuccess: "Fulfillment details saved.",
      saveFailed: "Failed to save fulfillment details.",
      itemSave: "Save item progress",
      itemSaving: "Saving item...",
      itemSaveSuccess: "Item fulfillment updated.",
      itemSaveFailed: "Failed to update item fulfillment.",
      shipToOzon: "Sync shipment to Ozon",
      shippingToOzon: "Syncing shipment...",
      shipToOzonSuccess: "Tracking and shipment synced to Ozon.",
      shipToOzonFailed: "Failed to sync shipment to Ozon.",
      shipMissingStore: "The store context for this order is missing.",
      productIdCopied: "1688 product ID copied.",
      productIdCopyFailed: "Failed to copy product ID.",
      matchedSource: "Source matched",
      unmatchedSource: "Source not matched",
      unknown: "Unknown",
      results: "Showing {visible} / {total} orders",
      itemsTitle: "Order items",
      itemsHint: "Review the item snapshot and matched offer/SKU for this order.",
      itemsCount: "{count} items",
      moreItems: "+{count} more items",
      noItems: "No item snapshot returned yet.",
      quickActionsTitle: "Quick procurement actions",
      quickActionsHint: "Open the original 1688 link, copy the supplier product ID, or jump to the published Ozon page.",
      fulfillmentPanelTitle: "Internal fulfillment",
      fulfillmentPanelHint: "Manage procurement and shipping progress inside the workspace without leaving this page.",
      timelineTitle: "Fulfillment timeline",
      detailTitle: "Order detail",
      trackingPlaceholder: "Enter an internal tracking number. This value will also be synced to Ozon when you confirm shipment.",
      itemNotePlaceholder: "Add item-level procurement notes or exceptions",
      notePlaceholder: "Add procurement notes, supplier feedback, or exceptions here",
      filters: {
        store: "Store",
        allStores: "All stores",
        ozonStatus: "Ozon status",
        allStatuses: "All Ozon statuses",
        fulfillmentStatus: "Fulfillment status",
        allFulfillmentStatuses: "All fulfillment statuses",
        search: "Search",
        searchPlaceholder: "Search posting number, offer ID, 1688 ID, SKU, or item name",
        clear: "Clear filters"
      },
      actions: {
        open1688: "Open 1688 source",
        copyProductId: "Copy 1688 ID",
        viewPublish: "Open Ozon page",
        openOrderCenter: "View orders",
        goPurchase: "Go purchase on 1688"
      },
      labels: {
        postingNumber: "Posting",
        store: "Store",
        totalPrice: "Total price",
        inProcessAt: "Created",
        shipmentDate: "Ship by",
        substatus: "Substatus",
        noSubstatus: "No substatus",
        offerId: "Offer ID",
        noOfferId: "No offer ID matched",
        productId: "1688 ID",
        noProductId: "No 1688 ID matched",
        collectionId: "Collection ID",
        matchSource: "Match result",
        fulfillmentStatus: "Fulfillment status",
        ozonTrackingNumber: "Ozon tracking",
        trackingNumber: "Internal tracking",
        note: "Notes"
      },
      timeline: {
        purchasedAt: "Purchased at",
        receivedAt: "Received at",
        readyToShipAt: "Ready to ship at",
        shippedAt: "Marked shipped at",
        ozonShipmentDate: "Ozon ship deadline"
      },
      fulfillmentStatus: {
        pending_purchase: "Pending purchase",
        purchased: "Purchased",
        received: "Received",
        ready_to_ship: "Ready to ship",
        shipped: "Shipped",
        issue: "Issue"
      }
    },

    // Import
    import: {
      nav: "Import Product",
      title: "Import Product Source",
      subtitle: "Paste your supplier link to begin AI optimization.",
      extActive: "Browser Extension Active",
      connected: "Connected",
      extActiveDesc: "The companion is active. You can import directly from suppliers.",
      extSettings: "Extension Settings",
      extMissing: "Extension Required",
      extMissingDesc: "Please install the companion to scrape product data locally.",
      extUpgradeRequiredDesc: "Current extension version {current}. Please update to {required} or later.",
      extVersionUnknown: "unknown",
      extUpdateBtn: "Update Extension",
      extRecheckBtn: "I've Updated",
      extRecheckGuide: "Rechecking extension status...",
      downloadBtn: "Download Extension",
      downloadGuide: "Opening extension download...",
      urlLabel: "Product URL",
      urlSub: "Supports AliExpress, 1688, Amazon, Shopify, and more.",
      urlPlaceholder: "Paste your product link here...",
      urlRequired: "Please enter a product URL.",
      authRequired: "Please log in to start collection.",
      guestFailed: "Unable to start guest session. Please try again.",
      consent: {
        title: "Before you continue",
        subtitle: "To start a guest session, please read and agree to the Terms of Service and Privacy Policy.",
        note: "You can continue as a guest. We will only use your data to provide the import and AI optimization services.",
        cancel: "Cancel",
        continue: "Agree & Continue"
      },
      importBtn: "Import",
      submitting: "Submitting...",
      collectFailed: "Failed to submit collection task.",
      uploadTitle: "Upload Media Assets",
      uploadSub: "Drag and drop images, videos or ZIP folders here",
      chooseFiles: "Choose Files",
      alternative: "Alternative",
      targetGlobal: "Target: Global Market",
      taskCenterEntry: "Task Center",
      taskCenterHint: "Log in to track progress in Task Center.",
      recent: {
        title: "Recent Task",
        subtitle: "Latest import: {status}",
        taskId: "Task ID",
        source: "Source URL",
        createdAt: "Created",
        statusHint: "We will notify you when the collection finishes.",
        continue: "Continue",
        hint: {
          processing: "Capture is running. Keep this page open or track progress in Task Center.",
          queued: "Task is queued. Capture will start shortly.",
          completed: "Capture complete. Continue to preview and configure AI settings.",
          failed: "Capture failed. Check Task Center for details or retry.",
          generating: "AI is generating. Track progress in Task Center.",
          publishing: "Publishing in progress. View publish progress.",
          publishFailed: "Publishing failed. Review result details and follow manual review guidance.",
          ready: "Generation complete. Continue to publish.",
          uploaded: "Published. View it in Assets."
        },
        viewTaskCenter: "View Task Center",
        loginToView: "Log in to View",
        status: {
          processing: "Processing",
          queued: "Queued",
          completed: "Completed",
          failed: "Failed"
        }
      },
      success: {
        title: "Task Created",
        subtitle: "We started collecting your product data. Continue to preview or track it in Task Center.",
        taskId: "Task ID",
        source: "Source URL",
        status: "Status",
        statusProcessing: "Processing",
        statusHint: "Collection is running in the background.",
        continue: "Continue Import",
        goTaskCenter: "Go to Task Center",
        loginTaskCenter: "Log in to View Tasks",
        loginHint: "Log in to unlock Task Center tracking."
      }
    },

    // General Info Settings
    generalInfo: {
      title: "General Information",
      subtitle: "Manage your account details and profile information.",
      proPlan: "Pro Plan",
      upgradePlan: "Upgrade Plan",
      uploadNew: "Upload New",
      remove: "Remove",
      photoHint: "At least 400x400 px recommended. JPG or PNG is allowed.",
      fullName: "Full Name",
      fullNamePlaceholder: "e.g. Alex Chen",
      professionalEmail: "Professional Email",
      emailPlaceholder: "name@company.com",
      emailReadonlyHint: "Email is currently read-only.",
      companyName: "Company Name",
      companyPlaceholder: "Enter your company name",
      cancel: "Cancel",
      saveChanges: "Save Changes",
      saving: "Saving...",
      savedChanges: "Saved Changes",
      saveFailed: "Failed to save changes.",
      avatarUploadFailed: "Avatar upload failed.",
      avatarUpdated: "Avatar updated.",
      avatarRemoved: "Avatar removed.",
      footer: "Platform Settings"
    },

    // Billing & Credits
    billing: {
      title: "Billing & Credits",
      subtitle: "Track credit usage, image billing rules, and your full payment history.",
      currentBalance: "Current Balance",
      credits: "Credits",
      balanceHint: "Subscription credits reset each billing cycle; top-up credits never expire.",
      topUp: "Top Up",
      subscriptionPlan: "Subscription Plan",
      proPlanPrice: "Pro Plan · 560 credits/mo ($49)",
      nextRenewal: "Next renewal:",
      noPlan: "No active plan",
      noPlanHint: "No active subscription",
      loading: "Loading billing data...",
      loadFailed: "Failed to load billing data.",
      emptyHistory: "No billing history yet.",
      paymentSuccess: "Subscription payment completed.",
      paymentCanceled: "Subscription payment canceled.",
      rechargeSuccess: "Top-up completed.",
      rechargeCanceled: "Top-up canceled.",
      refresh: "Refresh",
      syncing: "Syncing...",
      summaryTotal: "Total Credits",
      summarySubscription: "Subscription Credits",
      summaryRecharge: "Top-up Credits",
      summaryTrial: "Trial Credits",
      statusActive: "Active",
      statusInactive: "Inactive",
      badgeSubscription: "Subscription",
      badgeRecharge: "Top-up",
      badgeAdjustment: "Adjustment",
      badgeRefund: "Refund",
      badgePromo: "Promo",
      badgeUsage: "Usage",
      badgeOther: "Other",
      usageImageGenerate: "Image generation · {count} images",
      usageImageRefund: "Image refund · {count} images",
      managePlan: "Manage Plan",
      billingHistory: "Billing History",
      date: "Date",
      description: "Description",
      amount: "Amount",
      invoice: "Invoice",
      proSubscription: "Pro Plan Subscription (560 Credits)",
      creditTopUp: "Credit Top-up (300 Credits)",
      downloadAll: "Export billing history",
      exportRow: "Export row",
      exportSuccess: "Billing history exported.",
      exporting: "Exporting...",
      footer: "All prices are in USD. Subscription credits reset each billing cycle; top-up credits never expire.",
      footerCopyright: "Financial Operations",
      pricingTitle: "Pricing Rules",
      ruleImagePrice: "Image generation costs {creditsPerImage} credits per image (${usdPerImage}).",
      ruleExchange: "1 credit = ${usdPerCredit}.",
      ruleRollover: "Subscription credits reset each billing cycle; top-up credits never expire.",
      ruleGift: "New users get {registerGiftCredits} credits; guests get {guestTrialCredits} credits.",
      imageUsageTitle: "Image usage by mode (Last {days} days)",
      imageUsageHint: "Counts billed image-generation records only; refunds are shown in the history list.",
      imageUsageTotal: "Total",
      imageUsageUnknownHint: "Other: {images} images · {credits} credits.",
      faqTitle: "FAQ",
      faq: {
        q1: "Why only charge for image generation?",
        a1: "Images are the primary cost driver; text and other modules are free to keep pricing simple.",
        q2: "Do subscription credits roll over?",
        a2: "No. Subscription credits reset each billing cycle. Top-up credits are permanent.",
        q3: "What happens if generation fails?",
        a3: "Failed images are refunded automatically based on the audit record.",
        q4: "Can I top up while subscribed?",
        a4: "Yes. Top-up credits stack on top of your subscription credits.",
      }
    },

    purchaseCredits: {
      title: "Purchase Credits",
      subtitle: "Choose a credit pack for image generation ({plan} · {creditsPerImage} credits per image).",
      priceHint: "1 credit = ${usdPerCredit}. Top-up credits never expire.",
      planSectionTitle: "Image Generation Mode",
      planThirdParty: "Value",
      planThirdPartyDesc: "Partner channels · best for everyday batch generation with fewer credits per image.",
      planOfficial: "Official",
      planOfficialDesc: "More stable for critical tasks, costs more credits per image.",
      planRecommended: "Recommended",
      planUnavailable: "This mode is temporarily unavailable. Please contact the admin.",
      planUnavailableTag: "Unavailable",
      noChannelsHint: "No available AI channels are configured. Please contact the admin to enable them.",
      creditsUnitPerImage: "credits/image",
      planEstimateHint: "Estimates assume {imagesPerListing} images per listing (main + detail + lifestyle). Actual may vary.",
      bucketSubscription: "Subscription credits",
      bucketRecharge: "Top-up credits",
      bucketLegacy: "Legacy credits",
      creditsScopePrefix: "Credits used: ",
      creditsScopeSuffixThirdParty: " (deduct order: subscription → top-up → legacy).",
      creditsScopeSuffixOfficial: " (deduct order: top-up first; subscription/legacy depends on settings).",
      summaryPlanLine: "Mode: {plan} · {creditsPerImage} credits/image",
      listingsLabel: "Listings",
      listingsUnit: "listings",
      timeSaveHint: "Estimated time saved: ~{hoursMin}-{hoursMax} hours (for reference).",
      laborHint: "Manual listing creation typically takes 30–60 minutes; scaling often needs dedicated staff (≈4k–8k RMB/month).",
      planCompareHint:
        "Same credits: Value ≈ {thirdPartyImages} images / {thirdPartyListings} listings; Official ≈ {officialImages} images / {officialListings} listings (based on {imagesPerListing} images per listing).",
      planUpdateFailed: "Failed to update image mode.",
      stepPack: "Pick Pack",
      stepPay: "Choose Payment",
      stepConfirm: "Complete",
      creditsLabel: "Credits",
      imagesLabel: "Images",
      imagesUnit: "images",
      topUpLabel: "Top up",
      bestValue: "Best Value",
      saveLabel: "Save",
      summaryTitle: "Selected Pack",
      summaryPay: "Payment Method",
      bonusLabel: "Bonus",
      featureNeverExpire: "Top-up credits never expire",
      featureBatch: "Batch generation",
      featureNoBatch: "No batch generation",
      featurePriority: "Priority support",
      paymentTitle: "Payment Method",
      purchaseButton: "Complete Purchase",
      buttonProcessing: "Processing...",
      processing: "Processing payment...",
      redirecting: "Redirecting to payment...",
      success: "Purchase successful! Credits added.",
      paymentFailed: "Failed to start payment.",
      payHint: "Top-ups support WeChat Pay and Alipay. Credits arrive instantly after payment.",
      acceptedMethods: "Accepted Methods",
      visaLabel: "Visa ending in 4242",
      visaExpire: "Expires 12/26",
      wechat: "WeChat Pay",
      wechatDesc: "Fast and secure mobile payment",
      wechatPendingBadge: "Pending",
      wechatPendingTitle: "WeChat Pay pending approval",
      wechatPendingDesc: "WeChat Pay is still under review in Stripe Live mode and is temporarily unavailable. Please use Alipay for now.",
      wechatPendingToast: "WeChat Pay is temporarily unavailable. Please use Alipay.",
      alipay: "Alipay",
      alipayDesc: "Global payment solution",
      addNew: "Add New Payment Method",
      addNewDesc: "Credit Card, WeChat Pay, or Alipay",
      security: "Secured by Stripe SSL Encryption",
      total: "Total to Pay",
      payHintAlipayOnly: "Top-ups currently support Alipay. WeChat Pay is pending Stripe approval.",
      footer: "© {year} OneClickSell Financial Operations"
    },

    subscription: {
      title: "Manage Subscription Plan",
      subtitle: "Select the plan that matches your image generation volume.",
      currentPlanLabel: "Current Plan",
      currentPlanBadge: "Current Plan",
      activeTag: "Active",
      inactiveTag: "Inactive",
      noPlan: "No active plan",
      noPlanHint: "No active subscription",
      nextBilling: "Next billing date:",
      nextBillingDate: "Updating...",
      activeCycle: "Active Cycle",
      monthly: "Monthly",
      yearly: "Yearly",
      yearlySave: "2 months free",
      billedMonthly: "Billed monthly",
      billedYearly: "Billed yearly (10 months)",
      rolloverHint: "Subscription credits reset each billing cycle; top-up credits never expire.",
      creditsScopeHint: "Current image mode: {plan} · {creditsPerImage} credits/image. Credits used: {scope}.",
      officialTopUpOnly: "top-up credits only",
      perMonth: "/mo",
      perYear: "/yr",
      plansEmpty: "No subscription plans available.",
      planImagesPerMonth: "{count} images per month",
      planListingsPerMonth: "{count} listings per month",
      planCreditsPerMonth: "{count} credits per month",
      planUnlimited: "Unlimited",
      planStarterDesc: "Ideal for individuals getting started.",
      planProDesc: "Best for growing teams.",
      planBusinessDesc: "Designed for high-volume sellers.",
      featureStarter1: "Basic batch generation",
      featureStarter2: "HD image downloads",
      featureStarter3: "Standard support (48h)",
      featurePro1: "Advanced batch generation",
      featurePro2: "HD + 4K image downloads",
      featurePro3: "Priority support (12h)",
      featurePro4: "Dedicated growth manager",
      featureBusiness1: "High-volume batch generation",
      featureBusiness2: "Brand asset kit exports",
      featureBusiness3: "Priority support + success manager",
      featureBusiness4: "Workflow automation guidance",
      downgrade: "Downgrade",
      downgradeSuccess: "Plan downgraded.",
      upgrade: "Upgrade",
      manageInStripe: "Manage in Stripe",
      activePlan: "Active Plan",
      subscribe: "Subscribe",
      processing: "Redirecting to Stripe checkout...",
      paymentFailed: "Payment initialization failed.",
      loading: "Syncing subscription status...",
      buttonProcessing: "Processing...",
      cardHint: "Subscriptions are charged via credit/debit card (Stripe).",
      contactSales: "Contact Sales",
      customTitle: "Need a custom plan?",
      customDesc: "Our team can tailor a plan for your volume and workflow.",
      consultSales: "Consult with Sales",
      cancel: "Cancel Subscription",
      cancelConfirm: "Are you sure you want to cancel?",
      footer: "© {year} OneClickSell Financial Operations"
    },

    // Subscription Settings
    subscriptionSettings: {
      title: "Manage Subscription Plan",
      subtitle: "Select the best plan for your business needs.",
      currentPlanLabel: "Current Plan:",
      currentPlan: "Pro Plan",
      active: "Active",
      nextBillingLabel: "Next billing date:",
      nextBillingDate: "Updating...",
      activeCycleLabel: "Active Cycle:",
      monthly: "Monthly",
      yearly: "Yearly",
      save20: "Save 20%",
      billedAnnually: "Billed annually for maximum savings",
      billed: "billed {cycle}",
      perMonth: "/mo",
      plans: {
        starter: {
          title: "Starter",
          desc: "Perfect for individuals and small startups."
        },
        pro: {
          title: "Pro",
          desc: "Most popular for growing teams."
        },
      },
      features: {
        starterCredits: "500 Credits per month",
        starterAgents: "Basic AI Agents access",
        starterSupport: "Email support (48h)",
        customIntegrations: "Custom integrations",
        proCredits: "2,000 Credits per month",
        proAgents: "All AI Agents access",
        proSupport: "Priority support (12h)",
        proIntegrations: "Up to 5 integrations",
      },
      buttons: {
        downgrade: "Downgrade",
        activePlan: "Active Plan",
        contactSales: "Contact Sales",
        consultSales: "Consult with Sales",
        cancelSubscription: "Cancel Subscription"
      },
      currentBadge: "Current Plan",
      custom: {
        title: "Need a custom plan?",
        desc: "Our sales experts can help you design a tailored solution that fits your specific high-volume business requirements."
      },
      alerts: {
        downgrade: "Downgrading to Starter...",
        contactSales: "Contacting sales team...",
        openChat: "Opening sales chat...",
        cancelConfirm: "Are you sure?",
        cancelRequested: "Subscription cancellation requested."
      }
    },

    // Security Settings
    securitySettings: {
      title: "Security",
      subtitle: "Manage your account protection, authentication methods, and active sessions to keep your data secure.",
      twoFactor: {
        title: "Two-Factor Authentication (2FA)",
        desc: "Secure your account with an extra layer of protection. When enabled, you'll be asked for a verification code when signing in from a new device.",
        comingSoon: "Coming soon",
        available: "Available"
      },
      password: {
        title: "Password Management",
        updated: "Update your password regularly to keep your account secure.",
        reset: "Reset Password"
      },
      sessions: {
        title: "Active Sessions",
        subtitle: "These are the devices that have logged into your account. Revoke any sessions that you do not recognize.",
        currentSession: "Current Session",
        lastLogin: "Last login:",
        current: "Current",
        activeNow: "Active Now",
        signOut: "Sign Out",
        chromeDevice: "Chrome on macOS",
        chromeLocation: "San Francisco, US • 192.168.1.1",
        iphoneDevice: "iPhone 15 Pro",
        iphoneLocation: "London, UK • 2 days ago"
      },
      cancel: "Cancel",
      saving: "Saving...",
      saved: "Saved",
      save: "Save Changes",
      signOutConfirm: "Sign out this device?",
      signOutAlert: "Signing out device...",
      footer: "Security Infrastructure"
    },

    // Notifications Center
    notificationsCenter: {
      title: "Notifications",
      subtitle: "View important updates and task results here.",
      tabInbox: "Inbox",
      tabSettings: "Settings",
      unreadOnly: "Unread only",
      refresh: "Refresh",
      markAllRead: "Mark all read",
      loading: "Loading notifications...",
      loadingMore: "Loading...",
      loadMore: "Load more",
      loadFailed: "Failed to load notifications.",
      emptyTitle: "No notifications yet",
      emptyDesc: "When something important happens, it will appear here.",
      delete: "Delete",
      deleteConfirm: "Delete this notification?",
      deleteFailed: "Failed to delete notification.",
      deleteDone: "Deleted",
      markAllDone: "All marked as read",
      markAllFailed: "Failed to mark all as read."
    },
    feedbackCenter: {
      title: "My Feedback",
      subtitle: "Track AI processing and resolution updates for your submitted bugs/features.",
      loading: "Loading feedback...",
      refresh: "Refresh",
      empty: "No feedback submitted yet.",
      createdAt: "Created",
      updatedAt: "Updated"
    },

    // Notifications Settings
    notificationsSettings: {
      title: "Notifications",
      subtitle: "Manage how and where you receive system updates and alerts.",
      table: {
        type: "Notification Type",
        email: "Email",
        push: "Push"
      },
      loading: "Loading preferences...",
      loadFailed: "Failed to load notification preferences.",
      saveFailed: "Failed to save notification preferences.",
      pushComingSoon: "Push notifications are coming soon.",
      account: {
        title: "Account",
        desc: "Security alerts and account changes"
      },
      payment: {
        title: "Payments",
        desc: "Payment receipts and transaction updates"
      },
      subscription: {
        title: "Subscription",
        desc: "Plan renewals, invoices, and cancellations"
      },
      quota: {
        title: "Credits & Quota",
        desc: "Low balance alerts and usage notifications"
      },
      task: {
        title: "Tasks",
        desc: "Import, generation, and publish progress"
      },
      system: {
        title: "System",
        desc: "Announcements, maintenance, and security notices"
      },
      listing: {
        title: "Listing Success",
        desc: "Alert when products are published"
      },
      credit: {
        title: "Credit Warning",
        desc: "Alert when balance falls below 10%"
      },
      updates: {
        title: "Updates & Releases",
        desc: "New features and platform improvements"
      },
      mute: {
        title: "Mute all notifications",
        desc: "Temporarily silence all alerts and emails across the platform"
      },
      discard: "Discard Changes",
      saving: "Saving...",
      saved: "Preferences Saved",
      save: "Save Preferences"
    },

    // Integrations
    integrations: {
      title: "Integrations",
      subtitle: "Connect your favorite tools and marketplaces to streamline your workflow.",
      loadFailed: "Failed to load integrations.",
      connected: "Connected",
      connect: "Connect",
      shopify: "Shopify",
      shopifyDesc: "Sync products and inventory.",
      amazon: "Amazon Seller",
      amazonDesc: "Import listings and manage orders.",
      ozon: "Ozon",
      ozonDesc: "Russian marketplace integration.",
      slack: "Slack",
      slackDesc: "Receive notifications in your channels."
    },

    // Platform Preferences
    platformPreferences: {
      title: "Platform Preferences",
      subtitle: "Customize your default marketplace settings and AI agent behavior.",
      targetMarketplace: "Default Target Marketplace",
      advancedHint: "Need finer control? Configure advanced capabilities.",
      openAdvanced: "Advanced Settings",
      loading: "Loading preferences...",
      primaryPlatform: "Primary Platform",
      targetCountry: "Target Country",
      russia: "Russia",
      us: "United States",
      unitedStates: "United States",
      uk: "United Kingdom",
      unitedKingdom: "United Kingdom",
      germany: "Germany",
      france: "France",
      italy: "Italy",
      spain: "Spain",
      japan: "Japan",
      brazil: "Brazil",
      canada: "Canada",
      mexico: "Mexico",
      australia: "Australia",
      singapore: "Singapore",
      india: "India",
      turkey: "Turkey",
      uae: "United Arab Emirates",
      saudi: "Saudi Arabia",
      netherlands: "Netherlands",
      sweden: "Sweden",
      poland: "Poland",
      belgium: "Belgium",
      ireland: "Ireland",
      southAfrica: "South Africa",
      egypt: "Egypt",
      global: "Global",
      aiPersona: "AI Writing Persona",
      poweredBy: "Powered by LexiCopy",
      persuasive: "Persuasive",
      professional: "Professional",
      minimalist: "Minimalist",
      seoFocused: "SEO-Focused",
      personaHint: "This style will be applied by default to all new product descriptions and ad copies.",
      brandProtection: "Automatic Brand Protection",
      brandGuard: "Brand Guard",
      brandGuardDesc: "Reduce infringement risk by filtering brand terms during generation.",
      brandName: "Default Brand Name",
      brandPlaceholder: "Enter brand name for watermarking...",
      autoWatermark: "Auto-apply AI watermarking",
      autoWatermarkDesc: "Protect all new generations with your brand signature",
      imagePreferences: "Image Preferences",
      deepEnhancement: "Deep Image Enhancement",
      deepEnhancementDesc: "AI upscale and clarity boost",
      autoBgRemoval: "Auto-Background Removal",
      autoBgRemovalDesc: "Clean marketplace-ready assets",
      autoSaveHint: "Changes are saved automatically to your workspace profile.",
      saveHint: "Changes apply to new tasks. Save to sync to your workspace profile.",
      resetDefaults: "Reset to Defaults",
      saveChanges: "Save Changes",
      saving: "Saving...",
      savedChanges: "Saved Changes",
      saveFailed: "Failed to save preferences.",
      footer: "Global Workspace"
    },

    // Assets Management
    assets: {
      nav: "Assets",
      title: "Asset Operations Library",
      subtitle: "Operate completed listings and publish links. For task scheduling, go to Task Center.",
      loading: "Loading assets...",
      loadFailed: "Failed to load assets.",
      loginTitle: "Log in to view assets",
      loginDesc: "Sign in to manage your generated listings and upload records.",
      loginAction: "Log In",
      emptyTitle: "No assets yet",
      emptyHint: "Check Task Center for in-progress jobs, then return here to operate completed listings.",
      emptyAction: "Go to Task Center",
      newOptimization: "Import New Product",
      goTaskCenter: "Task Center",
      focusTitle: "Asset Library only shows operation-ready results",
      focusHint: "Use this page for published links and final assets. Running or failed jobs should be handled in Task Center.",
      platformAll: "Platform: All",
      statusPublished: "Status: Published",
      statusAll: "Status: All",
      progressLabel: "Progress",
      last30Days: "Last 30 Days",
      last7Days: "Last 7 Days",
      last90Days: "Last 90 Days",
      allPeriod: "All Time",
      clearFilters: "Clear Filters",
      score: "Score",
      published: "Published",
      draft: "Draft",
      failed: "Failed",
      retry: "Retry",
      edit: "Edit",
      view: "View",
      viewProgress: "View Progress",
      goToPublish: "Publish",
      fixError: "Fix",
      openListing: "Open Listing",
      noPublishUrl: "No publish link yet.",
      moreActionsComingSoon: "More actions are under development.",
      addNewListing: "Add New Listing",
      addNewDesc: "Upload raw photos for AI enhancement.",
      assetsSelected: "Assets Selected",
      batchDownload: "Batch Download",
      batchSync: "Batch Sync",
      delete: "Delete",
      actions: {
        copyPublishLink: "Copy publish link",
        openPublishLink: "Open publish link",
        deleteRecord: "Delete record",
        deleteTitle: "Delete record",
        deleteConfirmMessage: "Delete this record? This action cannot be undone.",
        deleted: "Deleted.",
        deleteFailed: "Failed to delete.",
        publishLinkCopied: "Publish link copied.",
        publishLinkCopyFailed: "Failed to copy publish link."
      },
      showingAssets: "Showing {from} to {to} of {total} assets",
      searchPlaceholder: "Search assets..."
    },

    // Import Preview
    importPreview: {
      nav: "Product Preview",
      breadcrumb: "Workspace",
      collectStatus: "Collection Status",
      collectQueued: "Queued",
      collecting: "Collecting",
      collectCompleted: "Completed",
      collectFailed: "Failed",
      collectingHint: "The extension is collecting data in the background.",
      collectProgressTitle: "Collection Progress",
      collectProgressHint: "We are syncing with the extension. You'll see live updates here.",
      progressLabel: "Progress",
      stepQueued: "Waiting for Extension",
      stepAssigned: "Extension Accepted",
      stepCollecting: "Collecting Data",
      stepUploading: "Uploading Data",
      stepCompleted: "Collection Complete",
      currentStage: "Current Stage",
      stage: {
        pending: "Waiting for extension",
        assigned: "Task assigned",
        preparing: "Preparing page",
        scrolling: "Scrolling page",
        collecting: "Collecting content",
        saving: "Saving data",
        uploading: "Uploading assets",
        cached: "Loading cached result",
        completed: "Completed"
      },
      authRequired: "Please log in to view collection progress.",
      pollFailed: "Failed to fetch collection progress.",
      previewFailed: "Failed to load collected data.",
      retryCollect: "Retry collection",
      attributeList: "Additional Attributes",
      detailHighlights: "Detail Highlights",
      noHighlights: "No highlight details detected yet.",
      skuStructure: "SKU Structure",
      skuStock: "Stock",
      skuVariant: "Variant",
      skuSyncing: "Syncing SKU selection...",
      skuAnchorSyncing: "Syncing SKU anchor image...",
      skuLockedByUser: "Locked by you",
      skuGuideTitle: "How to choose SKU",
      skuGuideStepSelect: "1. Select the target SKU variant first.",
      skuGuideStepAnchor: "2. Optionally lock one anchor image for better consistency.",
      skuGuideStepStart: "3. Start AI optimization after confirmation.",
      skuSelectionNudgeTitle: "Suggestion: lock SKU before generation",
      skuSelectionNudgeDesc: "For multi-SKU products, manual SKU lock helps keep reference images and final output consistent.",
      skuSelectionNudgeAction: "Go to SKU selection now",
      skuRecommendationTitle: "AI SKU suggestion",
      skuRecommendationHint: "Generated by model inference, review before applying.",
      skuRecommendationLoading: "AI is analyzing SKU suggestion...",
      skuRecommendationRefresh: "Refresh suggestion",
      skuRecommendationRefreshing: "Refreshing...",
      skuRecommendationFailed: "Failed to load SKU suggestion.",
      skuRecommendationEmpty: "No valid SKU suggestion returned.",
      skuRecommendationApply: "Apply suggestion",
      skuRecommendationApplied: "Applied",
      skuRecommendationPendingApply: "AI suggestion is ready. You can apply it with one click.",
      skuRecommendationConfidence: "Confidence {confidence}%",
      skuRecommendationSourceLabel: "Suggestion source: {source}",
      skuRecommendationSourceModel: "Model",
      skuRecommendationSourceCache: "Cache",
      skuRecommendationSourceFallback: "Fallback",
      skuRecommendationNoReason: "No model reason returned for this suggestion.",
      skuSelectionSyncFailed: "Failed to sync SKU selection. Please retry.",
      skuAnchorSyncFailed: "Failed to sync SKU anchor image. Please retry.",
      skuAnchorMissingHint: "No SKU anchor image locked yet. The system will fallback to text matching.",
      skuAnchorLocked: "SKU anchor image locked: #{index}",
      skuAnchorSelectHint: "Select one SKU anchor image (recommended):",
      skuAnchorOptionTitle: "Lock as SKU anchor image #{index}",
      skuAnchorSourceLabel: "Anchor source: {source}",
      skuAnchorExpand: "Edit anchor",
      skuAnchorCollapse: "Collapse",
      manualSkuReuseDisabledHint: "Manual SKU lock enabled. Historical reuse is disabled to keep reference images aligned.",
      noSkuData: "No SKU structure data detected yet.",
      productDetails: "Product Details",
      detailSummary: "Detail Summary",
      noDetailSummary: "No detail summary available yet.",
      noImages: "No images yet",
      previewImage: "Preview image",
      dataOverview: "Data Overview",
      overviewImages: "Images",
      overviewSkus: "SKUs",
      overviewAttributes: "Attributes",
      overviewSections: "Raw Sections",
      overviewRawText: "Raw Text Length",
      characters: "chars",
      noOverview: "No overview data yet.",
      flags: "Collection Flags",
      noFlags: "No flags detected yet.",
      flagEnabled: "Yes",
      flagDisabled: "No",
      dimensionsInfo: "Dimensions & Weight",
      noDimensions: "No dimension info available yet.",
      imageTags: "Image Tags",
      noImageTags: "No image tag data yet.",
      rawText: "Raw Text",
      noRawText: "No raw text available yet.",
      rawSections: "Raw Sections",
      validationWarning: "Data Validation Issues",
      issuesDetected: "issues detected that may affect AI optimization",
      manualReview: "Manual Review Required",
      allResolved: "All Issues Resolved",
      source: "Source",
      marketPricing: "Market Pricing Comparison",
      supplierPrice: "Supplier Price (1688)",
      priceRange: "Price Range",
      invalid: "Invalid",
      aiSuggest: "AI Suggest",
      marketAvg: "Market Avg. Price",
      suggestedAiPrice: "Suggested AI Price",
      profitMargin: "Profit Margin",
      marginDesc: "Estimated 72.4% net margin after typical shipping.",
      aiStrategy: "AI Optimization Strategy",
      seoEnhancements: "SEO Enhancements",
      seoDesc: "Will add \"orthopedic support\" and \"breathable mesh\" keywords to boost search visibility by 35%.",
      imageRefinement: "Image Refinement",
      imageDesc: "AI will remove 1688 watermarks and generate lifestyle backgrounds for main photos.",
      marketPositioning: "Market Positioning",
      positionDesc: "Targeting \"Affordable Premium\" segment with focus on urban durability.",
      skuAttributes: "SKU Attributes",
      colorVariants: "Color Variants",
      sizeAvailability: "Size Availability",
      currentStock: "Current Stock",
      shippingEst: "Shipping Est.",
      shippingLocal: "3-5 Days (Local)",
      weight: "Weight",
      dimensions: "Dimensions",
      missing: "MISSING",
      aiAutofill: "AI Autofill Missing Data",
      dataFilled: "Data Filled by AI",
      viewMetadata: "View All Raw Metadata",
      lowRes: "Low Res",
      dataRequired: "DATA VALIDATION REQUIRED",
      readyOptimize: "READY FOR OPTIMIZATION",
      agentsPaused: "Optimization agents paused",
      aiFillAll: "AI Fill All Missing Data",
      saveDraft: "Save Draft",
      saved: "Saved",
      startOptimization: "Start AI Optimization",
      optimized: "Optimized",
      fixIssues: "Fix issues to improve AI results",
      exportToStore: "Export to Store",
      exporting: "Exporting...",
      exported: "Exported!",
      exportSuccess: "Product successfully exported to store!"
    },

    // Review (Updated for AI Agent Canvas)
    review: {
      nav: "AI Data Review",
      validating: "AI is suggesting values based on product category.",
      validated: "AI Optimization Successful. All fields validated.",
      pending: "Manual Review Required",
      completed: "Review Completed",
      marketPricing: "Market Pricing Comparison",
      supplierPrice: "Supplier Price",
      invalid: "Invalid",
      marketAvg: "Market Avg. Price",
      suggestedPrice: "Suggested AI Price",
      aiGenerated: "AI Generated",
      marginAnalysis: "Profit Margin Analysis",
      marginDesc: "Estimated 72.4% net margin after localized shipping.",
      strategy: "AI Optimization Strategy",
      seoEnhance: "SEO Enhancements",
      seoDesc: "Will add optimized keywords to boost visibility.",
      imgRefine: "Image Refinement",
      imgDesc: "AI will remove watermarks and generate lifestyle backgrounds.",
      marketPos: "Market Positioning",
      marketPosDesc: "Targeting 'Affordable Premium' segment for durability.",
      skuAttrs: "SKU Attributes",
      originalData: "Original Data",
      colorVariants: "Color Variants",
      sizeAvail: "Size Availability",
      stock: "Current Stock",
      shipping: "Shipping Est.",
      weight: "Weight",
      dimensions: "Dimensions",
      missing: "Missing",
      confirmBtn: "Confirmed All AI Suggestions",
      autofillBtn: "AI Autofill Missing Data",
      saveDraft: "Save Draft",
      applyAi: "Apply AI Changes",
      startAi: "Start AI Optimization",
      exportBtn: "Export to Store"
    }
  },
  zh: {
    ".": ".",
    appName: "一键大卖",
    trustAppName: "一键大卖 Trust",

    // 导航
    nav: {
      features: "功能",
      pricing: "定价",
      faq: "常见问题",
      login: "登录",
      startFree: "免费开始"
    },
    common: {
      ok: "知道了",
      cancel: "取消",
      confirm: "确认",
      notice: "提示",
      backToWorkspace: "返回工作区",
      backHome: "返回首页"
    },
    appError: {
      title: "页面出错了",
      subtitle: "你可以刷新页面，或返回任务中心继续操作。",
      backTaskCenter: "返回任务中心",
      refresh: "刷新页面",
      copy: "复制错误信息"
    },

    theme: {
      light: "浅色",
      dark: "深色",
      switchToLight: "切换到浅色模式",
      switchToDark: "切换到深色模式"
    },

    appNav: {
      more: "更多",
      loginHint: "登录后可使用该功能。",
      unlockHint: "完成导入后解锁。",
      comingSoonBadge: "开发中",
      extensionConnected: "插件已连接",
      extensionMissing: "未检测到插件",
      extensionOffline: "插件离线",
      extensionUpgradeRequired: "插件版本过低",
      extensionUpgradeDetail: "当前 {current}，要求 {required}",
      extensionVersionUnknown: "未知版本",
      extensionTakeoverStatusLabel: "自动接管状态",
      extensionAwaitingSync: "等待站点自动接管",
      extensionAwaitingSyncDetail: "插件已安装成功，保持当前页面打开，系统会自动同步登录态。",
      extensionStatusChecking: "正在确认插件状态",
      extensionStatusCheckingDetail: "插件已安装，正在确认站点接管与后台就绪状态。",
      extensionBackendUnreachable: "后端不可达",
      extensionBackendUnreachableDetail: "插件已安装，但当前后端地址不可访问，请检查本地或线上服务状态。",
      extensionReadyPolling: "自动轮询已就绪",
      extensionReadyPollingDetail: "站点接管已完成，插件正在等待后端任务并自动采集。",
      extensionCollecting: "自动采集中",
      extensionCollectingDetail: "插件正在后台自动采集商品数据。",
      extensionConnecting: "插件连接中",
      extensionConnectingDetail: "站点接管已完成，插件正在建立后台连接。",
      extensionDownload: "下载插件",
      extensionUpdate: "立即更新",
      extensionDownloadGuide: "正在打开插件下载页...",
      assetsComingSoon: "资产库正在开发中：用于统一管理生成/上传的图片、文案、上架记录等素材，支持检索、复用与跨平台同步。",
      copyrightComingSoon: "版权资产正在开发中：用于对 PSD/图片进行水印与指纹保护、生成侵权证据包，并提供申诉/维权模板，降低侵权风险。"
    },
    extensionGuide: {
      badge: "新手友好安装向导",
      title: "我们一步一步安装 Chrome 插件",
      subtitle: "不用技术背景，按步骤点击即可完成，最后会自动检测是否安装成功。",
      resumeHint: "已恢复你上次的进度，可以从当前步骤继续。",
      progress: "第 {current} / {total} 步",
      sources: {
        header: "来自顶部导航入口",
        workspace: "来自工作区入口",
        import: "来自导入页入口"
      },
      steps: {
        intro: {
          title: "欢迎开始，预计 2 分钟完成",
          desc: "这个插件用于安全采集商品信息。只需要安装一次，后续可长期使用。",
          tip: "建议：保持本引导页面打开，按按钮一步一步操作。"
        },
        download: {
          title: "第 1 步：下载插件 ZIP 压缩包",
          desc: "点击下方按钮下载。下载后请把文件放在你容易找到的位置（如桌面）。",
          tip: "如果浏览器让你选择保存位置，请选一个你能快速找到的文件夹。"
        },
        unzip: {
          title: "第 2 步：解压下载好的文件",
          desc: "找到 ZIP 文件后右键解压。解压完成后你会得到一个普通文件夹。",
          tip: "后面加载插件时要选“解压后的文件夹”，不要选 ZIP 压缩包。"
        },
        openExtensions: {
          title: "第 3 步：打开 Chrome 扩展管理页",
          desc: "点击下方按钮打开 chrome://extensions/。建议保留当前引导页不要关闭。",
          tip: "如果浏览器未自动打开，请手动在地址栏输入 chrome://extensions/。"
        },
        loadUnpacked: {
          title: "第 4 步：开启开发者模式并加载插件",
          desc: "在扩展管理页打开“开发者模式”，点击“加载已解压的扩展程序”，选择第 2 步解压得到的文件夹。",
          tip: "加载成功后，你会在扩展列表中看到本插件卡片。"
        },
        verify: {
          title: "第 5 步：自动检测安装状态",
          desc: "请留在当前页，我们会自动检测插件是否已连通。若失败，可一键重试。",
          tip: "如果检测超时，请返回上一步确认是否选择了正确的解压目录。"
        },
        done: {
          title: "安装完成，插件已可用",
          desc: "恭喜你，插件已成功安装并连接。",
          tip: "现在可以继续导入商品开始流程。"
        }
      },
      help: {
        title: "我卡住了，怎么办？",
        show: "展开帮助",
        hide: "收起帮助",
        intro1: "第一次安装很正常会陌生，按当前步骤逐个点就行。",
        intro2: "如果误关了引导，再点击下载/更新按钮即可重新打开。",
        download1: "如果没有弹出下载，请检查浏览器是否拦截了弹窗。",
        download2: "如果你已经下载完成，可直接点“我已下载完成”。",
        unzip1: "macOS 通常双击 ZIP 文件即可解压。",
        unzip2: "Windows 可右键 ZIP 文件，选择“全部解压缩”。",
        openExtensions1: "地址需完整输入为 chrome://extensions/。",
        openExtensions2: "建议一个标签页放引导，另一个标签页操作扩展页面。",
        loadUnpacked1: "“开发者模式”开关一般在扩展页右上角。",
        loadUnpacked2: "如果提示失败，请重新选择“解压后的插件目录”。",
        verify1: "系统会每 1.5 秒自动检测一次，无需手动刷新页面。",
        verify2: "若版本过低，点击“下载最新安装包”后按步骤重新安装。",
        done1: "后续如果状态异常，可再次点击顶部插件状态按钮重新检测。",
        done2: "你可以随时从工作区或导入页重新打开这个引导。"
      },
      actions: {
        close: "关闭",
        prev: "上一步",
        start: "开始引导",
        next: "下一步",
        downloadZip: "立即下载 ZIP 安装包",
        downloadDone: "我已下载完成",
        openChromeExtensions: "打开 chrome://extensions/",
        backToExtensionsPage: "返回扩展管理页",
        retryDetect: "重新检测",
        downloadUpdate: "下载最新安装包"
      },
      status: {
        ready: "插件已连接成功",
        needUpgrade: "当前版本 {current}，请升级到 {required} 或更高版本。",
        unknownVersion: "未知版本",
        authRequired: "插件已安装，等待站点自动接管。",
        backendUnreachable: "插件已安装，但后端不可达。",
        readyPolling: "插件已就绪，正在等待后端任务。",
        collecting: "插件正在自动采集中。",
        connecting: "插件正在连接后端...",
        connectedWaiting: "已检测到插件，正在完成最终校验...",
        offline: "插件可能处于离线状态，请重启 Chrome 后重试。",
        verifyTimeout: "检测超时，请检查第 4 步后再重试。",
        detecting: "正在检测插件状态..."
      },
      verify: {
        successHint: "状态正常，正在进入完成页。",
        upgradeHint: "插件已连接，但当前版本偏低，需要先升级。",
        authRequiredHint: "插件已经安装完成，当前站点会自动把登录态同步给插件，完成后即可自动轮询任务。",
        backendUnreachableHint: "请确认当前后端服务可访问后再重新检测。",
        readyPollingHint: "站点自动接管已完成，插件正在等待后端任务。",
        collectingHint: "很好，插件已经在后台自动执行采集任务。",
        connectingHint: "插件已拿到站点凭证，正在建立后台连接。",
        timeoutHint: "别担心，点“重新检测”即可；也可返回上一步重新加载插件目录。",
        detectingHint: "自动检测中（第 {attempts} 次）..."
      },
      doneDesc: "当前插件版本：{version} · 最低要求版本：{required}。现在可以继续操作了。",
      doneActions: {
        workspace: "去导入商品",
        import: "立即开始导入",
        header: "继续当前操作"
      }
    },

    admin: {
      title: "管理后台",
      subtitle: "运营与配置中心",
      backToWorkspace: "返回工作台",
      login: {
        title: "管理员登录",
        subtitle: "请输入管理员账号密码",
        checking: "正在验证登录状态...",
        username: "管理员账号",
        usernamePlaceholder: "请输入管理员账号",
        password: "管理员密码",
        passwordPlaceholder: "请输入管理员密码",
        action: "登录后台",
        loggingIn: "登录中...",
        required: "请输入账号和密码",
        failed: "登录失败",
        denied: "无管理员权限",
        logout: "退出登录"
      },
      nav: {
        overview: "概览",
        aiRuntime: "AI 运行时",
        aiChannels: "AI 渠道",
        aiMonitor: "渠道监控",
        aiUsage: "AI 用量统计",
        marketing: "营销分析",
        billing: "计费与积分",
        consumption: "积分流水",
        users: "用户管理",
        logs: "操作日志",
        settings: "系统设置"
      },
      marketing: {
        title: "营销埋点分析",
        desc: "查看 PV、停留时长、CTA 漏斗与 UTM 归因效果。",
        loading: "正在加载营销分析数据...",
        error: "营销分析数据加载失败。",
        empty: "当前筛选下暂无营销埋点数据。",
        apply: "应用筛选",
        reset: "重置筛选",
        updatedAt: "更新时间：{time}",
        cards: {
          pv: "总 PV",
          visitors: "独立访客",
          engagement: "平均停留",
          ctaClicks: "CTA 点击",
          conversions: "转化次数",
          conversionRate: "转化率"
        },
        filters: {
          days: "时间范围",
          utmSource: "UTM 来源",
          utmMedium: "UTM 媒介",
          utmCampaign: "UTM 活动",
          experimentId: "实验 ID",
          experimentVariant: "实验分组",
          pagePath: "页面路径"
        },
        business: {
          cards: {
            paidOrders: "支付订单数",
            revenue: "归因收入",
            adCost: "广告成本",
            grossProfit: "毛利润",
            roas: "ROAS",
            roi: "ROI",
            aov: "客单价",
            cac: "单支付获客成本",
            cpa: "单转化成本"
          }
        },
        trend: {
          title: "按天 PV/UV/转化趋势",
          engagementTitle: "按天平均停留时长（秒）",
          series: {
            pv: "PV",
            uv: "UV",
            conversions: "转化",
            engagement: "平均停留（秒）"
          }
        },
        funnel: {
          title: "CTA 转化漏斗",
          view: "浏览",
          ctaClick: "CTA 点击",
          conversion: "转化"
        },
        utm: {
          title: "UTM 归因"
        },
        experiment: {
          title: "实验归因",
          empty: "当前筛选下暂无实验归因数据。",
          columns: {
            id: "实验",
            variant: "分组",
            pv: "PV",
            uv: "UV",
            conversions: "转化",
            paidOrders: "支付订单",
            revenue: "收入",
            adCost: "成本",
            roas: "ROAS",
            roi: "ROI",
            cvr: "CVR"
          }
        },
        pages: {
          title: "页面维度明细",
          empty: "当前筛选下暂无页面明细数据。",
          summary: {
            highPriorityPages: "高优先级页面",
            mediumPriorityPages: "中优先级页面",
            lowPriorityPages: "低优先级页面",
            avgScore: "平均优化评分"
          },
          priority: {
            high: "高优先级",
            medium: "中优先级",
            low: "低优先级"
          },
          signals: {
            HIGH_BOUNCE_RATE: "跳出率偏高",
            LOW_CTA_RATE: "CTA 点击率偏低",
            LOW_CTA_TO_CONVERSION: "CTA 后转化率偏低",
            HIGH_ENGAGEMENT_LOW_CONVERSION: "停留较高但转化偏低",
            NO_ENGAGEMENT_EVENTS: "缺少停留事件数据"
          },
          actions: {
            OPTIMIZE_ABOVE_THE_FOLD: "优先优化首屏价值表达与首屏信息结构",
            IMPROVE_CTA_VISIBILITY: "提高 CTA 位置可见性并强化按钮文案",
            REDUCE_FORM_FRICTION: "减少表单与下单流程阻塞，缩短转化路径",
            ALIGN_CONTENT_AND_OFFER: "让页面内容与活动权益、用户意图更一致",
            VERIFY_ENGAGEMENT_TRACKING: "补齐停留类埋点，避免分析盲区",
            ENRICH_TRAFFIC_QUALITY: "优化流量来源与投放定向质量",
            SCALE_WINNING_PAGE: "可加大该页面流量投放并扩量验证"
          },
          columns: {
            rank: "优先级排名",
            priority: "优先级",
            score: "优化评分",
            page: "页面",
            events: "事件数",
            pv: "PV",
            uv: "UV",
            uvAll: "总 UV",
            uvPageView: "PV UV",
            entry: "入口",
            exit: "出口",
            bounce: "跳出",
            engagement: "平均停留",
            engagementEvents: "停留事件",
            ctaClicks: "CTA 点击",
            ctaRate: "CTA 率",
            conversions: "转化",
            cvr: "CVR",
            cvrPv: "PV 转化率",
            cvrFromCta: "CTA 转化率",
            paidOrders: "支付订单",
            revenue: "收入",
            adCost: "成本",
            roas: "ROAS",
            roi: "ROI",
            aov: "客单价",
            cac: "单支付成本",
            cpa: "单转化成本",
            signals: "问题信号",
            actions: "优化动作",
            topEvents: "事件构成"
          }
        },
        table: {
          source: "来源",
          medium: "媒介",
          campaign: "活动",
          pv: "PV",
          uv: "UV",
          conversions: "转化",
          paidOrders: "支付订单",
          revenue: "收入",
          adCost: "成本",
          roas: "ROAS",
          cvr: "CVR"
        }
      },
      aiUsage: {
        title: "AI 用量统计",
        desc: "按供应商、调用类型、模型维度分析 Token 消耗",
        loading: "正在加载 AI 用量数据...",
        error: "AI 用量数据加载失败。",
        empty: "当前筛选下暂无用量记录。",
        apply: "应用",
        reset: "重置",
        summary: "时间范围：{start} ~ {end}",
        cards: {
          totalCalls: "总调用次数",
          totalTokens: "总 Token",
          totalInputTokens: "输入 Token",
          totalOutputTokens: "输出 Token",
          imageCalls: "图片调用",
          nonImageCalls: "非图片调用",
          avgTokens: "单次均值",
          quantity: "数量"
        },
        distribution: {
          provider: "供应商分布",
          callType: "调用类型分布",
          model: "模型分布"
        },
        trend: {
          title: "趋势图"
        },
        cost: {
          title: "成本估算",
          description: "可按当前渠道定价口径手工调整，便于估算真实支出。",
          reset: "恢复默认值",
          inputPrice: "输入 Token 单价（USD / 100万）",
          outputPrice: "输出 Token 单价（USD / 100万）",
          imageCost: "图片成本（RMB/张）",
          exchangeRate: "美元兑人民币",
          totalTokenCost: "Token 成本",
          imageCostTitle: "图片成本",
          totalCost: "总计成本",
          avgPerTask: "单次均摊成本",
          tokenPerNonImageCall: "非图片单次均摊"
        },
        filters: {
          periodStart: "开始时间",
          periodEnd: "结束时间",
          interval: "趋势粒度",
          all: "全部",
          image: "仅图片调用",
          yes: "是",
          no: "否",
          userId: "用户ID",
          provider: "供应商",
          model: "模型",
          callType: "调用类型",
          action: "Action",
          pipelineStage: "Pipeline Stage",
          stage: "Stage",
          runId: "Run ID",
          callId: "Call ID"
        },
        intervals: {
          hour: "小时",
          day: "天",
          week: "周",
          month: "月"
        },
        table: {
          time: "时间",
          user: "用户",
          runId: "Run ID",
          callId: "Call ID",
          action: "动作",
          provider: "供应商",
          model: "模型",
          callType: "调用类型",
          tokens: "Token",
          estimatedCost: "预估成本",
          image: "是否图片",
          metadata: "元数据"
        },
        records: "明细记录",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页"
      },
      navGroups: {
        overview: "概览",
        ai: "AI 控制台",
        operations: "运营管理",
        system: "系统管理"
      },
      access: {
        checking: "正在验证管理员权限...",
        loginTitle: "请先登录",
        loginDesc: "管理后台需要登录后访问。",
        loginAction: "立即登录",
        deniedTitle: "无权限访问",
        deniedDesc: "当前账号不是管理员。"
      },
      listStates: {
        loading: {
          title: "正在同步列表数据",
          description: "请稍候，系统正在拉取最新记录。"
        },
        error: {
          title: "列表加载失败",
          description: "请重试以获取最新数据。",
          detailPrefix: "错误详情："
        },
        empty: {
          title: "暂无可展示数据",
          description: "可尝试调整筛选条件或刷新后重试。"
        },
        actions: {
          retry: "重试",
          retrying: "重试中...",
          refresh: "刷新数据",
          backToOverview: "返回概览"
        }
      },
      overview: {
        title: "数据概览",
        desc: "核心指标与最近趋势",
        loading: "正在加载概览数据...",
        error: "加载概览数据失败。",
        empty: "暂无数据。",
        userTrend: "最近 7 天新增用户",
        revenueTrend: "最近 7 天收入",
        cards: {
          totalUsers: "累计用户",
          activeToday: "今日活跃",
          newToday: "今日新增",
          totalRevenue: "累计收入",
          revenueMonth: "本月收入",
          totalConsumption: "累计消费"
        },
        units: {
          users: "用户",
          usd: "USD",
          credits: "积分"
        }
      },
      aiChannels: {
        title: "AI 渠道管理",
        desc: "管理平台 AI 渠道与路由策略",
        error: "加载渠道失败。",
        testResult: {
          success: "测试通过",
          failed: "测试失败",
          successMessage: "渠道测试成功。",
          failureUnknown: "测试失败，请查看原因：",
          failureReason: "失败原因",
          checkedAt: "检测时间：{time}"
        },
        validationName: "请输入渠道名称。",
        validationKey: "请输入 API Key。",
        validationBaseUrl: "非官方渠道必须填写 Base URL。",
        validationExpiresAtRequired: "请设置有效期时间，或勾选永久有效。",
        validationExpiresAtInvalid: "有效期时间格式不正确。",
        validationVertexProjectLocation: "Vertex AI 渠道必须填写 GCP Project 和 Location。",
        validationVertexProjectLocationPair: "GCP Project 和 Location 需同时填写或同时留空。",
        enableConfirm: "确认启用渠道 {name}？",
        disableConfirm: "确认停用渠道 {name}？",
        testConfirm: "确认测试渠道 {name}？",
        updated: "渠道已更新。",
        created: "渠道已创建。",
        deleted: "渠道已删除。",
        tested: "测试已完成。",
        deleteConfirm: "确认删除渠道 {name}？",
        officialBaseUrl: "官方 Base URL：{url}",
        vertexBaseUrlHint: "Vertex AI 渠道无需配置 Base URL。",
        vertexAuthHint: "Vertex AI 可直接使用 API Key；Project/Location 为可选项，填写时需成对提供。",
        hints: {
          neverExpires: "当前渠道为永久有效。"
        },
        refresh: "刷新",
        updatedAt: "更新时间 {time}",
        edit: "编辑渠道",
        create: "新增渠道",
        newChannel: "新建渠道",
        cancel: "取消编辑",
        formHint: "编辑时留空 API Key 可保持原值。",
        searchPlaceholder: "按名称 / Base URL 搜索",
        filters: {
          all: "全部渠道",
          enabled: "仅启用",
          disabled: "仅停用",
          official: "仅官方",
          custom: "仅自定义",
          degraded: "仅异常"
        },
        summary: {
          total: "渠道总数",
          enabled: "启用渠道",
          degraded: "异常渠道",
          requests: "24小时请求",
          failureRate: "24小时失败率"
        },
        providers: {
          developerAPI: "Gemini Developer API",
          vertexAI: "Vertex AI",
          googleOfficial: "Google 官方",
          customProxy: "自定义代理"
        },
        fields: {
          name: "渠道名称",
          apiKey: "API Key",  // pragma: allowlist secret
          provider: "Provider",
          baseUrl: "Base URL",
          gcpProject: "GCP Project",
          gcpLocation: "GCP Location",
          tier: "等级",
          priority: "优先级",
          creditsPerImage: "图片单价(积分/张)",
          expiresAt: "有效期截止时间",
          maxFail: "最大失败次数",
          recovery: "恢复时间（秒）",
          description: "描述",
          enabled: "启用",
          isOfficial: "官方渠道",
          neverExpires: "永久有效"
        },
        placeholders: {
          name: "例如：Gemini-Primary",
          apiKey: "粘贴 API Key",  // pragma: allowlist secret
          apiKeyEdit: "留空则保持不变",
          baseUrl: "https://api.example.com",
          gcpProject: "例如：my-gcp-project",
          gcpLocation: "例如：us-central1",
          creditsPerImage: "留空则使用系统定价",
          description: "用途说明"
        },
        save: "保存",
        saving: "保存中...",
        listTitle: "渠道列表",
        loading: "正在加载渠道...",
        empty: "暂无渠道配置。",
        statusUnknown: "未知",
        table: {
          name: "渠道",
          status: "状态",
          priority: "优先级",
          creditsPerImage: "积分/张",
          baseUrl: "Base URL",
          requests: "24小时请求",
          failRate: "24小时失败率",
          updatedAt: "更新时间",
          avgLatency: "平均耗时",
          validUntil: "有效期",
          permanent: "永久有效",
          expired: "已过期",
          actions: "操作",
          scrollHint: "可横向滑动查看完整列。"
        },
        actions: {
          edit: "编辑",
          disable: "停用",
          enable: "启用",
          enabling: "启用中...",
          disabling: "停用中...",
          test: "测试",
          testing: "测试中...",
          delete: "删除",
          deleting: "删除中..."
        }
      },
      aiRuntime: {
        title: "AI 运行时配置",
        desc: "按模块配置模型、思考强度和渠道策略",
        quickPolicyTitle: "快捷路由策略",
        quickPolicyDesc: "一键强制所有分析模块走官方渠道；生图仍由用户模式决定。",
        manageChannels: "管理渠道",
        analysisOfficialOnlyLabel: "分析模块强制官方渠道",
        analysisOfficialOnlyHintOn: "已开启：所有分析模块固定走官方渠道，仅图片生成可使用超值模式。",
        analysisOfficialOnlyHintOff: "已关闭：分析模块将按下方全局/模块渠道策略执行。",
        enabledOfficialChannels: "已启用官方渠道：{count}",
        enabledThirdPartyChannels: "已启用第三方渠道：{count}",
        loading: "正在加载运行时配置...",
        error: "加载运行时配置失败。",
        saved: "运行时配置已更新。",
        save: "保存配置",
        saving: "保存中...",
        saveConfirm: "即将保存运行时配置并立即生效，确认继续？",
        reset: "重置未保存改动",
        summaryTitle: "运行态总览",
        statusDirty: "有未保存改动",
        statusSaved: "已保存",
        global: "全局默认",
        globalHint: "全局配置会被所有模块继承，可在模块中单独覆盖。",
        modules: "模块覆盖",
        channelsHint: "当选择指定渠道时，故障切换仅在所选渠道中进行。",
        emptyChannels: "暂无渠道，请先配置 AI 渠道。",
        effectiveConfig: "最终生效配置",
        copyGlobal: "复制全局配置",
        searchResultCount: "当前显示 {count} 个模块",
        groups: {
          all: "全部",
          extract: "提取",
          content: "内容",
          image: "图片"
        },
        summary: {
          total: "模块总数",
          enabled: "启用中",
          override: "有覆盖配置",
          specific: "指定渠道"
        },
        fields: {
          model: "模型",
          thinking: "思考强度",
          strategy: "渠道策略",
          channels: "渠道",
          enabled: "启用"
        },
        hints: {
          globalModel: "模块未设置模型时，继承该模型。",
          globalThinking: "模块未设置思考强度时，使用该默认值。",
          globalStrategy: "模块未设置策略时，继承该策略。",
          globalChannels: "仅当渠道策略为指定渠道时生效。",
          moduleModel: "覆盖模块模型；留空则继承全局默认。",
          moduleThinking: "覆盖模块思考强度；留空则继承全局默认。",
          moduleStrategy: "覆盖模块路由策略。",
          moduleChannels: "选择指定渠道后，仅在勾选渠道中路由请求。",
          moduleEnabled: "控制该模块是否参与运行。"
        },
        strategy: {
          auto: "自动",
          official_only: "仅官方渠道",
          specific_channels: "指定渠道"
        },
        inherit: "继承全局",
        moduleSearch: "搜索模块",
        moduleCount: "{count} 个模块",
        selectedModule: "当前模块"
      },
      aiMonitor: {
        title: "渠道运行监控",
        desc: "查看渠道实时状态与健康度",
        loading: "正在加载运行状态...",
        error: "加载渠道状态失败。",
        refreshing: "刷新中...",
        refresh: "刷新",
        autoRefreshOn: "自动刷新：开启",
        autoRefreshOff: "自动刷新：关闭",
        autoRefresh: "自动刷新",
        updatedAt: "更新时间 {time}",
        current: "当前渠道",
        total: "渠道数量",
        healthy: "健康渠道",
        requests: "24小时请求",
        failureRate: "失败率",
        avgLatency: "平均耗时",
        requestUnit: "次",
        percentUnit: "%",
        msUnit: "ms",
        trend: "调用趋势",
        last24h: "最近 24 小时",
        noTrend: "暂无趋势数据",
        errors: "近期错误",
        errorsDesc: "最近 24 小时失败记录",
        noErrors: "暂无错误",
        noErrorMessage: "暂无信息",
        expand: "展开",
        collapse: "收起",
        none: "暂无",
        empty: "暂无运行数据。",
        statusUnknown: "未知",
        yes: "是",
        no: "否",
        table: {
          name: "渠道",
          status: "状态",
          priority: "优先级",
          enabled: "启用",
          available: "可用",
          successRate: "成功率",
          failCount: "失败次数",
          avgLatency: "平均耗时"
        }
      },
      billing: {
        title: "积分与计费",
        desc: "调整用户积分与手动充值",
        error: "加载积分用户失败。",
        creditsBreakdown: "订阅 {subscription} / 充值 {recharge} / 旧 {legacy}",
        creditUnit: "积分",
        actionTitle: "积分操作",
        validationUser: "请填写用户 ID。",
        defaultReason: "管理员调整",
        rechargeReason: "管理员充值",
        actionSuccess: "操作完成。",
        submitting: "提交中...",
        submit: "提交",
        listTitle: "用户积分列表",
        search: "查询",
        loading: "正在加载用户...",
        empty: "暂无用户。",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        fields: {
          userId: "用户 ID",
          actionType: "操作类型",
          reason: "原因",
          credits: "积分",
          amount: "金额",
          bonus: "赠送"
        },
        placeholders: {
          userId: "从列表选择或粘贴 ID",
          reason: "可选原因",
          search: "搜索用户"
        },
        actions: {
          adjust: "调整积分",
          recharge: "充值",
          select: "选择"
        },
        chart: {
          title: "计费订单趋势",
          desc: "按天对比已支付/待支付/异常订单",
          loading: "正在加载计费趋势...",
          error: "加载计费趋势失败。",
          empty: "暂无计费趋势数据。",
          orderUnit: "单",
          series: {
            paid: "已支付",
            pending: "待支付",
            issue: "异常"
          }
        },
        table: {
          user: "用户",
          plan: "套餐",
          credits: "积分",
          trial: "试用",
          actions: "操作"
        }
      },
      consumption: {
        title: "积分流水",
        desc: "审计充值、消费、赠送、退款与管理员调整",
        loading: "正在加载积分流水...",
        error: "加载积分流水失败。",
        empty: "暂无记录。",
        creditUnit: "积分",
        search: "查询",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholders: {
          userId: "按用户 ID 筛选"
        },
        chart: {
          title: "积分消耗趋势",
          desc: "按天对比积分消耗与补充",
          loading: "正在加载消耗趋势...",
          error: "加载消耗趋势失败。",
          empty: "暂无消耗趋势数据。",
          series: {
            consumed: "消耗积分",
            recharged: "补充积分"
          }
        },
        table: {
          user: "用户",
          createdAt: "时间"
        }
      },
      ledger: {
        placeholders: {
          search: "按手机号/邮箱/用户名/描述搜索"
        },
        filters: {
          allTypes: "全部类型",
          allBuckets: "全部来源"
        },
        types: {
          recharge: "充值",
          consumption: "消费",
          subscription: "订阅赠送",
          adjustment: "管理员调整",
          promo: "活动赠送",
          refund: "退款返还",
          trialGrant: "试用赠送"
        },
        buckets: {
          subscription: "订阅积分",
          recharge: "充值积分",
          legacy: "旧主账户"
        },
        table: {
          type: "类型/说明",
          bucket: "积分池",
          change: "变动",
          balance: "余额",
          reference: "关联",
          money: "金额/成本"
        }
      },
      users: {
        title: "用户管理",
        desc: "管理用户状态、套餐与管理员权限",
        error: "加载用户失败。",
        filters: "筛选条件",
        search: "搜索",
        filtersAllStatus: "全部状态",
        filtersAllAdmin: "全部用户",
        filtersAdmin: "仅管理员",
        filtersNonAdmin: "非管理员",
        editorTitle: "编辑用户",
        detailTitle: "用户详情",
        closeEditor: "关闭",
        adminToggle: "管理员权限",
        saved: "已保存",
        saving: "保存中...",
        save: "保存修改",
        discardChanges: "放弃修改",
        discardSuccess: "已放弃未保存的修改。",
        listTitle: "用户列表",
        loading: "正在加载用户...",
        empty: "暂无用户。",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholderEmpty: "-",
        compareTitle: "待保存变更",
        noChanges: "暂无待保存变更。",
        riskConfirmTitle: "检测到高风险变更",
        riskConfirm: "以下高风险变更需要确认：",
        saveError: "保存失败：{error}",
        unknownError: "未知错误",
        adminStateEnabled: "已开启",
        adminStateDisabled: "未开启",
        fields: {
          status: "状态",
          plan: "套餐",
          admin: "管理员",
          notes: "备注"
        },
        detailLabels: {
          userId: "用户 ID",
          phone: "手机号",
          email: "邮箱",
          plan: "套餐",
          status: "状态",
          credits: "积分",
          trial: "试用",
          createdAt: "创建时间",
          lastLogin: "最近登录"
        },
        placeholders: {
          search: "按用户名/手机号/邮箱搜索",
          notes: "管理员备注"
        },
        status: {
          active: "正常",
          inactive: "未激活",
          banned: "封禁"
        },
        plan: {
          free: "免费版",
          earlyBird: "早鸟版",
          standard: "标准版",
          enterprise: "企业版"
        },
        table: {
          user: "用户",
          plan: "套餐",
          status: "状态",
          credits: "积分",
          actions: "操作"
        },
        actions: {
          manage: "管理"
        }
      },
      logs: {
        title: "操作日志",
        desc: "管理员操作审计",
        loading: "正在加载日志...",
        error: "加载日志失败。",
        empty: "暂无日志。",
        search: "查询",
        reset: "重置",
        pagination: "第 {current} / {total} 页",
        prev: "上一页",
        next: "下一页",
        placeholders: {
          action: "按操作筛选",
          admin: "按管理员 ID 筛选",
          filter: "按关键字筛选",
          module: "按模块筛选",
          startDate: "开始日期",
          endDate: "结束日期"
        },
        labels: {
          level: "日志级别",
          module: "模块",
          keyword: "关键字",
          startDate: "开始日期",
          endDate: "结束日期"
        },
        table: {
          status: "状态",
          level: "级别",
          action: "操作",
          target: "目标",
          admin: "管理员",
          details: "详情",
          ip: "IP",
          createdAt: "时间"
        },
        levels: {
          all: "全部",
          error: "错误",
          warn: "警告",
          info: "信息"
        },
        copy: "复制详情",
        copying: "复制中...",
        copied: "已复制"
      },
      pagination: {
        jumpTo: "跳转到第",
        jump: "跳转",
        page: "页码"
      },
      settings: {
        title: "系统设置",
        desc: "调整平台运行参数",
        error: "加载设置失败。",
        loading: "正在加载设置...",
        empty: "暂无设置项。",
        search: "搜索",
        searchPlaceholder: "按配置名 / 分组 / 说明搜索",
        onlyChanged: "仅显示已修改",
        expandAll: "全部展开",
        collapseAll: "全部收起",
        saved: "设置已保存。",
        saving: "保存中...",
        saveConfirm: "保存该配置将覆盖当前值，是否继续？",
        save: "保存",
        boolean: "启用",
        items: "项",
        noMatch: "没有匹配到配置项。",
        unsavedChanges: {
          leaveTitle: "存在未保存修改",
          leaveConfirm: "你有未保存的系统设置修改，离开当前页面后这些更改将丢失，是否继续离开？",
          leaveNow: "确认离开",
          stay: "留在当前页"
        },
        updatedAt: "更新时间 {time}",
        typeHints: {
          secret: "敏感凭证，变更后会被系统加密保存，请勿外泄。",
          number: "数字类型配置，避免填写非数字内容。",
          boolean: "通过开关控制该能力是否启用。",
          text: "文本内容会直接影响当前功能行为，请谨慎修改。"
        },
        groups: {
          billing: "计费",
          pricing: "定价",
          ai_channel: "AI 渠道",
          platform: "平台",
          business: "业务",
          feature: "功能",
          other: "其他"
        },
        showMore: "展开剩余 {count} 项",
        showLess: "收起",
        ozonPublic: {
          title: "Ozon 公共查询 Key",
          desc: "用于类目/属性等公共只读查询。新用户即使未绑定店铺也能完成生成；真正发布仍需绑定自己的店铺。",
          clientId: "Client ID",
          apiKey: "API Key",  // pragma: allowlist secret
          configured: "已配置",
          notConfigured: "未配置",
          clearTitle: "清除 Ozon 公共查询 Key",
          saved: "Ozon 公共查询 Key 已保存。",
          saveConfirm: "你即将保存新的 Ozon 公共查询配置，确认后新用户将使用该配置进行公共查询，是否继续？",
          fillRequired: "测试前请先填写完整的 Client ID 与 API Key。",
          clientIdHint: "请填写 Ozon 控制台中的 public client ID。",
          apiKeyHint: "请填写 Ozon 控制台中的 public API Key，提交后该值不会回显。",  // pragma: allowlist secret
          test: "测试连接",
          testing: "测试中...",
          testSuccess: "Ozon 连接正常。",
          clear: "清除",
          clearConfirm: "确定清除 Ozon 公共查询 Key？这会影响新用户类目/属性查询。",
          cleared: "Ozon 公共查询 Key 已清除。",
          secretHint: "API Key 仅展示末 4 位，保存后不会回显。",  // pragma: allowlist secret
        }
      }
    },

    // 首页横幅
    hero: {
      badge: "AI 驱动的电商平台",
      title: "一键转化商品",
      titleHighlight: "上架全球市场",
      subtitle:
        "粘贴链接，AI 自动生成图片、文案、SEO 与平台适配，一键发布到 Ozon 等平台。把 30–60 分钟/条的 Listing 制作压缩到分钟级。",
      placeholder: "粘贴 1688 或速卖通链接...",
      button: "开始 AI 优化"
    },

    // 功能展示 1
    feature1: {
      title: "见证 AI 魔法",
      subtitle: "看我们如何将供应商原始数据转化为专业的电商 Listing。",
      tab1: "智能手表",
      tab2: "耳机",
      tab3: "运动鞋",
      imgBtn1: "AI 优化后",
      imgBtn2: "原图",
      imgBtn3: "对比",
      aiBadge: "AI 优化",
      marketingTitle: "营销素材",
      viewAll: "查看全部",
      listingTitleTag: "商品标题",
      translated: "已翻译",
      bulletTitle: "核心卖点",
      seoTitle: "SEO 关键词",
      pushBtn: "发布到平台",
      syncText: "已同步到 Ozon",
      // 手表产品
      prodTitle_watch: "高端智能手表 Pro Max",
      bullet1Title_watch: "健康监测",
      bullet1Desc_watch: "24小时心率、血氧、睡眠追踪",
      bullet2Title_watch: "超长续航",
      bullet2Desc_watch: "单次充电可用 14 天",
      key1_watch: "智能手表",
      key2_watch: "健身追踪器",
      key3_watch: "健康监测",
      hotspot1: "AMOLED 屏幕",
      hotspot2: "钛金属表壳",
      // 耳机产品
      prodTitle_headphones: "无线降噪耳机 Studio",
      bullet1Title_headphones: "主动降噪",
      bullet1Desc_headphones: "隔绝 99% 环境噪音",
      bullet2Title_headphones: "Hi-Res 音质",
      bullet2Desc_headphones: "40mm 驱动单元，支持 LDAC",
      key1_headphones: "无线耳机",
      key2_headphones: "降噪耳机",
      key3_headphones: "蓝牙耳机",
      hotspot_headphones_1: "记忆海绵耳垫",
      hotspot_headphones_2: "触控操作",
      // 运动鞋产品
      prodTitle_sneakers: "超轻跑步鞋 X9",
      bullet1Title_sneakers: "云端缓震",
      bullet1Desc_sneakers: "革命性泡棉科技",
      bullet2Title_sneakers: "透气网面",
      bullet2Desc_sneakers: "360° 通风系统",
      key1_sneakers: "跑步鞋",
      key2_sneakers: "运动鞋",
      key3_sneakers: "休闲运动鞋",
      hotspot_sneakers_1: "碳板支撑",
      hotspot_sneakers_2: "抓地大底"
    },

    // 功能展示 2 - 使用流程
    feature2: {
      title: "使用流程",
      subtitle: "三步完成一条 Listing：导入 → AI 优化 → 一键发布。",
      card1Title: "导入分析",
      card1Desc: "粘贴任意供应商链接，AI 自动提取商品数据、图片和规格。",
      card2Title: "AI 优化",
      card2Desc: "生成 SEO 标题、翻译描述、优化图片、创建 A+ 内容，分钟级完成。"
    },

    // 功能展示 3 - 安全
    feature3: {
      badge: "企业级安全",
      title: "保护您的品牌资产",
      subtitle: "军事级别的图片和 Listing 保护。",
      verified: "已验证",
      watermark: "神经水印",
      hash: "SHA-256 哈希",
      cert: "已认证",
      card1Title: "隐形水印",
      card1Desc: "AI 嵌入的标记，经过压缩和裁剪仍然有效。",
      card2Title: "法律证据包",
      card2Desc: "自动生成 DMCA 删除文件和区块链证明。",
      card3Title: "即时检测",
      card3Desc: "当您的图片出现在未授权的 Listing 时收到提醒。",
      downloadBtn: "下载白皮书",
      downloadSub: "安全架构概述",
      learnMore: "了解更多保护方案"
    },

    // 定价
    pricing: {
      title: "简单透明的定价",
      subtitle: "只对图片生成收费（超值模式 {creditsPerImage} 积分/张）。按每条 listing≈{imagesPerListing} 张图估算，直观看到可生成多少条 listing。",
      roiTitle: "算一笔人力账",
      roiDesc:
        "人工做 1 条 listing 通常要 {minutesMin}–{minutesMax} 分钟；要稳定批量，往往需要专职人员（约 {laborMin}–{laborMax} 元/月）。用 AI 把图片、文案、SEO、属性适配交给系统，你只需审核与一键发布。",
      timeSaveHint: "预计可节省约 {hoursMin}-{hoursMax} 小时（仅供参考）。",
      timeSaveHintMonthly: "每月预计可节省约 {hoursMin}-{hoursMax} 小时（仅供参考）。",
      monthly: "月付",
      yearly: "年付",
      save: "送 2 个月",
      bestForGuests: "游客首选",
      onDemand: "按需付费",
      credits: "积分",
      creditsLabel: "积分",
      imagesLabel: "张图片",
      perMonth: "/月",
      perYear: "/年",
      packBasic: "基础包",
      packGrowth: "成长包",
      packScale: "扩容包",
      bestValue: "最划算",
      featExpires: "充值积分永久有效",
      featAi: "AI 图片生成",
      featPsd: "高清素材下载",
      featPriority: "优先支持",
      purchase: "立即购买",
      planStarter: "入门版",
      planPro: "专业版",
      planBusiness: "商务版",
      featStore: "每月赠送积分",
      featBatch: "批量图片生成",
      featSupport: "优先支持",
      featBatchStarter: "基础批量生成",
      featBatchPro: "高级批量生成",
      featBatchBusiness: "高并发批量生成",
      featSupportStarter: "标准支持（48 小时）",
      featSupportPro: "优先支持（12 小时）",
      featSupportBusiness: "优先支持 + 成功经理",
      featExportsStarter: "高清素材下载",
      featExportsPro: "高清 + 4K 素材下载",
      featExportsBusiness: "品牌素材包导出",
      loadFailed: "计费信息加载失败，请重试或联系支持。",
      retryAction: "重新加载"
    },

    // 常见问题
    faq: {
      title: "常见问题",
      subtitle: "关于平台的一切你需要知道的。",
      contact: "联系客服",
      q1: "AI 优化会改坏我的商品信息吗？",
      a1: "不会。系统以原始数据为基准生成优化方案，所有字段在发布前可预览、可编辑，确保不会改变关键属性。",
      q2: "需要安装插件或绑定店铺吗？",
      a2: "无需安装插件或绑定店铺即可体验，粘贴链接即可开始。仅在发布或同步到平台时才需要授权。",
      q3: "我的数据和图片安全吗？",
      a3: "我们采用企业级加密与权限隔离，图片生成自带隐形水印与取证信息，防止被盗用。",
      q4: "试用与收费如何计算？",
      a4: "新用户可免费体验 1 次任务，体验仅可生成 1 张主图；后续按订阅或积分计费，透明无隐藏费用。"
    },

    // CTA
    cta: {
      title: "准备好扩展",
      titleBreak: "您的电商业务了吗？",
      subtitle: "加入数千名已在使用 AI 征战全球市场的卖家。",
      button: "免费开始"
    },

    // 页脚
    footer: {
      privacy: "隐私政策",
      terms: "服务条款",
      support: "联系我",
      rights: "© {year} 一键大卖 版权所有"
    },

    // Trust 页面
    trust: {
      forensicData: "取证元数据",
      badge: "企业级安全",
      title: "保护您的",
      titleHighlight: "数字资产",
      subtitle: "军事级别的产品图片和品牌保护。",
      dmcaBadge: "DMCA 保护",
      forensicBadge: "取证就绪",
      encryptedBadge: "256位加密",
      nav: {
        technology: "技术方案",
        legal: "法律框架",
        whitepaper: "资源"
      },
      resources: {
        copyrightTitle: "版权白皮书",
        copyrightDesc: "水印与取证技术的完整说明。",
        legalTitle: "法律证明材料",
        legalDesc: "用于平台验真的官方模板与证书。"
      },
      tech1Title: "神经水印",
      tech1Desc: "隐形标记，经过任何转换仍然有效。",
      tech2Title: "区块链时间戳",
      tech2Desc: "分布式账本上不可篡改的创作证明。",
      tech3Title: "AI 检测",
      tech3Desc: "全平台实时监控。",
      compareTitle: "看看区别",
      compareSub: "移动滑块查看隐藏水印。",
      rawOutput: "原始输出",
      status: "状态",
      ctaBtn: "开始保护",
      protectedLayer: "防护层",
      frameworkTitle: "法律框架",
      frameworkSub: "合规文档与可验证证据链。",
      licenseTitle: "授权协议",
      licenseDesc: "自动生成授权文件并提供不可篡改证明。",
      dmcaPackageTitle: "DMCA 取证包",
      dmcaPackageDesc: "一键生成平台下架所需证据材料。",
      complianceTitle: "合规就绪",
      complianceDesc: "满足平台执法要求的结构化证据。",
      platformLabel: "平台",
      priority: "优先级",
      refLabel: "参考编号",
      evidenceLabel: "证据",
      evidence1: "原始商品截图",
      evidence2: "水印验证哈希",
      generatorTitle: "自动 DMCA 生成器",
      generatorSub: "几分钟内生成完整执法文件。",
      generatorNote: "兼容 Amazon、Shopify、Etsy 等 30+ 平台。",
      letterAutomation: "自动生成 DMCA 通知",
      letterBody1: "本通知确认版权所有及侵权证据。",
      letterBody2: "所有元数据与时间戳已链上验证。",
      letterBody3: "相关证据已随案件材料附上。",
      letterBody4: "请立即移除侵权内容。",
      letterNotice: "本通知已准备好提交。",
      letterSign: "授权代表",
      ctaTitle: "准备好保护品牌了吗？",
      ctaTitleBreak: "马上启动执法",
      ctaSub: "5 分钟内完成合规保护部署。",
      downloadBtn: "下载证据包",
      verifiedHash: "已验证哈希",
      urlLabel: "侵权链接",
      selectPlatform: "选择平台",
      toLabel: "致",
      rights: "版权所有"
    },

    welcome: "欢迎回来",
    loginSubtitle: "请输入您的详细信息以登录您的专业帐户。",
    smsMethod: "短信验证码",
    passMethod: "密码",
    phoneLabel: "手机号码",
    phonePlaceholder: "138 0000 0000",
    mobilePlaceholder: "手机号码",
    phoneRequired: "请先填写手机号。",
    phoneInvalid: "手机号格式不正确。",
    sendCode: "发送验证码",
    sendCodeFailed: "验证码发送失败，请稍后重试。",
    debugCodeHint: "开发环境验证码：{code}",
    verifyLabel: "验证码",
    resendIn: "{time}秒后重发",
    resendNow: "重新发送",
    invalidCode: "验证码无效。请尝试 123456。",  // pragma: allowlist secret
    loginFailed: "登录失败，请稍后重试。",
    authErrorTermsRequired: "请先同意《用户协议》和《隐私政策》，再继续操作。",
    authErrorInvalidToken: "登录凭证无效，请重新登录后再试。",
    authErrorTokenExpired: "登录已过期，请重新登录后继续。",
    authErrorTokenRevoked: "当前登录状态已失效，请重新登录。",
    authErrorUnauthorized: "当前未登录，请先登录后重试。",
    authErrorForbidden: "当前账号没有此操作权限，如需帮助请联系支持。",
    authErrorUserNotFound: "未找到该账号，请检查手机号或先注册。",
    authErrorUserDisabled: "该账号已被禁用，请联系支持恢复。",
    authErrorInvalidCredentials: "手机号或密码错误，请重新输入。",
    authErrorPhoneInvalid: "手机号格式不正确，请输入 11 位手机号。",
    authErrorSmsRateLimited: "验证码请求过于频繁，请等待 1 分钟后重试。",
    authErrorSmsDailyLimit: "今日验证码发送次数已达上限，请明日再试。",
    authErrorSmsServiceUnavailable: "短信服务暂时不可用，请稍后再试。",
    passwordLabel: "密码",
    forgotPass: "忘记密码？",
    passPlaceholder: "输入您的密码",
    passwordRequired: "请输入密码。",
    passwordTooShort: "密码至少 8 位。",
    signIn: "登录",
    newToApp: "新用户？",
    signUp: "注册",
    createAccount: "创建您的账户",
    signupSubtitle: "加入 5,000+ 优化列表的卖家。",
    codeVerified: "验证码已验证",
    setPassPlaceholder: "设置您的密码",
    agree: "我同意",
    userAgreement: "用户协议",
    and: "和",
    privacyPolicy: "隐私政策",
    termsRequired: "请先阅读并同意用户协议与隐私政策。",
    autoRegisterHint: "未注册手机号验证后将自动创建账号。",
    getStarted: "免费开始",
    haveAccount: "已有账户？",
    login: "登录",
    resetTitle: "重置密码",
    resetSubtitle: "输入您的手机号码以接收验证码。",
    sendReset: "发送重置码",
    resetStep1: "验证手机号",
    resetStep2: "输入验证码",
    resetStep3: "设置新密码",
    resetCodeSent: "验证码已发送，请注意查收。",
    resetCodeHint: "请输入 6 位验证码",
    resetVerifyAction: "验证并继续",
    resetBackStep: "返回上一步",
    resetMismatch: "两次密码不一致。",
    resetRequirement: "至少 8 位，包含字母和数字。",
    resetSuccess: "密码已更新。",
    resetSuccessHint: "正在返回登录...",
    resetContextMissing: "重置会话已失效，请重新获取验证码。",
    resetFailed: "密码重置失败，请稍后重试。",
    resetStrengthLabel: "强度：{level}",
    resetStrengthWeak: "弱",
    resetStrengthMedium: "中",
    resetStrengthStrong: "强",
    backLogin: "返回登录",
    phoneVerified: "手机已验证",
    setNewPass: "设置新密码",
    newPassSubtitle: "请创建一个强密码以保护您的账户。",
    newPassLabel: "新密码",
    newPassPlaceholder: "创建新密码",
    strength: "强度：强",
    confirmPassLabel: "确认新密码",
    confirmPassPlaceholder: "重复新密码",
    updatePass: "更新密码",
    backSignIn: "返回登录",
    captchaLabel: "人机验证",
    captchaHint: "请先完成验证以继续。",
    captchaLoading: "验证加载中...",
    captchaRequired: "请先完成人机验证。",
    captchaUnavailable: "人机验证暂不可用，请稍后再试。",
    captchaLoadFailed: "验证加载失败，请刷新重试。",
    captchaError: "验证失败，请重试。",

    profile: {
      proPlan: "专业版",
      freePlan: "免费版",
      guest: "游客",
      coreNav: "核心导航",
      workspace: "工作区",
      assetMgmt: "资产管理",
      copyright: "版权资产",
      pricing: "价格策略",
      storeAuth: "店铺授权",
      marketIntel: "市场情报",
      beta: "Beta",
      marketIntelComingSoon: "市场情报功能即将上线。如需优先体验，请联系支持。",
      account: "账户",
      settings: "账户设置",
      billing: "账单与积分",
      help: "帮助中心",
      signOut: "退出登录"
    },

    settings: {
      general: "基本信息",
      security: "安全",
      preferences: "平台偏好",
      billing: "账单与积分",
      notifications: "通知",
      feedback: "我的反馈",
      integrations: "集成",
      storeAuth: "店铺授权"
    },

    // 店铺绑定
    storeBind: {
      title: "店铺授权管理",
      subtitle: "关联您的电商平台账号，开启 AI 智能发布能力。",
      guideTitle: "配置指南",
      step1Title: "登录 Ozon 后台",
      step1Desc: "前往 Ozon 卖家中心完成身份认证，开始接入流程。",
      step1Btn: "前往 Ozon 卖家中心",
      step2Title: "获取 API 凭证",
      step2Desc: "在顶部菜单中依次进入「设置」→「API 密钥」。",
      step2Btn: "打开 API 设置",
      step3Title: "生成管理员密钥",
      step3Desc: "选择「Admin」密钥类型并生成，复制 Client ID 和 API Key 填入下方表单。",

      // 店铺切换器
      activeSwitcher: "当前激活店铺",
      manageAll: "管理全部店铺",
      currentlyActive: "当前激活",
      synced: "已同步",
      connected: "已连接",
      disabled: "已停用",
      syncingShort: "同步中",
      syncFailedShort: "同步失败",
      uploads7d: "近7日上架",
      connectNew: "添加新店铺",

      // 平台选择
      fullApiIntegration: "完整 API 集成",
      readyConnect: "可立即接入",
      comingSoon: "即将支持",
      active: "已激活",

      // 连接表单
      connectOzon: "连接 Ozon 店铺",
      statusLabel: "状态",
      authorized: "已授权，准备就绪",
      noStoresLinked: "暂无已关联店铺",
      howToGetKey: "如何获取 API 密钥？",
      storeName: "店铺名称",
      storeNamePlaceholder: "例如：我的 Ozon 店铺",
      clientId: "客户端 ID",
      clientIdPlaceholder: "输入 Client ID",
      apiKey: "API 密钥",  // pragma: allowlist secret
      addStore: "添加 Ozon 店铺",
      testConnection: "测试连接",
      testing: "连接测试中...",

      // 同步状态
      syncingData: "正在同步店铺数据...",
      syncingDesc: "正在从 Ozon 导入类目、物流方式和仓储信息，请稍候片刻。",
      syncComplete: "同步完成",
      syncCompleteDesc: "类目及仓储数据已成功同步，您的店铺已准备就绪，可开始 AI 智能上架。",
      success: "成功",

      // 成功状态
      startListing: "开始智能上架",
      connectionSuccess: "连接成功",

      // 安全说明
      securityTitle: "API 连接安全保障",
      securityEncryption: "您的凭证采用 AES-256 军事级加密存储。",
      securityNoPassword: "一键大卖绝不会索取或存储您的平台登录密码。",

      // 表格
      authorizedStores: "已授权店铺",
      platform: "平台",
      actions: "操作",
      syncSettings: "同步设置",
      items: "件商品",

      // 设置弹窗
      settingsTitle: "店铺设置",
      settingsSubtitle: "管理店铺启用状态、自动同步与版权保护。",
      loading: "加载中...",
      settingActive: "启用店铺",
      settingActiveDesc: "关闭后将暂停该店铺的发布与同步。",
      settingAutoSync: "自动同步",
      settingAutoSyncDesc: "自动同步商品、库存与订单数据。",
      settingPsd: "PSD 版权保护",
      settingPsdDesc: "启用图片版权嵌入与证据材料生成。",
      settingSyncPsd: "同步 PSD 处理",
      settingSyncPsdDesc: "上传前自动嵌入 PSD 版权信息。",
      settingRotateKey: "更新 API Key",
      settingRotateKeyHint: "不填写则保持当前 API Key 不变。",
      deleteStore: "删除店铺",
      setDefault: "设为默认店铺",
      saving: "保存中...",
      saveChanges: "保存更改",

      // 同步弹窗
      syncModalTitle: "同步店铺数据",
      syncModalSubtitle: "同步 {name} 的数据。",
      syncModalDesc: "请选择要同步的数据类型。",
      syncProducts: "商品",
      syncProductsDesc: "从 Ozon 刷新商品列表。",
      syncStocks: "库存",
      syncStocksDesc: "按仓库刷新库存数量。",
      syncOrders: "订单",
      syncOrdersDesc: "刷新最近订单与状态。",
      syncNow: "开始同步",

      // 删除确认
      deleteTitle: "删除店铺",
      confirmDelete: "确定要删除该店铺吗？此操作不可恢复。",
      deleteConfirm: "删除",

      // 底部
      storageSystem: "存储体系：本地化 AES 加密",
      securityGuaranteed: "安全保障",
      encryptedBridge: "加密 API 通道",
      noPasswordStorage: "零密码存储",
      cancel: "取消",
      saveAuth: "保存授权",

      // 提示
      toastTitle: "Ozon 店铺就绪",
      toastDesc: "类目及仓储数据已成功同步。",
      toasts: {
        defaultUpdated: "默认店铺已更新。",
        noChanges: "没有需要保存的改动。",
        storeUpdated: "店铺设置已更新。",
        storeDeleted: "店铺已删除。",
        syncStarted: "已开始同步，数据将自动刷新。"
      },

      continuePublish: "继续发布",
      noStores: "暂无已授权店铺。",
      testSuccess: "API 凭证验证成功。",
      errors: {
        missingFields: "请完整填写店铺名称、Client ID 与 API Key。",
        testFailed: "连接测试失败，请检查凭证是否正确。",
        bindFailed: "店铺绑定失败（状态码 {status}）。",
        statusFailed: "加载店铺状态失败（状态码 {status}）。",
        syncFailed: "同步失败，可在「同步设置」中重试。",
        defaultFailed: "更新默认店铺失败。",
        storeDetailFailed: "加载店铺详情失败（状态码 {status}）。",
        storeUpdateFailed: "更新店铺失败（状态码 {status}）。",
        storeNameRequired: "店铺名称不能为空。",
        deleteFailed: "删除店铺失败（状态码 {status}）。",
        syncTypesRequired: "请至少选择一种同步类型。",
        syncTriggerFailed: "触发同步失败（状态码 {status}）。"
      }
    },

    publish: {
      title: "发布确认",
      subtitle: "当前已针对 Ozon 平台优化发布。",
      healthScore: "上架健康度",
      health: {
        excellent: "优秀可发布",
        good: "表现良好",
        warning: "需要检查",
        poor: "需要优化"
      },
      targetPlatforms: "1. 目标平台",
      storeLabel: "店铺",
      manageStores: "管理店铺",
      storeModeSingle: "单店铺",
      storeModeBatch: "批量",
      batchPrimary: "摘要店铺",
      batchHint: "批量模式会为每个店铺创建独立发布任务。",
      batchSelectedCount: "已选择 {count} 个店铺",
      selectAllStores: "全选",
      clearStores: "清空",
      creatingTasks: "正在创建发布任务（{current}/{total}）...",
      modeNoticeUpdate: "检测到已发布商品（offer_id: {offerId}），本次将自动执行“更新商品”，避免重复创建。",
      modeNoticeCreate: "当前没有已发布记录，本次将创建新的 Ozon 商品。",
      platformReady: "Ozon Global",
      platformUnavailable: "暂不可用",
      comingSoon: "即将支持",
      pricingConfirmation: "2. 价格确认",
      aiOptimized: "AI 智能定价",
      tablePlatform: "平台",
      tableSuggested: "建议价格",
      tableAdjusted: "调整价格",
      tableProfit: "预估利润",
      scheduleTitle: "3. 发布计划",
      publishNow: "立即发布",
      publishNowDesc: "立即同步到 Ozon 上架。",
      scheduleLater: "定时发布",
      scheduleLaterDesc: "选择高峰时间发布。",
      releaseDate: "发布日期",
      releaseTime: "发布时间",
      backToEdit: "返回编辑",
      confirmLaunch: "确认发布",
      confirmLaunchBatch: "确认批量发布（{count}）",
      launching: "发布中...",
      loading: "加载中...",
      progressTitle: "Ozon 发布进度",
      progressSubtitle: "OneClickSell · Ozon API 集成",
      publishing: "发布中",
      batchPublishing: "批量发布中",
      viewResult: "查看结果",
      estimatedRemaining: "预计剩余",
      stepCompleted: "已完成",
      stepInProgress: "进行中...",
      stepFailed: "失败",
      stepPending: "等待中",
      stepLabels: {
        validate: "校验发布数据",
        uploadImages: "上传商品图片",
        createListing: "创建 Ozon 商品",
        syncStock: "同步价格与库存",
        updateListing: "更新商品基础信息",
        updateAttributes: "同步商品属性",
        updatePrices: "同步商品价格",
        updateStock: "同步商品库存",
        updateImages: "同步商品图片",
        verifyUpdate: "验证更新结果"
      },
      doNotClose: "请勿关闭此页面。",
      doNotCloseDesc: "商品正在安全上传至 Ozon API。",
      successTitle: "商品发布成功",
      successSubtitle: "商品已通过校验并上线到平台。",
      successWarningTitle: "商品已创建（需处理）",
      successWarningSubtitle: "商品卡片已创建，但当前在 Ozon 后台不可见。",
      liveNow: "已上线",
      needsAction: "需处理",
      openInOzon: "前往 Ozon 店铺",
      openInOzonDisabledHint: "发布完成后将显示商品链接。",
      returnDashboard: "返回工作台",
      goToAssets: "进入资产库",
      publishAnother: "继续发布新商品",
      resultFooter: "© {year} AI Listing Optimizer. 批次号：{ref}",
      failureTitle: "Ozon 发布失败",
      failureSubtitle: "平台 API 未能处理该商品。",
      errorDetails: "错误详情",
      referenceId: "参考编号：{ref}",
      retryPublishing: "重新发布",
      retrying: "重试中...",
      contactSupport: "联系支持",
      batchFailuresTitle: "{count} 个店铺任务创建失败",
      manualReview: {
        title: "建议人工复核",
        desc: "Ozon 可能返回失败，但商品卡片已部分创建。请登录 Ozon 卖家后台核对真实状态。",
        partialSuccessHint: "如果你已在 Ozon 卖家后台看到该商品，通常表示上传已部分成功，仅需按后台提示修正具体字段。",
        checkOzon: "登录 Ozon 卖家后台查看该商品的具体校验错误。",
        fixInOzon: "若商品已创建，可在 Ozon 后台手工修正字段后再次提交。",
        aiNotPerfect: "AI 生成数据可显著提效，但并非 100% 准确，部分商品仍需要人工复核。"
      },
      recovery: {
        title: "恢复方案",
        loading: "正在分析失败原因...",
        reasonFallback: "Ozon 未返回明确错误原因，可先尝试重发或联系支持。",
        reasonCode: "规则",
        newOfferId: "新 SKU",
        newOfferIdPlaceholder: "请输入新的 SKU（offer_id）",
        newOfferIdHint: "仅支持字母、数字、下划线、连字符，最长 50 位。",
        retryWithNewSku: "使用新 SKU 重新发布",
        retrySamePayload: "使用原参数重发",
        executing: "提交中...",
        goBindStore: "检查店铺授权",
        offerIdRequired: "请先填写新的 SKU。",
        loadFailed: "恢复方案加载失败。",
        executeFailed: "重新发布失败，请稍后重试。",
        actions: {
          retry_with_new_offer_id: "建议：使用新 SKU 重发",
          retry_same_payload: "建议：原参数重试",
          fix_content_then_retry: "建议：先修复内容再发布",
          store_auth_required: "建议：先处理店铺授权",
          wait_and_poll: "建议：继续等待并刷新状态",
          contact_support: "建议：联系支持排查"
        }
      },
      result: {
        untitled: "未命名商品"
      },
      errors: {
        missingCollection: "缺少采集记录，请返回重试。",
        summaryFailed: "加载发布摘要失败（状态码 {status}）。",
        storeStatusFailed: "加载店铺状态失败（状态码 {status}）。",
        missingFields: "仍有必填项未完成，请先补齐。",
        noUpdateFields: "未检测到实际变更（价格/图片/内容均未变化），本次无需提交更新。",
        richContentInvalid: "富文本格式不正确，Ozon 不会接受，请重新生成后再发布。",
        missingFieldsHint: "缺失字段：{fields}",
        storeRequired: "请选择至少一个店铺。",
        missingSchedule: "请选择发布日期和时间。",
        publishFailed: "发布失败（状态码 {status}）。",
        statusFailed: "获取发布状态失败（状态码 {status}）。",
        retryFailed: "重试失败，请稍后再试。",
        batchPartialFailed: "{count} 个店铺创建任务失败，其余店铺已开始发布。",
        socksProxyDependencyMissing: "发布请求失败：系统代理组件缺失，已自动切换直连重试，请稍后再次提交。",
        viewTechnicalDetails: "查看技术详情",
        hideTechnicalDetails: "收起技术详情"
      }
    },

    capability: {
      title: "能力设置",
      subtitle: "设置默认的 6 Agent 工作流。Ozon 支持全量自动化，其他平台仅导出列表。",
      targetPlatform: "目标平台选择",
      supported: "全量支持",
      exportOnly: "仅导出列表",
      currentPlatform: "当前：",
      systemNotice: "系统通知",
      systemNoticeBody: "6 Agent 流程与平台无关，仅 Ozon 支持直连发布，其他平台会输出列表包供手动上传。",
      platformActive: "全量自动化",
      platformOzonDesc: "支持全流程与一键发布",
      platformAmazonDesc: "全球市场，导出列表后手动上传",
      platformShopifyDesc: "店铺型平台，导出列表包",
      platformAliDesc: "跨境平台，导出列表包",
      platformEbayDesc: "海外平台，导出列表包",
      languageLockedHint: "Ozon 语言已锁定为俄语",
      targetMarket: "目标市场",
      russiaDefault: "俄罗斯（Ozon 默认）",
      outputLanguage: "输出语言",
      russianDefault: "俄语（标准西里尔文）",
      contentStyleTitle: "内容风格",
      seoLevelTitle: "SEO 深度",
      customPromptTitle: "自定义提示词",
      customPromptPlaceholder: "请输入自定义提示词...",
      customPromptHint: "仅在选择「自定义风格」时生效。",
      brandGuardTitle: "品牌与合规保护",
      brandGuardDesc: "为内容与图片启用品牌保护和合规保障。",
      brandGuardHint: "平台侧将继续提供高级合规防护。",
      summaryTitle: "执行摘要",
      summaryHint: "本次将执行",
      summaryImages: "生成 {count} 张图片（主图 + 场景 + 卖点）。",
      summarySeo: "SEO 深度：{level}。",
      summaryRich: "富文本主题：{theme}。",
      summaryBrandOn: "品牌保护已启用。",
      summaryBrandOff: "品牌保护已关闭。",
      summaryExportOnly: "{platform} 当前仅支持导出列表。",
      saveDefault: "保存为默认",
      useDefault: "恢复默认",
      saving: "保存中...",
      saved: "已保存",
      skipAutoOpen: "保存为默认后不再自动弹出",
      skipAutoOpenHint: "需要修改时可从导航栏进入",
      aiContentGen: "AI 内容生成",
      aiContentDesc: "图片、标题和本地化规格",
      active: "活跃",
      directPublish: "一键直发",
      directPublishDesc: "跳过手动 CSV 导出/导入",
      ozonExclusive: "OZON 专属",
      launchBtn: "启动 AI 优化",
      status: "系统状态：准备好 Ozon 高速同步",
      secure: "安全协议",
      neural: "神经引擎 v4.2",
      apiSync: "API 同步活跃",
      quickModeSubtitle: "快速编辑现有配置",
      expertModeSubtitle: "完全控制所有设置",
      guidedModeSubtitle: "完成设置以开始使用",
      currentConfig: "当前配置",
      quickModeDesc: "编辑关键设置或使用当前配置启动 AI",
      expertMode: "专家模式",
      quickMode: "快速模式",
      showAdvanced: "显示高级设置",
      saveChanges: "保存更改",
      launchAI: "启动 AI",
      perProduct: "每件产品",
      platformLabel: "平台",
      marketLabel: "市场",
      styleLabel: "风格",
      imagesLabel: "图片",
      marketRuShort: "俄罗斯",
      marketUsShort: "美国",
      marketCnShort: "中国",
      marketGlobalShort: "全球",
      styleProfessionalDesc: "商务正式语气",
      styleCreativeDesc: "情感化故事叙述",
      styleConciseDesc: "简洁有力的表达",
      styleCustomDesc: "自定义提示词配置",
      stepPlatformHint: "先确认要执行流程的平台。",
      stepMarketHint: "确认本次上架内容面向的核心市场。",
      stepLanguageHint: "选择生成文案时使用的目标语言。",
      stepStyleHint: "选择最符合品牌调性的文案风格。",
      stepCustomPromptHint: "填写你希望 AI 严格遵循的自定义写作指令。",
      stepImageCountHint: "设置每个商品需要生成的图片数量。",
      stepSkipAutoOpenHint: "选择下次是否自动打开此配置流程。",
      stepSaved: "当前步骤已保存。",
      loginRequired: "请先登录后再保存设置",
      saveSuccess: "设置已保存",
      saveFailed: "保存设置失败",
      remoteLoadFallbackHint: "云端配置加载失败，已使用本地配置，可点击重试。",
      strategyTierTitle: "策略档位",
      strategyTierHint: "选择自动化深度与质量级别。",
      tierEconomy: "经济档",
      tierEconomyDesc: "图片数量更少、输出更快，适合快速起量。",
      tierBalanced: "平衡档",
      tierBalancedDesc: "质量与成本平衡，推荐默认使用。",
      tierPremium: "高阶档",
      tierPremiumDesc: "图片数量更多，模型质量更高。",
      guidedStepLabel: "第 {current} / {total} 步",
      guidedBack: "上一步",
      guidedNext: "下一步",
      guidedSaveNext: "保存并下一步",
      guidedLaunch: "启动优化"
    },

    aiConfig: {
      badge: "AI 配置",
      pageTitle: "AI 优化设置",
      pageSubtitle: "在启动优化任务前，确认并微调 AI 参数。",
      statusHint: "AI 建议已准备好复核",
      targetPlatform: "目标平台",
      platformActive: "Ozon 已启用",
      platformOzonDesc: "面向独联体市场与本地物流优化",
      platformAmazonDesc: "全球市场优化流程",
      platformShopifyDesc: "Shopify 店铺上架准备",
      platformAliDesc: "跨境上架准备",
      comingSoon: "即将上线",
      targetLanguage: "目标语言",
      languageRu: "俄语",
      languageEn: "英语",
      languageDe: "德语",
      languageFr: "法语",
      languageIt: "意大利语",
      languageEs: "西班牙语",
      languageJa: "日语",
      languagePtBr: "葡萄牙语（巴西）",
      languagePtPt: "葡萄牙语（葡萄牙）",
      languageNl: "荷兰语",
      languageSv: "瑞典语",
      languagePl: "波兰语",
      languageTr: "土耳其语",
      languageAr: "阿拉伯语",
      languageZhHans: "简体中文",
      languageZhHant: "繁体中文",
      languageCs: "捷克语",
      languageDa: "丹麦语",
      languageFi: "芬兰语",
      languageKo: "韩语",
      languageNb: "挪威语（博克马尔）",
      languageTh: "泰语",
      contentStyle: "内容风格",
      styleProfessional: "专业",
      styleCreative: "创意",
      styleConcise: "简洁",
      styleCustom: "自定义",
      stylePrompt: "风格提示逻辑",
      currentStyle: "当前风格：",
      customActive: "自定义逻辑生效",
      promptProfessional: "提示词：突出技术规格、耐用性与商务语气。",
      promptCreative: "提示词：强调情绪价值、故事化表达与生活方式收益。",
      promptConcise: "提示词：用简洁有力的句子聚焦核心卖点。",
      promptCustom: "已加载自定义提示词，请在高级设置中精细调整。",
      customPrompt: "自定义提示词编辑器",
      customPromptPlaceholder: "在此输入自定义提示词...",
      customPromptHint: "可用变量：{product_name}、{category}、{specs}",
      customHint: "前往高级编辑器完善自定义提示词",
      imageGeneration: "开启 AI 图片生成",
      imageGenerationDesc: "去除水印并生成生活方式场景图。",
      imageGenerationOnce: "仅本次生效。",
      imageCredits: "图片生成将消耗图片积分。",
      imageCount: "生成图片张数",
      imageCountHint: "可生成 1-10 张图片，至少 1 张主图；按顺序依次生成。",
      trialImageBadge: "体验仅生成 1 张主图",
      trialImageHint: "体验模式仅生成 1 张主图，升级后解锁完整图片组。",
      trialUpgradeCta: "查看套餐",
      seoLevel: "SEO 优化级别",
      seoStandard: "标准",
      seoStandardDesc: "基础关键词注入与元标签优化",
      seoDeep: "深度分析",
      seoDeepDesc: "语义聚类、竞品分析与长尾关键词整合",
      seoLockedHint: "SEO 深度已固定为最优（系统默认）。",
      recommended: "推荐",
      launch: "启动 AI 优化",

      modalTitle: "AI 优化配置",
      modalSubtitle: "启动 AI 任务前设置最终偏好。",
      reuseTitle: "检测到历史结果",
      reuseSubtitle: "该商品存在历史优化产出。",
      reuseDesc: "可复用最近一次生成的下游结果，或重新生成。",
      reuseNotice: "复用将直接展示结果，不会重新生成。",
      reuseTimeLabel: "最近生成时间：{time}",
      reuseStageLabel: "可复用阶段：{stages}",
      reuseSourceLabel: "来源任务：{id}",
      reuseViewHistory: "查看历史结果",
      reuseRegenerate: "重新生成",
      reuseUse: "复用历史结果",
      reuseApplying: "复用中...",
      useDefault: "使用默认配置",
      advanced: "高级设置",
      cancel: "取消",
      saveDraft: "保存草稿",
      saving: "保存中...",
      saved: "已保存",
      footerHint: "AI 将按照当前策略与积分消耗执行任务。"
    },

    ai: {
      workspaceTitle: "活跃列表优化",
      syncProgress: "同步进度",
      sourceRef: "来源参考",
      rawMedia: "原始媒体资产",
      brandProt: "品牌保护",
      agentOrch: "代理编排",
      neuralCenter: "神经洞察中心",
      sourceAnalysis: "来源分析与结构",
      byImageAnalyzer: "通过图像分析器",
      dataStructurer: "数据结构化工具",
      rawImages: "原始供应商图片",
      scanned: "已扫描",
      unstructured: "非结构化文本 → 结构化属性",
      processed: "已处理",
      seoArch: "SEO 关键词图谱",
      bySeo: "通过 SEO 架构师",
      optTitle: "优化标题",
      score: "评分",
      bullets: "要点",
      keywordStrat: "关键词策略",
      core: "核心",
      hot: "热门",
      longTail: "长尾",
      visualPipeline: "视觉资产管道",
      byImgSel: "通过图像选择器",
      visualPlanner: "视觉规划师",
      refSel: "参考选择",
      sourcesLocked: "来源已锁定",
      visualPlan: "视觉计划（生成提示）",
      readyArtist: "艺术家准备就绪",
      optGallery: "Listing 文案",
      byArtist: "通过视觉艺术家",
      marketInt: "平台适配",
      byRichBuilder: "通过丰富内容构建器",
      contextMaster: "上下文大师",
      aplusPreview: "富文本预览",
      fieldMap: "平台字段映射",
      motionProd: "导出交付",
      byVideoCreator: "通过视频创作者",
      rendering: "VEO 3.1 渲染引擎",
      scriptGen: "脚本已生成",
      analyzer: "商品理解",
      structurer: "商品理解",
      architect: "SEO 关键词代理",
      planner: "视觉策略",
      selector: "视觉策略",
      richContent: "平台适配",
      videoCreator: "导出代理",
      visualArtist: "Listing 文案",
      complete: "完成",
      thinking: "思考中",
      queued: "排队中",
      waiting: "等待中",
      idle: "空闲",
      active: "活跃（渲染中）",
      enableVideo: "启用视频生成",
      reviewChanges: "审查更改",
      reviewAfterGenerate: "生成后审查",
      oneClick: "一键发布",
      neuralActive: "神经洞察中心活跃。管道针对 {platform}{market}（{language}）市场。"
    },
    markets: {
      ru: "俄罗斯",
      us: "美国",
      cn: "中国",
      global: "全球"
    },

    studio: {
      control: {
        title: "生成与调试控制",
        simpleTitle: "AI 生成控制",
        collection: "Collection:",
        collectionEmpty: "未选择",
        setupRequiredTitle: "请先完成 AI 设置",
        setupRequiredDesc: "请先确认目标市场/语言与图片生成设置，再开始生成流程。",
        setupRequiredAction: "去 AI 设置",
        store: "店铺",
        manageStores: "管理店铺",
        marketStatus: "市场状态",
        skipImages: "跳过图片",
        onceOnly: "仅本次生效",
        costHint: "预计扣费：{count} 张 / {credits} 积分",
        confirmCostTitle: "确认扣费",
        confirmCostMessage: "本次预计生成 {count} 张图片，将扣除 {credits} 积分。\n是否继续？",
        confirmCostConfirm: "继续生成",
        start: "开始生成",
        regenerate: "重新生成",
        resume: "继续生成",
        starting: "启动中...",
        cancelGenerate: "取消任务",
        cancelling: "取消中...",
        runningStages: "执行中阶段",
        parallelRunningHint: "多 Agent 并行执行中",
        stageDebug: "单阶段调试",
        runStage: "执行单阶段",
        executing: "执行中...",
        infoStructureHint: "提示：执行商品理解只更新本阶段数据。"
      },
      credits: {
        balanceHint: "余额：{credits} 积分",
        totalBalanceHint: "总计：{credits} 积分",
        bucketSubscription: "订阅赠送积分",
        bucketRecharge: "充值积分",
        bucketLegacy: "旧积分",
        availableTooltipPrefix: "当前模式下，可用积分 = ",
        availableTooltipSuffixThirdParty: "（扣费顺序：订阅 → 充值 → 旧积分）",
        availableTooltipSuffixOfficial: "（扣费顺序：充值优先；是否使用订阅/旧积分取决于系统设置）",
        channelPlanLabel: "生图模式",
        channelPlanThirdParty: "超值模式",
        channelPlanOfficial: "官方模式",
        perImageHint: "{credits} 积分/张",
        channelPlanUnavailable: "该模式暂不可用，请联系管理员处理。",
        channelPlanUnavailableInline: "暂不可用",
        channelPlanCompareHint: "超值 {thirdParty} 积分/张 · 官方 {official} 积分/张",
        channelSwitchChecking: "正在检测任务重试状态...",
        channelSwitchBlockedDefault: "仅当当前任务自动重试全部耗尽后，才允许切换渠道。",
        switchOfficialTitle: "切换到官方模式？",
        switchOfficialConfirm: "官方模式每张图会消耗更多积分，是否继续切换？",
        switchOfficialConfirmWithDiff:
          "官方模式将按 {official} 积分/张扣费（当前 {current} 积分/张，每张多 {diff} 积分），确认切换？",
        switchOfficialConfirmLabel: "确认切换",
        switchOfficialCancelLabel: "取消",
        insufficientTitle: "积分不足",
        insufficientMessage: "本次需要 {needed} 积分，你当前仅 {current} 积分。\n充值后即可继续生成图片。",
        insufficientMessageGeneric: "当前积分不足，充值后可继续生成图片。",
        goRecharge: "去充值",
        later: "稍后",
        skipImages: "先跳过图片",
        stageHint: "你可以先勾选“跳过图片”继续生成文案与适配，稍后充值后再补图。",
        generateHint: "本次预计生成 {count} 张图片。也可以先跳过图片继续流程。",
        affordableHint: "按当前余额，最多可生成 {count} 张图片。可先去 AI 设置调整图片张数。",
        singleHint: "图片生成会消耗积分（按张计费）。充值后再试一次。"
      },
      errors: {
        missingCollection: "缺少 collectionId，无法加载 Studio 数据。",
        previewFailed: "预览数据加载失败。",
        stageStatusFailed: "阶段状态加载失败。",
        generateFailed: "生成启动失败。",
        stageExecuteFailed: "单阶段执行失败。",
        agentLogFailed: "Agent 日志加载失败。",
        previewRequestFailed: "preview 请求失败（状态 {status}）。",
        imagePlanRequestFailed: "image-plans 请求失败（状态 {status}）。",
        imageGenerationStatusRequestFailed: "image-generation-status 请求失败（状态 {status}）。",
        stageStatusRequestFailed: "stage-status 请求失败（状态 {status}）。",
        generateRequestFailed: "generate-stream 请求失败（状态 {status}）。",
        stageExecuteRequestFailed: "execute-stage 请求失败（状态 {status}）。",
        agentLogRequestFailed: "agent-logs 请求失败（状态 {status}）。",
        promptUpdateFailed: "更新 Prompt 失败。",
        imageGenerateFailed: "生成图片失败。",
        imageRegenerateFailed: "重生成图片失败。",
        imageDecisionFailed: "处理图片选择失败。",
        imageDeleteFailed: "删除图片失败。",
        cancelGenerateFailed: "取消生成失败。",
        queueBusy: "队列繁忙，请稍后再试。",
        refundHint: "积分已退款。",
        publishBlocked: "当前流程在「{stage}」阶段失败，请先继续生成修复后再发布。",
        publishNotReady: "当前内容尚未生成完成，请先完成生成后再发布。",
        publishImageOpsPending: "图片仍在处理中（保存/生成/重生成），请稍后再发布。",
        publishMissingImages: "当前缺少可上传的图片，请先生成/补充图片后再发布。",
        publishMissingFields: "仍有必填属性缺失，请先完成平台适配后再发布。",
        stageFailed: "阶段失败：{stage}"
      },
      publishGuide: {
        title: "发布前检查",
        subtitle: "请先处理以下缺失项后再发布。",
        close: "我知道了",
        itemImages: "缺少可上传的图片",
        itemPendingImages: "图片仍在处理中",
        itemCategory: "类目/商品类型缺失",
        itemTitle: "标题缺失",
        itemAttributes: "必填属性缺失",
        itemPrice: "价格缺失",
        itemAdapter: "平台适配未完成",
        itemStageFailed: "阶段失败：{stage}",
        itemStageIncomplete: "阶段未完成：{stage}",
        goFixImages: "去补图",
        goToStage: "去 {stage}"
      },
      action: {
        generateStarted: "已启动生成流程。",
        generateAdjusted: "积分不足，已自动降级为 {count} 张图片并继续生成。",
        generateSkipImagesAdjusted: "积分不足，已自动切换为跳过图片（可先生成文案/适配，稍后充值再补图）。",
        generateQueued: "同一生成任务正在执行，已为你恢复当前进度。",
        cancelRequested: "已提交取消请求。",
        stageTriggered: "已触发阶段执行：{stage}",
        stageQueued: "同一阶段任务正在执行：{stage}",
        promptUpdated: "已更新图片 {index} 的 Prompt。",
        imageGenerateTriggered: "已触发图片生成：{index}",
        imageGenerateQueued: "图片生成已在队列中：{index}",
        imageRegenerateTriggered: "已触发图片重生成：{index}",
        imageRegenerateQueued: "图片重生成已在队列中：{index}",
        imageCandidateAccepted: "已采用方案 {index} 的新图。",
        imageCandidateRejected: "已保留方案 {index} 的旧图。",
        imageDeleted: "已删除图片：{index}",
        queueRetrying: "队列繁忙，正在重试（{attempt}/{max}）..."
      },
      imageGeneration: {
        title: "图片生成任务",
        statusQueued: "排队中",
        statusRunning: "执行中",
        statusRunningBackground: "后台执行中",
        statusCompleted: "已完成",
        statusFailed: "失败",
        statusCancelled: "已取消",
        progress: "已完成 {done}/{total}",
        progressWithFailed: "已完成 {done}/{total} · 失败 {failed}",
        defaultQueued: "任务已进入队列，正在等待执行。",
        defaultRunning: "图片生成进行中。",
        defaultRunningBackground: "任务仍在后台执行，完成后会自动同步结果。",
        defaultCompleted: "图片生成已完成，结果已同步。",
        defaultFailed: "图片生成失败，积分会自动退款。",
        defaultCancelled: "图片生成任务已取消。",
        backgroundHint: "你可以继续其他操作，任务仍在后台执行。",
        lastUpdated: "最近更新：{time}"
      },
      module: {
        expand: "展开",
        collapse: "收起",
        collapsedHint: "模块已收起，点击“展开”查看详情。"
      },
      stage: {
        productUnderstanding: "商品理解",
        seo: "SEO 关键词图谱",
        listing: "Listing 文案",
        selector: "参考图筛选",
        visual: "视觉策略",
        imageGeneration: "图片生成",
        normalized: "通用结构",
        category: "类目匹配",
        adapter: "平台适配",
        export: "导出交付"
      },
      market: {
        medium: "中等竞争",
        high: "高竞争",
        low: "低竞争"
      },
      source: {
        title: "商品理解",
        verified: "数据已确认",
        pending: "等待数据",
        visualDiagnostics: "资产诊断",
        analyzing: "分析中",
        ready: "就绪",
        summary: "摘要",
        expand: "展开详情",
        collapse: "收起详情",
        thumbnailAlt: "缩略图",
        mainImageAlt: "主图",
        emptyImages: "暂无来源图片",
        emptyTitle: "等待来源图片",
        emptyDesc: "连接插件或导入产品后将自动完成商品理解。",
        imageCount: "图片数量：{count}",
        noImages: "暂无图片",
        scanning: "分析中...",
        sectionsTitle: "采集区块",
        sectionsEmpty: "暂无区块数据"
      },
      rawText: {
        title: "原始文本",
        length: "{count} 字符",
        emptyTitle: "暂无原始文本",
        emptyDesc: "运行商品理解后将解析来源文本。"
      },
      productCore: {
        title: "产品核心",
        materials: "材质",
        specs: "规格",
        useCases: "使用场景",
        audience: "适用人群",
        facts: "关键事实",
        emptyTitle: "产品核心待生成",
        emptyDesc: "运行商品理解后将生成核心事实。"
      },
      productSchema: {
        title: "产品数据结构",
        identity: "基础信息",
        titleLabel: "标题",
        brandLabel: "品牌",
        categoryLabel: "类目",
        attributes: "关键属性",
        categoryKeywords: "类目候选关键词",
        noCategoryKeywords: "暂无类目候选关键词",
        empty: "暂无属性数据",
        emptyTitle: "属性待生成",
        emptyDesc: "完成结构化阶段后将自动生成关键属性。",
        logistics: "物流信息",
        price: "价格",
        weight: "重量",
        dimensions: "尺寸",
        emptyValue: "—",
        sku: "SKU 概览",
        skuCount: "SKU 数量",
        skuUnit: "条",
        noColors: "暂无颜色",
        sizes: "尺码",
        noSizes: "暂无尺码",
        skuReadOnlyHint: "为保证参考图筛选与 SKU 一致，SKU 选择统一在商品预览页操作。",
        skuReadOnlyAction: "前往商品预览设置 SKU"
      },
      metrics: {
        images: "图片",
        skus: "SKU",
        attributes: "属性",
        sections: "分段",
        rawText: "原文"
      },
      imageText: {
        title: "图片文字提取",
        count: "图片 {count}",
        empty: "暂无图片文字结果",
        emptyTitle: "暂无识别结果",
        emptyDesc: "运行图片文字提取后将在此显示结果。"
      },
      seo: {
        strength: "搜索强度",
        failed: "SEO 生成失败",
        failedDesc: "暂无 SEO 结果。",
        optimizedKeywords: "优化关键词",
        emptyKeywords: "暂无关键词数据",
        emptyTitle: "SEO 关键词待生成",
        emptyDesc: "完成内容生成后将输出关键词与搜索词。",
        searchVolume: "搜索量区间",
        competition: "意图",
        unknown: "未知",
        clusterLabel: "聚类",
        volumeTier: "热度",
        volumeHigh: "高",
        volumeMedium: "中",
        volumeLow: "低",
        volumeUnknown: "未知",
        searchPipeline: "关键词图谱概览",
        pipelinePending: "关键词图谱生成中...",
        graphTitle: "关键词图谱可视化",
        graphEmpty: "暂无图谱节点。",
        meaningLabel: "中文含义",
        coverageTitle: "覆盖报告",
        missingAspects: "缺失维度",
        duplicates: "重复关键词",
        languageIssues: "语言问题",
        coverageEmpty: "暂无问题"
      },
      keyword: {
        primary: "核心",
        secondary: "次要",
        longTail: "长尾",
        conversion: "转化",
        transactional: "交易",
        information: "信息",
        other: "其他"
      },
      visual: {
        title: "视觉策略",
        subtitle: "图片规划 • {count} 组",
        collapseAll: "全部收起",
        regeneratePrompts: "重跑提示词",
        regeneratePromptsTitle: "重跑视觉提示词",
        regeneratePromptsDesc: "将重建完整 10 条提示词。自动生图默认使用前 {count} 条。",
        regeneratePromptsRunning: "执行中...",
        regeneratePromptsOnly: "仅重跑提示词",
        regeneratePromptsAndGenerate: "重跑并自动生图",
        regeneratePromptsAutoTitle: "重跑提示词并自动生图",
        regeneratePromptsAutoAction: "确认执行",
        regeneratePromptsAutoConfirm:
          "将重跑全部提示词并自动生成前 {count} 张图。自动生图按当前规则扣费（{credits} 积分/张），是否继续？",
        regeneratePromptsSuccess: "提示词重跑完成（共 {count} 条），剩余免费次数：{remaining}。",
        regeneratePromptsCharged: "本次提示词重跑扣费 {credits} 积分。",
        regeneratePromptsAutoQueued: "已提交前 {count} 张自动生图任务。",
        regeneratePromptsAutoFailed: "提示词已重跑，但自动生图任务未提交成功。",
        empty: "暂无视觉策略输出，请先运行视觉策略阶段。",
        emptyTitle: "视觉策略待启动",
        emptyDesc: "运行视觉策略阶段后，系统会在这里输出可编辑的提示词与生成结果。",
        emptyStep1: "步骤 1：运行视觉策略",
        emptyStep2: "步骤 2：审核提示词",
        emptyStep3: "步骤 3：生成图片",
        constraintsTitle: "约束条件",
        constraintsEmpty: "暂无约束",
        keywordsTitle: "关键词聚焦",
        keywordsEmpty: "暂无关键词",
        referenceTitle: "参考图",
        referenceEmpty: "暂无参考图",
        referencePreviewTitle: "参考图 #{index}",
        referenceSwitchTitle: "切换参考图",
        referenceSwitchHint: "可通过缩略图或左右箭头快速切换。",
        referenceSource: {
          sku: "SKU",
          main: "主图",
          collection: "采集图",
          extracted: "抽取结果",
          legacy: "历史缓存",
          manual: "手动锚点",
          sku_image_url: "SKU 图片URL",
          selected_sku_image_indices: "SKU 索引集",
          none: "无",
          unknown: "未知"
        },
        anchorModeImage: "图片锚点",
        anchorModeText: "文字锚点 · 阈值 {threshold}%",
        anchorSourceLabel: "来源：{source}",
        selectorSelectedCount: "已选 {count} 张",
        selectorSummaryTitle: "筛选摘要",
        selectorSummaryEmpty: "暂无筛选摘要",
        selectorRationaleEmpty: "暂无筛选理由",
        selectorPromptTitle: "选图 Prompt",
        selectorPromptEmpty: "暂无 Prompt 快照",
        selectorDecisionPrimary: "主组优先",
        selectorDecisionSupplement: "置信度补选",
        selectorDecisionFallback: "兜底补齐",
        selectorReasonTitle: "入选理由",
        selectorReasonEmpty: "暂无详细理由",
        selectorConfidence: "置信度 {value}%",
        selectorSkuConfidence: "SKU 置信度 {value}%",
        singleSkuDecisionTitle: "单 SKU 决策",
        singleSkuDecisionEmpty: "暂无单 SKU 决策结果",
        singleSkuUnknown: "未知 SKU",
        singleSkuBadge: "SKU · {sku}",
        singleSkuReasonsTitle: "选择原因",
        singleSkuReasonsEmpty: "暂无单 SKU 理由",
        singleSkuExcludedTitle: "排除 SKU 说明",
        singleSkuExcludedEmpty: "暂无排除 SKU 说明",
        singleSkuRiskPrefix: "风险提示：{risk}",
        singleSkuCandidatesTitle: "SKU 候选评分",
        singleSkuCandidatesEmpty: "暂无 SKU 候选评分数据",
        singleSkuCandidateScore: "评分 {value}",
        singleSkuCandidateImageCount: "关联图片 {count} 张",
        singleSkuCandidateReasonEmpty: "暂无候选评分理由",
        manualAnchorAction: "前往导入预览页并锁定锚点图",
        referenceSkuTitle: "图像归属 SKU",
        referenceSkuEmpty: "暂无 SKU 归属",
        referenceSkuReasonTitle: "归属理由",
        referenceSkuReasonEmpty: "暂无 SKU 归属理由",
        overlayLabel: "图中文字",
        promptCopied: "提示词已复制",
        sceneLabel: "场景",
        placeholderMainTitle: "主图方案",
        placeholderMainDesc: "核心展示与主视觉",
        placeholderSceneTitle: "场景方案",
        placeholderSceneDesc: "使用场景与氛围",
        placeholderDetailTitle: "细节方案",
        placeholderDetailDesc: "材质与卖点特写",
        trackPrimary: "主图/轮播图",
        trackSecondary: "详情图",
        expand: "展开",
        collapse: "收起",
        morePlansHint: "还有 {count} 个方案已准备好提示词，可随时补生成。",
        expandExtraPlans: "展开另外 {count} 个方案",
        collapseExtraPlans: "收起额外方案",
        viewMore: "查看 {count} 条",
        statusWaiting: "状态：等待中",
        statusGenerated: "状态：已生成 {current}/{total}",
        statusPrompted: "状态：已规划 {count}",
        galleryTitle: "生成图片预览",
        generated: "已生成",
        openImage: "查看大图",
        selectingReference: "正在筛选参考图...",
        savePrompt: "保存 Prompt",
        savePromptSaving: "保存中...",
        generateImage: "生成图片",
        generating: "生成中...",
        regenerate: "重新生成",
        regenerating: "重生成中...",
        pendingCandidateLabel: "新图待确认",
        pendingCandidateAt: "生成时间",
        pendingCandidateExists: "该方案已有待确认新图，请先选择保留旧图或使用新图。",
        compareOpen: "对比",
        compareUseNew: "使用新图",
        compareKeepOld: "保留旧图",
        compareKeepOldTitle: "保留当前图片",
        compareKeepOldConfirm: "确认保留方案 #{index} 的旧图并丢弃新候选图吗？",
        compareOld: "当前图片",
        compareNew: "新候选图",
        compareModeSlider: "滑块对比",
        compareModeSideBySide: "并排对比",
        compareModalTitle: "图片方案 #{index} 对比",
        compareModalDesc: "请对比新旧两张图，并选择最终保留版本。",
        deleteImage: "删除图片",
        deleteConfirmTitle: "删除已生成图片",
        deleteConfirmMessage: "确定删除图片方案 #{index} 的已生成图片吗？可稍后重新生成。",
        deleteConfirm: "删除",
        costHint: "预计扣费：{count} 张 / {credits} 积分",
        planBadge: "{index} {type}",
        planDefaultType: "主图",
        planTypeTitle: "{type} 方案",
        planTitle: "图片方案 {index}",
        planAspect: "比例 {ratio}",
        planSubtitle: "图片生成方案",
        historyTitle: "历史版本",
        historyEmpty: "暂无历史记录",
        historyAccepted: "已采纳",
        historyRejected: "已拒绝",
        historyViewImage: "查看",
        historyRestore: "恢复使用",
        historyRestored: "已恢复历史版本",
        historyReplaced: "已替换",
        historyCompare: "对比",
        historyAt: "{time}",
        stylePreset: {
          dramatic: "戏剧感",
          minimal: "极简",
          lifestyle: "生活方式",
          tech: "科技感",
          warm: "暖调",
          industrial: "工业风",
          nature: "自然风",
          luxury: "高奢"
        }
      },
      listing: {
        title: "Listing 文案",
        writing: "生成中...",
        keywordInput: "SEO 关键词输入",
        keywordEmpty: "暂无关键词提示。",
        emptyTitle: "Listing 待生成",
        emptyDesc: "运行 Listing 阶段后将输出文案结果。",
        fieldTitle: "标题",
        shortDesc: "短描述",
        longDesc: "长描述",
        bullets: "要点",
        emptyBullets: "暂无要点。",
        faq: "FAQ",
        emptyFaq: "暂无 FAQ。"
      },
      normalized: {
        title: "通用结构",
        platformUnknown: "平台",
        summary: "结构概览",
        platform: "平台",
        market: "市场",
        language: "语言",
        keywordCount: "关键词",
        imageCount: "图片",
        skuCount: "SKU",
        factCount: "事实",
        payload: "结构输出",
        emptyPayload: "暂无通用结构输出。",
        emptyTitle: "通用结构待生成",
        emptyDesc: "请先运行通用结构阶段。"
      },
      adapter: {
        title: "平台适配",
        rerun: "重新执行平台适配",
        platformUnknown: "平台",
        summary: "适配概览",
        platform: "平台",
        format: "格式",
        fieldCount: "字段数",
        category: "类目",
        typeId: "类型 ID",
        price: "价格",
        images: "图片",
        attributesCount: "属性数",
        richContent: "富文本",
        richContentMissingHint: "富文本缺失，建议重新执行平台适配生成。",
        missingFields: "缺失字段",
        missingFieldsEmpty: "暂无缺失字段",
        payload: "适配输出",
        emptyPayload: "暂无适配输出。",
        fieldMap: "字段映射",
        emptyMappingTitle: "暂无字段映射",
        emptyMappingDesc: "请先运行平台适配阶段。",
        mappingField: "字段",
        mappingSource: "通用结构来源",
        mappingStatus: "状态",
        mappingSuggestion: "建议",
        mappingStatusMapped: "已映射",
        mappingStatusInferred: "可推断",
        mappingStatusMissing: "缺失",
        mappingSuggestUseSource: "可从通用结构填充",
        mappingSuggestInfer: "需要推断或补全",
        mappingSuggestCategory: "需要类目匹配",
        mappingPlatformHint: "当前为通用结构输出，请运行 Ozon 适配后查看字段映射。",
        schemaTitle: "上传参数",
        schemaFetchedAt: "Schema 拉取时间 {time}",
        schemaEmpty: "暂无参数数据。",
        schemaField: "字段",
        schemaFieldId: "ID",
        schemaRequired: "必填",
        schemaType: "类型",
        schemaValue: "值",
        schemaDictionary: "字典值（{count}）",
        schemaStatus: "状态",
        schemaStatusFilled: "已填写",
        schemaStatusMissing: "缺失",
        schemaRequiredYes: "必填",
        schemaRequiredNo: "可选"
      },
      export: {
        title: "导出交付",
        exporting: "导出中...",
        open: "导出详情",
        reexport: "重新导出",
        platform: "平台",
        category: "类目",
        columns: "字段数",
        rows: "行数",
        templateKey: "模板标识",
        format: "格式",
        version: "模板版本",
        validation: "校验",
        validationPass: "通过",
        validationPending: "待校验 / 有问题",
        fileName: "文件名",
        delivery: "交付",
        ready: "下载就绪。",
        pending: "等待导出数据。",
        download: "下载",
        waiting: "暂无下载链接。",
        validationErrors: "校验问题"
      },
      preview: {
        title: "生成结果预览",
        back: "返回",
        retry: "重试加载",
        images: "预览图片",
        adapter: "平台适配输出",
        upload: "上传参数",
        export: "导出内容",
        noImages: "暂无预览图片",
        imageCountSuffix: "张",
        mainImageAlt: "主图预览",
        listingTitle: "标题",
        shortDesc: "短描述",
        bulletPoints: "卖点",
        longDesc: "长描述",
        faq: "FAQ",
        attributes: "商品属性",
        highlights: "核心卖点",
        seoKeywords: "SEO 关键词",
        metricsTitle: "内容概览",
        metricImages: "图片",
        metricSkus: "SKU",
        metricAttributes: "属性",
        metricSections: "模块",
        metricRawText: "原始文本",
        priceSummary: "商品要点",
        price: "价格",
        skuCount: "SKU 数量",
        weight: "重量",
        size: "尺寸",
        empty: "暂无数据",
        statusReady: "可审查",
        statusUploaded: "已上传，可更新",
        reviewBtn: "审查更改",
        publishBtn: "一键发布",
        updateBtn: "更新到 Ozon",
        updateUnavailable: "当前任务尚未上传成功，暂不可更新。",
        updateTriggered: "更新请求已提交。",
        saveDraft: "保存草稿",
        savingDraft: "保存中...",
        draftSaved: "草稿已保存。",
        draftNoChanges: "没有需要保存的改动。",
        draftSaveFailed: "草稿保存失败。",
        draftUnsaved: "草稿有改动，未保存",
        openPricingEngine: "打开定价引擎",
        setPrimaryImage: "设为主图",
        primaryImageCurrent: "当前主图",
        readonlyTitle: "只读字段说明",
        tagsHint: "标签使用空格或逗号分隔",
        oldPrice: "原价",
        invalidPrice: "售价必须是大于 0 的数字。",
        invalidOldPrice: "原价必须是大于 0 的数字。",
        invalidPriceRelation: "原价不能小于售价。",
        invalidWidth: "宽度必须是大于 0 的数字。",
        invalidHeight: "高度必须是大于 0 的数字。",
        invalidDepth: "深度必须是大于 0 的数字。",
        invalidWeight: "重量必须是大于 0 的数字。",
        invalidImages: "至少保留一张图片。",
        invalidPrimaryImage: "主图必须来自当前图片列表。",
        ozonOnly: "当前仅支持 Ozon 平台预览。",
        ozonPayloadMissing: "未找到 Ozon 上传数据，请先执行 adapter 阶段。",
        ozonListing: "Ozon 商品信息",
        ozonOfferId: "Ozon SKU",
        ozonCurrency: "币种",
        ozonSpecs: "规格信息",
        ozonDescription: "Ozon 简介",
        ozonTags: "Ozon 标签",
        ozonAttributes: "Ozon 属性",
        pricingFinalized: "定价已锁定",
        pricingEngineTitle: "定价引擎逻辑",
        pricingProfitability: "盈利能力",
        pricingMargin: "利润率",
        pricingFinalPrice: "建议上架价",
        pricingViewBreakdown: "展开明细",
        pricingHideBreakdown: "收起明细",
        pricingDiscountExplainTitle: "固定40%折扣口径",
        pricingDiscountExplainLine: "上架价会按折扣策略上浮。折扣 {discount}，实付系数 {pay_factor}。在 Ozon 后台填写 {listing_price}，买家实付约为 {target_price}。",
        pricingBreakdownMissing: "暂无成本拆解数据",
        pricingBreakdown: {
          baseCost: "采购成本",
          platformFee: "平台费用",
          logistics: "物流成本",
          packaging: "包装/贴单",
          otherFees: "其他费用"
        }
      },
      gallery: {
        main: "主图",
        empty: "暂无生成图片",
        slot: "空位 {index}",
        diffusing: "生成中...",
        loading: "加载中...",
        mainPlaceholder: "主图位置",
        emptySlot: "图片位 {index}",
        emptyHint: "等待生成",
        waiting: "生成中..."
      },
      marketplace: {
        emptyRich: "暂无富文本素材",
        widgets: "模块 {count}",
        images: "图片 {count}",
        theme: "主题 {theme}",
        emptyMapping: "暂无属性映射"
      },
      richContent: {
        title: "富文本结构",
        summary: "{count} 个模块",
        empty: "暂无富文本模块",
        emptyTitle: "富文本待生成",
        emptyDesc: "富文本阶段完成后，模块与图片将在此展示。",
        emptyPreview: "暂无富文本预览",
        images: "富文本图片",
        emptyImages: "暂无富文本图片",
        version: "版本",
        generatedAt: "生成时间",
        blocks: "{count} 个区块",
        rows: "{count} 行",
        columns: "{count} 列",
        untitled: "未命名模块",
        noDescription: "暂无文字内容",
        moreWidgets: "更多模块：{count}",
        emptyMappingTitle: "暂无字段映射",
        emptyMappingDesc: "完成属性填充后会自动生成映射。",
        detailHint: "结构已就绪",
        viewDetail: "查看详情",
        collapse: "收起",
        detailTitle: "模块详情",
        detailDesc: "来自富文本代理的结构化输出。",
        jsonTitle: "模块 JSON",
        blocksTitle: "区块预览",
        emptyBlocks: "暂无区块内容",
        galleryTitle: "富文本图片瀑布流",
        emptyGallery: "暂无富文本图片",
        emptyGalleryDesc: "生成富文本后将在此展示图片流。",
        widget: {
          showcase: "图文展示",
          text: "文本模块",
          table: "表格模块",
          video: "视频模块",
          list: "列表模块",
          unknown: "模块"
        }
      },
      video: {
        empty: "视频生成尚未启用。",
        hint: "开启视频阶段后将生成动效素材。",
        recording: "录制中..."
      },
      status: {
        complete: "完成",
        thinking: "思考中",
        processing: "处理中",
        backgroundProcessing: "后台处理中",
        error: "错误",
        waiting: "等待中"
      },
      agent: {
        productUnderstanding: "商品理解",
        seo: "SEO 关键词代理",
        listing: "Listing 文案",
        visual: "视觉策略",
        selector: "参考图筛选",
        normalized: "通用结构",
        category: "类目匹配",
        adapter: "平台适配",
        export: "导出交付",
        error: "阶段失败，请查看日志。"
      },
      agentLogs: {
        title: "Agent 运行日志",
        subtitle: "代理实时控制台",
        copyDiagnostics: "复制诊断信息",
        copied: "已复制",
        downloadLogs: "下载日志",
        chainTitle: "AGENT 日志流",
        streaming: "实时更新",
        emptyTitle: "暂无日志",
        emptyDesc: "执行阶段后会在此显示代理日志。",
        logCount: "{count} 条日志",
        lastUpdated: "更新于 {time}",
        duration: "耗时 {duration}",
        expandLog: "展开",
        collapseLog: "收起",
        status: {
          running: "运行中",
          completed: "已完成",
          failed: "失败",
          idle: "空闲"
        },
        event: {
          start: "启动",
          thinking: "思考",
          result: "完成",
          error: "错误",
          cacheHit: "缓存",
          skip: "跳过"
        }
      }
    },

    pricingEngine: {
      title: "一键大卖动态定价引擎",
      subtitle: "多平台利润计算器，内置各平台费率与费用逻辑。",
      returnToPreview: "返回预览继续编辑",
      calcMode: "计算模式",
      manualEntry: "手动输入",
      myListings: "我的列表",
      destPlatform: "目标平台",
      globalChannels: "{count} 个主流平台可用",
      ready: "就绪",
      soon: "即将推出",
      listingsOzonOnly: "当前仅支持 Ozon 列表同步。",
      inputMode: "输入模式",
      compactMode: "简化",
      fullMode: "完整",
      singlePlatformHint: "大多数卖家专注单一平台。",
      rateSource: "实时汇率来源",
      rateLoading: "更新中...",
      calculationLoadingTitle: "正在重新计算定价...",
      calculationLoadingDesc: "系统正在更新 Ozon 费率与利润模拟，请稍候后再重试。",
      calculationFailed: "定价计算失败",
      retryCalculation: "重试计算",
      rateUpdated: "更新于 {date}",
      rateError: "汇率异常",
      rateErrorDesc: "获取最新汇率失败，请稍后重试。",
      networkErrorDesc: "网络连接失败，请重试。",
      requestTimeoutDesc: "请求超时，请重试。",
      autoRateOn: "自动汇率",
      autoRateOff: "手动输入",
      refreshRate: "刷新汇率",
      autoRateHint: "已启用自动汇率（Frankfurter API）。",
      manualRateHint: "可手动覆盖汇率。",
      tiktokRegion: "销售地区",
      regionUs: "美国",
      regionUk: "英国",
      regionSea: "东南亚",
      tiktokRegionHint: "切换地区会自动更新佣金/交易费率，可按实际合同调整。",
      selectProduct: "从工作区选择产品",
      viewDetails: "查看详情",
      dataSynced: "数据已同步",
      pricingStrategy: "定价策略",
      targetMargin: "目标利润率 (%)",
      targetProfit: "目标利润 (¥)",
      tieredProfit: "分段利润",
      tieredProfitHint: "按采购成本区间配置利润目标。生效目标 = max(固定利润, 成本×利润率)。",
      currentTier: "当前命中",
      tierFixedProfit: "固定利润",
      tierProfitRate: "利润率",
      tieredTargetHint: "当前区间目标利润：{value}",
      tierLabel: "区间",
      targetValue: "目标值",
      quickAdjustTag: "实时快调",
      quickAdjustTitle: "在这里直接调整定价策略",
      quickAdjustHint: "可拖动滑杆或直接输入",
      quickAdjustClickHint: "点击切换到利润率编辑",
      autoCalculated: "基于当前输入自动计算",
      breakEven: "盈亏平衡",
      suggestedPrice: "建议上架价格",
      ozonTargetPriceLabel: "客户实付价",
      rate: "汇率：1 CNY = {rate} {currency}（输入）",
      breakEvenPrice: "盈亏平衡价格：{price}",
      optimizedMsg: "优化为 {value}% 利润率",
      optimizedProfit: "优化为 {value} 利润",
      netProfit: "净利润",
      profitMargin: "利润率",
      targetMet: "目标达成",
      targetNotReached: "目标未达到（已应用上限）",
      roi: "投资回报率",
      highMargin: "高利润",
      updateListing: "更新列表价格",
      syncDesc: "立即将此价格同步到您工作区的 Ozon 列表。",
      syncSuccess: "价格同步完成",
      syncSuccessDesc: "已将 {price} 同步至 {platform} 列表。",
      feeBreakdown: "平台费用明细",
      catLogic: "类目逻辑已应用",
      commission: "市场佣金",
      lastMile: "最后一公里配送",
      handling: "处理与仓储",
      totalOpCost: "总运营成本",
      costBreakdown: "成本拆解",
      exportReport: "导出报告",
      shareImage: "生成分享图",
      shareReady: "分享图已生成",
      shareReadyDesc: "{platform} 分享图已保存到本地。",
      shareError: "分享图生成失败",
      shareErrorDesc: "无法渲染分享图，请稍后重试。",
      saveOzonConfig: "保存 Ozon 策略",
      savingConfig: "保存中...",
      applyOzonConfig: "应用到 Ozon 流程",
      saveAndApplyOzonConfig: "保存并应用到 Ozon 流程",
      applyingConfig: "应用中...",
      saveConfigSuccess: "Ozon 策略已保存",
      saveConfigSuccessDesc: "已成功保存你的 Ozon 自定义定价策略。",
      saveConfig: "保存配置",
      saveConfigFailed: "保存失败",
      saveConfigFailedDesc: "保存 Ozon 定价策略失败，请稍后重试。",
      applyConfig: "应用配置",
      applyConfigSuccess: "策略已应用",
      applyConfigSuccessDesc: "后续 Ozon 生成将使用当前策略。",
      applyConfigTriggeredDesc: "已应用策略，并开始回刷当前商品历史定价结果。",
      applyConfigNoCollectionDesc: "已应用到后续生成；当前页面无商品上下文，请前往 Studio 回刷。",
      applyConfigAutoSyncSuccessDesc: "策略已应用，并已自动同步到预览草稿（含固定40%折扣口径）。",
      applyConfigAutoSyncFailedDesc: "策略已应用，但自动同步预览失败。请点击“同步价格到预览”重试。",
      nextStepTitle: "下一步操作",
      nextStepSubtitle: "你可以立即应用当前定价、导出方案快照，或继续进入发布流程。",
      nextStepApplyCurrent: "应用到当前商品",
      nextStepApplyCurrentDesc: "将当前定价策略推送到已选中的商品上下文。",
      nextStepExportPlan: "导出方案",
      nextStepExportPlanDesc: "把当前输入参数与计算结果导出为 JSON 文件。",
      nextStepGoPublish: "进入发布",
      nextStepGoPublishDesc: "基于当前商品上下文，继续后续发布流程。",
      nextStepDisabledCalculating: "系统正在重新计算定价，请稍候再操作。",
      nextStepDisabledInvalidPrice: "当前建议价无效，请先调整输入参数。",
      nextStepApplyDisabledPlatform: "“应用到当前商品”仅支持 Ozon 平台。",
      nextStepApplyDisabledAuth: "请先登录后再应用到当前商品。",
      nextStepApplyDisabledContext: "请先选择商品上下文后再执行应用。",
      nextStepApplyDisabledApplying: "正在应用策略，请稍候。",
      nextStepApplyDisabledSaving: "正在保存策略，请稍候。",
      nextStepGoPublishDisabledAuth: "请先登录后再进入发布流程。",
      nextStepGoPublishDisabledContext: "请先选择商品上下文后再进入发布。",
      nextStepExportSuccess: "定价方案已导出。",
      nextStepExportFailed: "导出定价方案失败，请重试。",
      applyConfigFailed: "应用失败",
      applyConfigFailedDesc: "应用 Ozon 策略失败，请稍后重试。",
      loadConfigFailed: "加载失败",
      loadConfigFailedDesc: "加载已保存的 Ozon 策略失败。",
      loginRequiredTitle: "请先登录",
      loginRequiredDesc: "登录后才可保存并应用 Ozon 策略。",
      loadingOzonConfig: "正在加载 Ozon 策略...",
      updatedAt: "最近更新：{date}",
      ozonOnlyHint: "仅 Ozon 平台支持策略应用，其他平台即将支持。",
      ozonFlowStrategyTitle: "Ozon 流程策略",
      ozonFlowStrategyDesc: "直接把当前计算器目标同步到 Ozon 生成流程。",
      collectionContextTitle: "当前商品上下文",
      contextLoading: "正在加载当前商品上下文...",
      contextEmpty: "未检测到 collection 上下文，可在下方快速选品。",
      contextPriceHint: "当前建议 CNY 价格：{price}",
      contextLoadFailedDesc: "加载当前商品定价上下文失败。",
      collectionUnnamed: "未命名商品",
      quickSourceTitle: "快速选品",
      sourceAssets: "资产库",
      sourceTasks: "任务列表",
      sourceSearchPlaceholder: "按标题或链接搜索",
      sourcesLoading: "正在加载商品列表...",
      sourcesEmpty: "未找到匹配商品。",
      sourcesLoadFailedDesc: "加载快速选品列表失败。",
      sourceUploaded: "已上传",
      sourcePending: "未上传",
      sourceNotReadyTitle: "暂不可用",
      sourceNotReadyDesc: "该任务尚未生成 collection，暂不能用于定价。",
      syncToPreview: "同步价格到预览",
      syncingPreview: "同步中...",
      syncPreviewHint: "将当前计算结果同步到 Studio 预览草稿：上架价 = 客户实付价 ÷ 0.6（固定40%折扣）。",
      syncPreviewMissingCollection: "缺少 collection 上下文，请从 Studio 预览进入或先在上方选择商品。",
      syncPreviewInvalidRate: "当前汇率无效，无法将 RUB 正确换算为 CNY。",
      syncPreviewInvalidPrice: "建议价无效，无法同步。",
      syncPreviewFailed: "同步失败",
      syncPreviewFailedDesc: "无法将最新定价细节同步到 Studio 预览。",
      syncPreviewSuccess: "已同步到预览",
      syncPreviewSuccessDesc: "最新定价明细已写入 Studio 预览草稿。",
      ozonMinProfitSyncHint: "当前 Ozon 最低利润：¥{value}",
      ozonTieredProfitSyncHint: "当前 Ozon 区间目标：¥{value}（{range}）",
      ozonStrategyTitle: "Ozon 生成策略",
      ozonStrategyDesc: "以下配置会持久保存，并在 Ozon 生成流程定价阶段真实生效。",
      ozonProfitRate: "目标利润率",
      ozonMinProfit: "最低利润",
      ozonPromotionReserve: "促销预留",
      ozonOperationMode: "运营模式",
      ozonAdvertisingStrategy: "广告策略",
      ozonAdvertisingNone: "不投放",
      ozonAdvertisingCpoCpc: "CPO/CPC",
      ozonAdvertisingAggressive: "激进投放",
      ozonCrossBorder: "卖家类型",
      ozonCrossBorderOn: "跨境卖家",
      ozonCrossBorderOff: "本土卖家",
      ozonDefaultCategory: "默认品类代码",
      ozonExchangeRate: "自定义汇率（可选）",
      ozonDomesticShipping: "国内头程",
      ozonLabelingFee: "贴单费",
      ozonReturnRate: "退货率",
      ozonWithdrawalRate: "提现费率",
      ozonOrderProcessingRate: "订单处理费率",
      ozonAcquiringRate: "收单费率",
      ozonAgentRate: "Ozon 代理费率",
      ozonPartnerDeliveryRate: "Partner Delivery 基础费率",
      logisticsProvider: "物流商",
      logisticsProviderMode: "物流商选择",
      logisticsProviderModeAuto: "自动（推荐）",
      logisticsProviderModeManual: "手动",
      logisticsProviderUni: "UNI（旧版）",
      logisticsProviderUral: "Ural（2026-03 报价）",
      autoModeLabel: "自动",
      ozonDiscountExplainTitle: "固定40%折扣口径",
      ozonDiscountExplainLine: "先计算客户实付价，再反推后台上架价。折扣 {discount}，实付系数 {pay_factor}。客户实付 {target_rub}，后台应填写 {listing_rub}。",
      ozonDiscountExplainFormula: "公式：客户实付 = 上架价 × 0.6，因此上架价 = {target_cny} ÷ 0.6 = {listing_cny}。",
      saveScenario: "保存方案",
      prodMetadata: "产品元数据（锁定）",
      prodCategory: "产品类目",
      purchaseCost: "采购成本 (CNY)",
      weight: "重量 (kg)",
      dimensions: "尺寸 (长*宽*高 cm)",
      logistics: "物流",
      air: "空运",
      sea: "海运",
      saveWorkspace: "将此计算保存到您的工作区",
      saveWorkspaceDesc: "创建免费账户，直接同步到 Ozon 并跟踪您的利润历史。",
      createAccount: "创建免费账户",
      basicCosts: "基础成本",
      costCurrency: "成本币种：CNY → {currency}",
      purchaseCostLabel: "采购成本",
      shippingCostLabel: "头程运费",
      shippingEst: "预计国际物流（估算）",
      estimatedShipping: "预计运费",
      estimatedShippingHint: "基于当前重量、尺寸和配送方式估算。",
      packagingCostLabel: "包装/处理成本",
      otherCostLabel: "其他成本",
      estimatedTag: "估算",
      shippingCharged: "向买家收取的运费",
      exchangeRateLabel: "汇率 (1 CNY → {currency})",
      inputGuardrailInvalid: "请输入有效数字（范围 {range}），系统已自动修正为 {value}。",
      inputGuardrailAdjusted: "可接受范围：{range}，系统已自动修正为 {value}。",
      inputRangeBetween: "{min} - {max}",
      inputRangeMinimum: "≥ {min}",
      platformFees: "平台费用输入",
      marginCapHint: "当前可达利润率上限：{value}%",
      categoryLabel: "类目/细分",
      referralFeeLabel: "平台佣金",
      referralRateLabel: "平台佣金/费率",
      commissionRate: "佣金费率",
      referralMinFeeLabel: "最低佣金",
      sellingPlan: "销售计划",
      sellingPlanIndividual: "个人",
      sellingPlanProfessional: "专业",
      sellingPlanFee: "按件收费",
      closingFee: "成交费",
      closingFeeLabel: "成交费",
      fulfillmentMode: "履约方式",
      fulfillmentFba: "FBA",
      fulfillmentFbm: "FBM",
      fulfillmentFee: "履约费用",
      storageFee: "仓储费",
      inboundShippingFee: "入仓运费",
      fbmHandlingFee: "自发货处理费",
      platformDiscount: "平台补贴/折扣",
      tax: "税费/VAT",
      sellerDiscount: "卖家优惠",
      affiliateRate: "达人分佣费率",
      affiliatePartnerRate: "联盟分佣费率",
      adsRate: "广告投放费率",
      marketingCost: "营销成本",
      shippingType: "配送模式",
      shippingPlatform: "平台配送",
      shippingSeller: "卖家配送",
      shippingFbt: "FBT 平台履约",
      fbtFulfillmentFee: "FBT 履约费",
      fbtStorageFee: "FBT 仓储费",
      transactionFeeRate: "交易手续费率",
      transactionFeeFixed: "交易固定费",
      serviceFeeRate: "平台服务费率",
      serviceFeeFixed: "平台固定服务费",
      orderServiceFee: "订单服务费",
      platformSupportFee: "平台支持费",
      platformSupportFeeHint: "部分站点按单收取固定支持费（如马来西亚）。",
      shippingSubsidy: "运费补贴/项目成本",
      ozonPlacementFee: "Ozon 上架/服务费",
      ebayFinalValueRate: "成交费率",
      ebayPerOrderFee: "每单固定费用",
      ebayInsertionFee: "刊登费",
      ebayInternationalRate: "跨境交易费率",
      etsyListingFee: "上架费",
      etsyTransactionFee: "成交费率",
      etsyPaymentRate: "支付处理费率",
      paymentFeeRate: "支付费率",
      etsyPaymentFixed: "支付固定费",
      etsyOffsiteAdsRate: "站外广告费率",
      shippingSupportRate: "物流支持费率",
      totalCost: "总成本",
      totalBaseCost: "基础成本合计",
      platformFeeTotal: "平台费用",
      baseCostTotal: "基础成本",
      platformInsights: "平台提示",
      marketingCosts: "营销与达人费用",
      temuProgram: "Temu 补贴/激励",
      aliexpressProgram: "AliExpress 活动费用",
      shopeeProgram: "Shopee 项目费用",
      temuHeroTitle: "Temu 低佣金模型",
      temuHeroDesc: "多数品类佣金为 0%，可单独跟踪服务费、履约费与补贴。",
      temuHeroTag1: "0% 佣金",
      temuHeroTag2: "服务费可调",
      aliexpressHeroTitle: "AliExpress 类目佣金",
      aliexpressHeroDesc: "佣金随类目变化，建议同步建模支付/服务费。",
      aliexpressHeroTag1: "5-8% 佣金",
      aliexpressHeroTag2: "无年费",
      shopeeHeroTitle: "Shopee 费用叠加",
      shopeeHeroDesc: "叠加佣金、交易费与项目费，快速估算净利。",
      shopeeHeroTag1: "多费率",
      shopeeHeroTag2: "项目可选",
      shopeeHeroTag3: "支持费",
      amazonHighlightTitle: "Amazon 费用快照",
      amazonHighlightDesc: "同时考虑佣金、销售计划与 FBA/FBM 成本，快速确定价格下限。",
      ebayHighlightTitle: "eBay 成交费提示",
      ebayHighlightDesc: "成交费通常基于商品+运费+税费，叠加每单固定费与跨境费。",
      walmartHighlightTitle: "Walmart 佣金提醒",
      walmartHighlightDesc: "佣金按类目变化，WFS 等履约成本可在此基础上补充。",
      etsyHighlightTitle: "Etsy 列表周期提示",
      etsyHighlightDesc: "上架费、成交费、支付费与站外广告可按单配置。",
      platformNotesAmazon: "包含类目佣金、按件收费（个人计划）以及 FBA/FBM 履约费用。",
      platformNotesTikTok: "包含类目佣金、交易手续费、达人分佣与可选 FBT 履约费用。",
      platformNotesOzon: "佣金与类目/履约模式相关；国际物流、提现与退货预留属于估算项。",
      platformNotesEbay: "成交费基于商品+运费+税费，另加每单固定费用。",
      platformNotesWalmart: "佣金随类目变化，履约成本未在此快速计算中展开。",
      platformNotesEtsy: "包含上架费、成交费、支付处理费及可选站外广告费。",
      platformNotesTemu: "Temu 佣金可能为 0%，但服务费与履约成本需按项目填入。",
      platformNotesAliExpress: "AliExpress 佣金按类目变化，部分地区有支付/服务费。",
      platformNotesShopee: "Shopee 通常包含佣金、交易费与可选项目/支持费。",

      // Strategy presets (new)
      selectStrategy: "选择定价策略",
      strategyHint: "根据您的业务目标选择",
      strategyVolume: "快速起量",
      strategyVolumeDesc: "低利润率 (15-20%)，快速打开市场",
      strategyBalanced: "均衡增长",
      strategyBalancedDesc: "中等利润率 (25%)，平衡销量与利润",
      strategyProfit: "利润优先",
      strategyProfitDesc: "高利润率 (35%)，最大化单笔收益",
      strategyCustom: "自定义策略",
      strategyCustomDesc: "完全手动控制所有参数",
      strategyScenarioLabel: "适用场景",
      strategyRiskLabel: "风险提示",
      strategyVolumeScenario: "适合新品冷启动或活动期，需要先快速拉起订单体量。",
      strategyVolumeRisk: "单笔利润空间较薄，广告波动和退货会更容易吞噬利润。",
      strategyBalancedScenario: "适合稳定增长阶段，兼顾转化效率与利润表现。",
      strategyBalancedRisk: "若促销折扣失控，利润率会被持续侵蚀。",
      strategyProfitScenario: "适合差异化明显、需求稳定且具备一定议价能力的商品。",
      strategyProfitRisk: "定价抬升后，若竞品跟进降价，转化率可能下降。",
      strategyCustomScenario: "适合已掌握成本结构和渠道基准的成熟运营阶段。",
      strategyCustomRisk: "手动调参依赖持续监控，目标值越界会放大计算偏差。",
      strategyImpactTitle: "策略影响项",
      strategyImpactSubtitle: "切换后建议重点关注以下变化",
      strategyVolumeImpact1: "建议价基线下探，优先提升点击率与进店流量。",
      strategyVolumeImpact2: "库存周转速度提升，但单笔净利润贡献会下降。",
      strategyVolumeImpact3: "需要严格控制投放上限，避免利润被进一步压缩。",
      strategyBalancedImpact1: "利润与转化保持在中间区间，周度表现更稳定。",
      strategyBalancedImpact2: "便于持续 A/B 调整，不容易出现价格大幅波动。",
      strategyBalancedImpact3: "保留一定利润缓冲，可承接中等力度促销。",
      strategyProfitImpact1: "提升单笔贡献利润，优先追求净利润最大化。",
      strategyProfitImpact2: "需要内容差异化、评价质量和履约体验共同支撑。",
      strategyProfitImpact3: "更适合精细化流量投放，不建议盲目放量。",
      strategyCustomImpact1: "当前目标参数将直接主导建议价计算结果。",
      strategyCustomImpact2: "可快速配合活动档期或类目试验进行定制调优。",
      strategyCustomImpact3: "请持续校验输入范围，避免异常值影响策略判断。",
      recommended: "推荐",
      estimatedProfit: "预计利润",
      estimatedSales: "预计月销量",
      profitTarget: "利润目标",
      profitTargetHint: "可选择利润率、固定利润或分段利润。",
      enabled: "启用",

      // Slider
      adjusting: "调整中...",

      // Actions
      saving: "保存中...",
      applying: "应用中...",

      // Cost breakdown
      clickForDetails: "点击查看详情",

      // Logistics
      logisticsHint: "根据重量和尺寸估算物流成本影响。",

      platforms: {
        amazon: "Amazon",
        tiktok: "TikTok Shop",
        ozon: "Ozon",
        temu: "Temu",
        aliexpress: "AliExpress",
        shopee: "Shopee",
        ebay: "eBay",
        walmart: "Walmart",
        etsy: "Etsy"
      }
    },

    copyright: {
      nav: "版权资产",
      title: "版权与法律资产",
      subtitle: "一站式证据包，用于即时知识产权执法和版权保护。",
      statusActive: "法律安全状态：活跃",
      protected: "受保护列表",
      certified: "认证包",
      onChain: "链上证明",
      ready: "执法就绪",
      filter: "筛选",
      searchPlaceholder: "按产品名称或 SKU 搜索...",
      table: {
        preview: "资产预览",
        product: "产品 / 平台",
        status: "认证状态",
        bundle: "法律证据包",
        actions: "操作",
        download: "下载完整法律包",
        previewBtn: "预览",
        synced: "同步：",
        certified: "已认证",
        docs: "高清文档"
      },
      bundleNav: {
        back: "返回",
        details: "包详情",
        downloadCurrent: "下载当前",
        downloadAll: "下载完整证据包 (.zip)",
        contents: "包内容",
        letter: "官方授权书",
        psd: "取证 PSD 源文件",
        watermark: "神经水印分析",
        blockchain: "区块链创作证明",
        dispute: "知识产权争议模板",
        securityHash: "安全哈希"
      },
      tabs: {
        docView: "文档视图",
        metaDash: "元数据仪表板",
        xray: "X 射线分析"
      },
      letter: {
        title: "平台授权书",
        seal: "已加盖官方印章",
        certTitle: "真实性证书",
        certSub: "官方法律证据记录",
        docId: "文档 ID",
        date: "签发日期",
        subject: "主题识别",
        prodName: "产品名称",
        sku: "资产 SKU",
        jurisdiction: "平台管辖权",
        grade: "证据等级",
        gradeValue: "取证认证",
        body1: "本文件构成正式法律证据，证明上述资产已在一键大卖知识产权保护库中验证和注册。",
        body2: "验证包括 PSD 源文件的取证分析、在转换过程中持久存在的神经水印嵌入，以及公共区块链账本上的时间戳。",
        officer: "法律合规官",
        signature: "数字签名：",
        footer1: "安全取证链：#991-AUTH-Z",
        footer2: "专有证据格式 v4.2"
      },
      psd: {
        reportTitle: "取证元数据报告",
        verified: "元数据已验证",
        fileName: "MasterSource_V4_Final.psd",
        layers: "6 个图层",
        dpi: "300 DPI",
        colorProfile: "Adobe RGB",
        colProp: "技术属性",
        colValue: "取证值",
        created: "创建时间戳",
        createdDesc: "文件系统起源日期",
        fingerprint: "神经指纹 (SHA-256)",
        fingerprintDesc: "唯一数学标识",
        author: "作者 ID",
        authorDesc: "已验证的创作者签名",
        software: "软件签名",
        softwareDesc: "引擎和构建版本",
        seed: "嵌入水印种子",
        seedDesc: "持久神经追踪器",
        engine: "分析引擎",
        integrity: "完整性检查",
        secure: "100% 安全"
      },
      watermark: {
        title: "神经水印 X 射线",
        scanning: "正在扫描神经层...",
        status: "状态",
        detected: "检测到神经水印",
        score: "弹性评分",
        stable: "加密稳定",
        encodingTitle: "神经编码",
        encodingDesc: "此水印在生成扩散过程中嵌入神经层。与传统像素叠加不同，此签名是图像权重的一部分，使其能够抵抗基于 AI 的擦除、裁剪或噪声注入。",
        latentSeed: "潜在种子",
        engineSig: "引擎签名",
        inference: "推理时间",
        confidence: "扫描置信度"
      },
      blockchain: {
        title: "区块链验证证书",
        subtitle: "资产来源的不可变证明",
        verified: "已链上验证",
        network: "网络：",
        networkVal: "Arbitrum One / 以太坊主网",
        txHash: "交易哈希 (TxID)",
        signature: "数字签名 (EdDSA)",
        block: "区块高度",
        timestamp: "时间戳 (UTC)",
        legalNoteTitle: "法律可采性说明",
        legalNoteDesc: "此证书提供加密安全的时间戳和作者记录。记录永久锚定在 Arbitrum L2 网络上，继承以太坊结算层的完整安全性。",
        scan: "扫描验证",
        verifStatus: "验证状态",
        protocol: "OneClick-ChainLink 预言机 v2",
        confirmed: "已确认",
        confirmations: "节点确认数",
        integrity: "数据完整性",
        secure: "100% 安全"
      },
      dispute: {
        preview: "预览文档",
        settings: "模板设置",
        language: "语言：",
        title: "知识产权侵权删除通知",
        subtitle: "自动法律合规文档",
        to: "致：法律合规部门",
        ref: "参考：",
        date: "日期：",
        s1Title: "1. 所有权声明",
        s1Body: "本人，签署人，特此在伪证处罚下声明，本人是本文所述知识产权版权所有者的授权代表。一键大卖公司对与此声明相关的专有数字资产拥有专有权利。",
        s2Title: "2. 侵权描述",
        s2Body: "我们已在以下位置发现未经授权使用我们的版权视觉资产：",
        s2Url: "URL：",
        s2Desc: "此列表使用的专有产品图像和创意布局与我们的原创作品相同或实质相似。",
        s3Title: "3. 原创性证据",
        s3Body: "争议资产的原创性和创建日期通过附件证据包中包含的以下加密方法进行验证：",
        s3Item1: "神经水印：",
        s3Item1Desc: "嵌入图像数据中与我们主文件匹配的隐形取证标记。",
        s3Item2: "区块链证明：",
        s3Item2Desc: "记录在 Arbitrum 主网上的不可变创作证明。",
        s4Title: "4. 法律声明",
        s4Body: "本人真诚相信，以被投诉的方式使用该材料未经版权所有者、其代理人或法律授权。本通知中的信息准确无误。我们要求立即删除侵权材料。",
        signature: "授权签名",
        division: "一键大卖公司知识产权保护部",

        sidebar: {
          verifStatus: "验证状态",
          link: "侵权链接",
          linkDesc: "已提供 URL",
          psd: "取证 PSD",
          psdDesc: "图层数据已验证",
          evidence: "其他证据",
          missing: "缺少 2 个模块",
          prob: "执法概率",
          lowConf: "低置信度",
          guide: "证据完成指南",
          scan: "神经水印扫描",
          boost30: "缺失：+30% 提升",
          complete: "立即完成",
          proof: "区块链证明",
          boost22: "缺失：+22% 提升",
          autoFiling: "自动提交",
          filingNote: "置信度高于 80% 前不建议提交。",
          autoSubmit: "自动提交声明",
          processing: "处理中...",
          submitted: "声明已提交"
        }
      }
    },

    workspace: {
      nav: "工作区",
      heroTitle: "创建您的第一个 AI 列表",
      heroSub: "粘贴链接 → AI 优化 → 一键发布。在几秒钟内转换产品数据。",
      returningLabel: "欢迎回来",
      returningTitle: "继续优化你的最新列表",
      returningSub: "你的最近导入已准备就绪。继续完善内容或进入任务中心查看进度。",
      startBtn: "开始导入产品",
      taskCenterEntry: "进入任务中心",
      pasteLink: "粘贴链接",
      pasteDesc: "从速卖通、1688、亚马逊等平台导入。",
      uploadMedia: "上传媒体",
      uploadDesc: "拖放图片、视频或 ZIP 文件。",
      batchImport: "批量导入",
      batchDesc: "通过 CSV 或多个 URL 批量导入产品。",
      recentImports: "最近导入",
      recentImportsDesc: "你的最新导入及其当前状态",
      viewHistory: "查看历史",
      extRequired: "需要 Chrome 扩展",
      extNotFound: "未找到",
      extOutdated: "版本过低",
      extDesc: "从主要平台安全收集数据所必需。",
      extDownload: "下载扩展",
      extUpdate: "更新扩展",
      extRecheck: "我已更新，重新检测",
      extUpgradeRequired: "插件版本需要更新",
      extUpgradeDesc: "当前版本 {current}，最低要求 {required}。请先更新后再继续采集。",
      extVersionUnknown: "未知版本",
      watchTutorial: "观看视频教程",
      readGuide: "阅读用户指南",
      status: {
        processing: "处理中",
        ready: "待发布",
        uploaded: "已上传",
        failed: "失败"
      },
      loadFailed: "加载最近导入失败。",
      loadHint: "请重试以获取最新任务进度。",
      retry: "重试",
      emptyTitle: "暂无导入记录",
      emptyDesc: "先导入一个产品链接，就能在这里查看进度。",
      loginTitle: "登录后查看导入记录",
      loginDesc: "登录即可同步你的导入历史与流程进度。",
      loginAction: "立即登录",
      unknownSource: "未知来源",
      totalImports: "总导入数",
      completed: "已完成",
      processing: "处理中"
    },

    // 任务中心
    taskCenter: {
      nav: "统一工作台",
      breadcrumb: {
        workspace: "工作区",
        taskCenter: "任务中心"
      },
      title: "统一工作台",
      subtitle: "在一个页面完成任务调度与已完成资产运营，减少来回跳转。",
      target: {
        label: "目标：",
        platform: "Amazon",
        region: "美国",
        language: "英语"
      },
      sync: {
        label: "同步进度",
        value: "85%"
      },
      reviewChanges: "查看变更",
      noReviewableTasks: "暂无可查看的变更，请先完成采集或生成。",
      createTask: "创建新任务",
      dropdown: {
        singleTitle: "单品任务",
        singleDesc: "通过单个链接导入",
        batchTitle: "批量导入任务",
        batchDesc: "通过多链接或 CSV 导入"
      },
      table: {
        source: "来源",
        task: "任务名称 / ID",
        status: "状态",
        progress: "AI 进度",
        createdAt: "创建时间",
        action: "操作"
      },
      status: {
        active: "进行中",
        completed: "已完成",
        waiting: "等待中",
        idle: "空闲",
        collect_pending: "等待采集",
        assigned: "已分配采集",
        collecting: "采集中",
        collect_uploading: "上传中",
        collect_completed: "采集完成",
        collect_failed: "采集失败",
        collect_cancelled: "已取消",
        collect_expired: "已超时",
        pending_generate: "等待生成",
        generating: "生成中",
        publishing: "发布中",
        ready: "待发布",
        uploaded: "已发布",
        publish_failed: "发布失败",
        generation_failed: "生成失败"
      },
      batchLabel: "批量",
      itemsLabel: "件",
      progress: {
        processing: "处理中",
        copywriting: "AI 文案",
        inQueue: "排队中"
      },
      stage: {
        collect_pending: "等待采集",
        collect_assigned: "插件已接单",
        collect_preparing: "准备采集",
        collect_scrolling: "页面滚动中",
        collect_collecting: "采集数据中",
        collect_saving: "保存数据中",
        collect_uploading: "上传素材中",
        collect_completed: "采集完成",
        collect_failed: "采集失败",
        collect_cancelled: "任务已取消",
        collect_expired: "任务已超时",
        generate_pending: "等待生成",
        generate_info_structure: "商品理解中",
        generate_seo: "SEO 关键词图谱生成中",
        generate_listing: "Listing 文案生成中",
        generate_visual: "视觉策略生成中",
        generate_adapter: "平台适配中",
        generate_export: "导出交付中",
        publish_pending: "准备发布",
        publish_processing: "发布中",
        publish_scheduled: "已排期",
        publish_failed: "发布失败",
        publish_completed: "发布完成",
        generate_ready: "生成完成待发布",
        generate_uploaded: "已发布",
        generate_failed: "生成失败"
      },
      actions: {
        workspace: "工作区",
        preview: "预览",
        generate: "开始生成",
        publish: "去发布",
        viewPublish: "查看发布",
        update: "更新商品",
        batchOverview: "批次总览",
        assets: "进入资产视图",
        retry: "重试",
        retryCollect: "重新采集",
        retryGenerate: "重试生成",
        forceStopRetry: "终止并重试",
        pending: "待处理",
        archive: "归档",
        delete: "删除"
      },
      tooltips: {
        previewLocked: "采集完成后可预览",
        previewUnavailable: "采集完成后才可预览",
        previewCollecting: "采集中，稍后可预览。",
        previewFailed: "采集失败，暂无可预览内容。",
        expand: "展开批量任务",
        collapse: "收起批量任务",
        errorFallback: "任务执行失败，请重试或联系客服。",
        workspaceLocked: "采集与 AI 处理完成后可进入工作区",
        actionLocked: "任务处理中，请稍后再试"
      },
      confirmArchive: "确定要归档该任务吗？",
      confirmDelete: "确定要删除该任务吗？此操作无法撤销。",
      actionFailed: "操作失败，请稍后重试。",
      generateNotReady: "采集结果正在同步中，请稍后刷新再试。",
      retryUnavailable: "该任务缺少可重试的采集ID，请重新导入发起新任务。",
      errorSummaryDefault: "任务执行异常，请重试。",
      manualReview: {
        shortHint: "请到 Ozon 后台核对，该商品可能已部分创建。",
        detailHint:
          "Ozon 可能返回发布失败，但部分数据已创建成功。请登录 Ozon 卖家后台查看具体报错并手工修正字段。AI 生成数据并非 100% 准确，仍需人工复核。"
      },
      pagination: {
        showing: "显示 {count}/{total} 个任务"
      },
      stats: {
        total: "总数",
        active: "进行中",
        completed: "已完成",
        failed: "失败",
        waiting: "等待中",
        successRate: "成功率",
        avgTime: "平均完成时间",
        performance: "性能"
      },
      bulk: {
        selected: "已选择 {count} 项",
        retry: "重试",
        archive: "归档",
        delete: "删除",
        selectAll: "全选",
        deselectAll: "取消全选",
        confirmArchive: "确定要归档选中的 {count} 个任务吗？",
        confirmDelete: "确定要删除选中的 {count} 个任务吗？此操作无法撤销。"
      },
      view: {
        cards: "卡片",
        list: "列表",
        compact: "紧凑"
      },
      batch: "批量",
      refresh: "刷新",
      exportCSV: "导出 CSV",
      clearFilters: "清除筛选",
      importComposerToggleExpand: "展开快速导入",
      importComposerToggleCollapse: "收起快速导入",
      surfaceTasks: "待处理任务",
      surfaceAssets: "已完成资产",
      focusTitle: "这里用于任务调度与异常恢复",
      focusHint: "进行中/失败任务在此处理，已完成商品请切换到资产视图继续运营。",
      focusShowAll: "查看全部任务",
      focusGoAssets: "切换到资产视图",
      assetsFocusTitle: "这里用于已完成资产运营",
      assetsFocusHint: "在此处理待发布/已发布结果；重试与异常请切回任务视图。",
      assetsFilterAll: "全部结果",
      assetsFilterReady: "待发布",
      assetsFilterUploaded: "已发布",
      assetsFilterFailed: "需修复",
      filterPanelExpand: "展开筛选",
      filterPanelCollapse: "收起筛选",
      shortcuts: {
        title: "快捷键",
        refresh: "刷新任务",
        selectAll: "全选",
        bulkArchive: "批量归档",
        bulkDelete: "批量删除",
        bulkRetry: "批量重试",
        gridView: "网格视图",
        listView: "列表视图",
        compactView: "紧凑视图",
        search: "搜索",
        newTask: "新建任务"
      },
      banner: {
        template: "{pipeline}，{brand}。目标：{target}。任务完成后可进入工作区。",
        pipelineMulti: "多代理流水线已启用",
        pipelineLite: "轻量流水线已启用",
        brandOn: "品牌保护已启用",
        brandOff: "品牌保护未启用"
      },
      loading: "任务加载中...",
      emptyTitle: "这里暂时还没有任务",
      emptyHint: "导入单个商品或创建批量任务后，将在这里查看进度。",
      emptyAction: "创建新任务",
      emptyImportAction: "导入商品",
      emptySampleAction: "查看示例",
      emptyHelpAction: "帮助文档",
      emptyNoActiveTitle: "当前没有进行中的任务",
      emptyNoActiveHint: "你可以查看历史任务，或切换到资产视图管理已完成商品。",
      emptyNoActivePrimaryAction: "查看全部任务",
      emptyNoActiveSecondaryAction: "打开资产视图",
      assetsEmptyTitle: "当前还没有可运营资产",
      assetsEmptyHint: "请先创建并运行任务，完成后会自动出现在资产视图。",
      assetsEmptyPrimaryAction: "创建任务",
      assetsEmptySecondaryAction: "返回任务视图",
      loginHint: "登录后即可查看任务中心",
      loginDesc: "登录账户以追踪采集、生成与上传的完整进度。",
      loginAction: "去登录",
      loadFailed: "任务加载失败",
      retryHint: "请刷新页面或稍后重试。",
      retryAction: "重新加载",
      modal: {
        title: "批量导入任务",
        subtitle: "一次性向目录中添加多个商品。",
        productUrls: "商品链接",
        totalLinks: "唯一链接总数：",
        onePerLine: "每行一个链接",
        runModeTitle: "运行模式",
        runMode: {
          collect: {
            title: "只采集",
            desc: "只抓取商品信息，不生成、不发布（适合先收集素材）"
          },
          generate: {
            title: "采集 + 生成",
            desc: "采集完成后自动开始 AI 生成，停在“待发布”"
          },
          publish: {
            title: "一键上新",
            desc: "采集 + 生成 + 自动发布到你选择的店铺"
          }
        },
        recommended: "推荐",
        storeTitle: "选择要发布的店铺",
        storeNotBoundTitle: "还没有绑定 Ozon 店铺",
        storeNotBoundDesc: "先绑定店铺的 API 信息，才能发布到 Ozon。",
        storeBindAction: "去绑定",
        storeSelected: "已选择 {count} 个店铺",
        storeSelectAll: "全选",
        storeClear: "清空",
        defaultStore: "默认",
        scheduleTitle: "发布时间",
        scheduleNow: "立即发布",
        scheduleLater: "定时发布",
        scheduleHint: "时间以系统时区为准。",
        advancedTitle: "高级设置",
        skipImagesTitle: "不生成图片",
        skipImagesDesc: "更快，只生成文案。后续可再补图片。",
        useCacheTitle: "复用历史结果",
        useCacheDesc: "如果有历史缓存，将复用以节省时间和消耗。",
        emptyWarning: "请至少输入一个商品链接。",
        storeNotBound: "你还没有绑定 Ozon 店铺。现在去绑定吗？",
        storeEmptyWarning: "请选择至少 1 个店铺用于发布。",
        scheduleEmptyWarning: "请选择定时发布的日期和时间。",
        fileEmptyWarning: "文件中未找到有效链接。",
        duplicateWarning: "检测到 {count} 个重复链接，已自动移除。",
        uploadTitle: "上传 TXT 或 CSV 文件",
        uploadSubtitle: "每批最多 500 条链接，每行一个 URL。",
        uploadHint: "需要模板？下载示例文件",
        browseFiles: "浏览文件",
        cancel: "取消",
        startBatch: "开始批量导入",
        startCollect: "开始采集",
        startGenerate: "采集并生成",
        startPublish: "一键上新",
        batchPartial: "批量导入部分成功",
        batchSuccess: "批量导入已提交。"
      },
      batchOverview: {
        title: "批次总览",
        subtitle: "查看该批次每个商品在各店铺的发布结果。",
        back: "返回任务中心",
        batchIdLabel: "批次ID：",
        publishBatch: "发布到店铺",
        publishMore: "补发布到更多店铺",
        manageSchedule: "管理排期",
        refresh: "刷新",
        exportFailedCsv: "导出失败 CSV",
        exportingCsv: "导出中...",
        exportFailedCsvMessage: "导出失败，请稍后重试。",
        loading: "批次加载中...",
        loadFailed: "批次加载失败",
        retryLoad: "重新加载",
        empty: "该批次暂时还没有可展示的记录。",
        hint: "提示：点击某个店铺状态，可以查看更详细的发布进度。",
        searchPlaceholder: "搜索标题 / ID / 链接",
        search: "搜索",
        clear: "清除",
        viewLabel: "查看",
        view: {
          all: "全部",
          failed: "失败",
          publishing: "发布中",
          completed: "已发布",
          scheduled: "已排期"
        },
        storeColumns: "店铺列",
        storeColumnsHint: "已显示 {shown}/{total}",
        storeColumnsManage: "管理",
        pagination: "显示 {start}-{end} / {total}",
        prev: "上一页",
        next: "下一页",
        retryAllFailed: "重试全部失败",
        retryStoreFailed: "重试 {count} 个失败",
        retryItemFailed: "重试失败店铺",
        retrying: "重试中...",
        retrySubmitted: "已提交重试。",
        retryFailed: "重试失败，请稍后再试。",
        retryConfirm: "确定要重试该批次所有失败的发布吗？",
        retryStoreConfirm: "确定要重试该店铺的失败发布吗？",
        relaunchModal: {
          title: "智能恢复重发",
          subtitle: "范围：{scope}。系统已按失败类型生成重发方案。",
          scopeBatch: "整个批次",
          scopeStore: "当前店铺",
          scopeSelection: "选中失败项",
          planFailed: "恢复方案生成失败。",
          noActionable: "当前范围内没有可自动重发的失败项。",
          reasonFallback: "未返回可解释的错误详情。",
          originalSku: "原 SKU",
          newSkuLabel: "新 SKU",
          newSkuPlaceholder: "请输入新的 SKU",
          newSkuHint: "仅支持字母、数字、下划线、连字符，最长 50 位。",
          manualHint: "该失败需先人工处理（店铺授权 / 内容修复 / 联系支持）。",
          skippedTitle: "跳过记录（{count}）",
          selectedCount: "已选择 {count} 条可重发记录",
          selectAtLeastOne: "请至少选择一条可重发记录。",
          offerIdRequired: "请先补全新 SKU。",
          confirmExecute: "确认提交 {count} 条重发任务？",
          submit: "确认重发",
          submitting: "提交中...",
          executeFailed: "批量重发失败，请稍后再试。",
          executeSuccess: "重发已提交：成功 {created} 条，跳过 {skipped} 条。",
          actions: {
            retry_with_new_offer_id: "新 SKU 重发",
            retry_same_payload: "原参数重试",
            fix_content_then_retry: "先修复内容",
            store_auth_required: "先处理店铺授权",
            wait_and_poll: "继续等待",
            contact_support: "联系支持"
          },
          summary: {
            total: "已分析",
            newSku: "新 SKU",
            retry: "原参数重试",
            manual: "需人工处理"
          },
          skipped: {
            NOT_IN_BATCH: "不属于当前批次",
            NOT_FOUND: "发布记录不存在",
            NOT_RETRYABLE_STATUS: "当前状态不可重试",
            STORE_FILTERED: "被店铺筛选条件过滤",
            COLLECTION_ID_MISSING: "缺少采集记录引用",
            STORE_NOT_BOUND: "店铺未绑定或已失效",
            STRATEGY_NOT_ALLOWED: "所选策略与失败类型不匹配",
            INVALID_STRATEGY: "重发策略无效",
            EMPTY_OFFER_ID: "新 SKU 为空",
            INVALID_OFFER_ID_FORMAT: "新 SKU 格式不合法",
            DUPLICATE_IN_REQUEST: "本次请求中 SKU 重复",
            EXISTS_IN_LOCAL: "新 SKU 在本系统已存在",
            EXISTS_IN_OZON: "新 SKU 在 Ozon 店铺已存在",
            CHECK_OZON_FAILED: "校验 Ozon SKU 可用性失败",
            OFFER_ID_UNAVAILABLE: "新 SKU 不可用",
            RECOVERY_PLAN_BUILD_FAILED: "生成 SKU 建议失败",
            DUPLICATE_PUBLISH_ID: "请求中存在重复 publish_id",
            UNKNOWN: "未知原因"
          }
        },
        missingBatch: "缺少批次ID。",
        notStarted: "未开始",
        notSelected: "未选择",
        storeInactive: "已失效",
        openLink: "打开链接",
        failedCount: "{count} 个失败",
        storeFilter: {
          title: "选择店铺列",
          subtitle: "选择要在表格中显示的店铺列。",
          selected: "已选 {count} 个",
          selectAll: "全选",
          activeOnly: "仅启用",
          clear: "清空",
          save: "保存",
          empty: "至少选择 1 个店铺列。"
        },
        publishModal: {
          title: "发布该批次",
          subtitle: "选择要发布的店铺，并设置发布时间。",
          storeTitle: "选择店铺",
          selectAll: "全选",
          clear: "清空",
          noActiveStores: "还没有可用店铺，请先绑定或启用店铺。",
          storeEmpty: "请选择至少 1 个店铺用于发布。",
          scheduleTitle: "发布时间",
          scheduleNow: "立即发布",
          scheduleLater: "定时发布",
          scheduleHint: "时间以系统时区为准。",
          scheduleEmpty: "请选择定时发布的日期和时间。",
          scheduleInvalid: "定时发布时间无效。",
          submit: "提交",
          submitting: "提交中...",
          submitted: "已提交发布。",
          failed: "提交失败，请稍后重试。"
        },
        scheduleModal: {
          title: "管理排期",
          subtitle: "可取消或修改该店铺的定时发布。",
          storeTitle: "选择店铺",
          storePlaceholder: "请选择店铺",
          storeEmpty: "请选择店铺。",
          actionTitle: "操作",
          cancel: "取消排期",
          reschedule: "修改时间",
          timeTitle: "新时间",
          scheduleHint: "时间以系统时区为准。",
          scheduleEmpty: "请选择新的日期和时间。",
          scheduleInvalid: "新的发布时间无效。",
          submit: "提交",
          submitting: "提交中...",
          submitted: "排期已更新。",
          failed: "排期更新失败，请稍后再试。"
        },
        summary: {
          items: "商品数",
          completed: "已发布",
          failed: "发布失败",
          processing: "发布中",
          scheduled: "已排期"
        },
        table: {
          product: "商品",
          actions: "操作"
        }
      }
    },

    orders: {
      nav: "订单中心",
      title: "订单与采购中心",
      subtitle: "集中查看 Ozon 订单、站内履约进度，并快速回到 1688 原链接完成采购。",
      refresh: "刷新订单",
      retry: "重试",
      loading: "加载订单中...",
      loadFailed: "订单加载失败",
      loadFailedDetail: "当前无法获取最新订单列表，请稍后重试。",
      detailLoadFailed: "订单详情加载失败",
      detailLoadFailedDetail: "当前无法获取最新订单详情，请稍后重试。",
      loginTitle: "请先登录查看订单",
      loginDesc: "登录后即可加载 Ozon 订单并更新履约进度。",
      loginAction: "立即登录",
      emptyTitle: "暂无订单",
      emptyDesc: "待真实 Ozon 订单同步后，可在此处理采购与履约。",
      filteredEmptyTitle: "当前筛选下暂无订单",
      filteredEmptyDesc: "可清空部分筛选条件后查看其他订单。",
      detailEmptyTitle: "请选择一个订单",
      detailEmptyDesc: "从订单列表打开详情后，可查看采购回源链接与履约操作。",
      save: "保存履约信息",
      saving: "保存中...",
      saveSuccess: "履约信息已保存。",
      saveFailed: "履约信息保存失败。",
      itemSave: "保存商品进度",
      itemSaving: "保存商品中...",
      itemSaveSuccess: "商品履约进度已更新。",
      itemSaveFailed: "商品履约进度更新失败。",
      shipToOzon: "回写 Ozon 发货",
      shippingToOzon: "回写发货中...",
      shipToOzonSuccess: "物流号与发货状态已同步到 Ozon。",
      shipToOzonFailed: "同步 Ozon 发货失败。",
      shipMissingStore: "当前订单缺少店铺上下文，暂时无法回写发货。",
      productIdCopied: "1688 商品 ID 已复制。",
      productIdCopyFailed: "复制 1688 商品 ID 失败。",
      matchedSource: "已匹配货源",
      unmatchedSource: "未匹配货源",
      unknown: "未知",
      results: "显示 {visible} / {total} 条订单",
      itemsTitle: "订单商品",
      itemsHint: "查看当前订单的商品快照，以及匹配到的 offer/SKU 信息。",
      itemsCount: "{count} 件商品",
      moreItems: "还有 {count} 件商品",
      noItems: "当前暂无商品快照。",
      quickActionsTitle: "快速采购操作",
      quickActionsHint: "可直接打开 1688 原链接、复制供应商商品 ID，或查看 Ozon 发布页。",
      fulfillmentPanelTitle: "站内履约台",
      fulfillmentPanelHint: "在当前页面维护采购、到货与发货状态，无需离开工作台。",
      timelineTitle: "履约时间线",
      detailTitle: "订单详情",
      trackingPlaceholder: "填写内部物流号；回写 Ozon 发货时会同步该值",
      itemNotePlaceholder: "记录该商品的采购备注或异常说明",
      notePlaceholder: "记录采购备注、供应商反馈或异常说明",
      filters: {
        store: "店铺",
        allStores: "全部店铺",
        ozonStatus: "Ozon 状态",
        allStatuses: "全部 Ozon 状态",
        fulfillmentStatus: "履约状态",
        allFulfillmentStatuses: "全部履约状态",
        search: "搜索",
        searchPlaceholder: "搜索 posting number、offer ID、1688 ID、SKU 或商品名",
        clear: "清空筛选"
      },
      actions: {
        open1688: "打开 1688 原链接",
        copyProductId: "复制 1688 ID",
        viewPublish: "查看 Ozon 页面",
        openOrderCenter: "查看订单中心",
        goPurchase: "去 1688 采购"
      },
      labels: {
        postingNumber: "订单号",
        store: "店铺",
        totalPrice: "订单金额",
        inProcessAt: "创建时间",
        shipmentDate: "最晚发货时间",
        substatus: "子状态",
        noSubstatus: "暂无子状态",
        offerId: "Offer ID",
        noOfferId: "暂无匹配 Offer ID",
        productId: "1688 ID",
        noProductId: "暂无匹配 1688 ID",
        collectionId: "Collection ID",
        matchSource: "匹配结果",
        fulfillmentStatus: "履约状态",
        ozonTrackingNumber: "Ozon 物流号",
        trackingNumber: "内部物流号",
        note: "备注"
      },
      timeline: {
        purchasedAt: "已采购时间",
        receivedAt: "已到货时间",
        readyToShipAt: "待发货时间",
        shippedAt: "已发货时间",
        ozonShipmentDate: "Ozon 发货截止"
      },
      fulfillmentStatus: {
        pending_purchase: "待采购",
        purchased: "已采购",
        received: "已到货",
        ready_to_ship: "待发货",
        shipped: "已发货",
        issue: "异常"
      }
    },

    import: {
      nav: "导入产品",
      title: "导入产品来源",
      subtitle: "连接您的供应商链接以开始 AI 优化。",
      extActive: "浏览器扩展活跃",
      connected: "已连接",
      extActiveDesc: "伴侣扩展活跃。您可以直接从供应商导入。",
      extSettings: "扩展设置",
      extMissing: "需要扩展",
      extMissingDesc: "请安装伴侣扩展以在本地抓取产品数据。",
      extUpgradeRequiredDesc: "当前插件版本 {current}，请升级到 {required} 或更高版本后继续。",
      extVersionUnknown: "未知版本",
      extUpdateBtn: "更新扩展",
      extRecheckBtn: "我已更新",
      extRecheckGuide: "正在重新检测插件状态...",
      downloadBtn: "下载扩展",
      downloadGuide: "正在打开扩展下载页...",
      urlLabel: "产品 URL",
      urlSub: "支持速卖通、1688、亚马逊、Shopify 等。",
      urlPlaceholder: "在此粘贴您的产品链接...",
      urlRequired: "请先填写产品链接。",
      authRequired: "请先登录后再开始采集。",
      guestFailed: "无法创建游客体验，请稍后重试。",
      consent: {
        title: "继续前请确认",
        subtitle: "为创建游客会话并继续导入，请先阅读并同意服务条款与隐私政策。",
        note: "你可以先以游客身份体验；我们将仅为提供导入与 AI 优化服务处理相关数据。",
        cancel: "取消",
        continue: "同意并继续"
      },
      importBtn: "导入",
      submitting: "正在提交...",
      collectFailed: "提交采集任务失败。",
      uploadTitle: "上传媒体资产",
      uploadSub: "将图片、视频或 ZIP 文件夹拖放到此处",
      chooseFiles: "选择文件",
      alternative: "替代方式",
      targetGlobal: "目标：全球市场",
      taskCenterEntry: "任务中心",
      taskCenterHint: "登录后可在任务中心查看进度。",
      recent: {
        title: "最近任务",
        subtitle: "最新导入任务：{status}",
        taskId: "任务ID",
        source: "来源链接",
        createdAt: "创建时间",
        statusHint: "采集完成后会在任务中心通知。",
        continue: "继续查看",
        hint: {
          processing: "采集中，请保持页面打开或前往任务中心查看进度。",
          queued: "任务已进入队列，即将开始采集。",
          completed: "采集已完成，可继续预览并配置生成设置。",
          failed: "采集失败，可在任务中心查看原因并重试。",
          generating: "AI 生成中，可前往任务中心查看进度。",
          publishing: "发布中，可查看发布进度。",
          publishFailed: "发布失败，请查看发布结果并按人工复核建议处理。",
          ready: "生成完成，可去发布。",
          uploaded: "已发布，可在资产库查看。"
        },
        viewTaskCenter: "查看任务中心",
        loginToView: "登录查看",
        status: {
          processing: "处理中",
          queued: "排队中",
          completed: "已完成",
          failed: "失败"
        }
      },
      success: {
        title: "任务已创建",
        subtitle: "我们已开始采集商品数据。您可以继续预览或前往任务中心跟踪进度。",
        taskId: "任务ID",
        source: "来源链接",
        status: "状态",
        statusProcessing: "处理中",
        statusHint: "采集正在后台执行。",
        continue: "继续导入",
        goTaskCenter: "前往任务中心",
        loginTaskCenter: "登录查看任务",
        loginHint: "登录后可在任务中心查看进度。"
      }
    },

    // 基本信息设置
    generalInfo: {
      title: "基本信息",
      subtitle: "管理您的账户详情和个人资料。",
      proPlan: "专业版",
      upgradePlan: "升级套餐",
      uploadNew: "上传新头像",
      remove: "移除",
      photoHint: "建议尺寸不小于 400x400 像素，支持 JPG 或 PNG 格式。",
      fullName: "姓名",
      fullNamePlaceholder: "例如：张三",
      professionalEmail: "工作邮箱",
      emailPlaceholder: "name@company.com",
      emailReadonlyHint: "邮箱暂不可修改。",
      companyName: "公司名称",
      companyPlaceholder: "请输入公司名称",
      cancel: "取消",
      saveChanges: "保存更改",
      saving: "保存中...",
      savedChanges: "已保存",
      saveFailed: "保存失败。",
      avatarUploadFailed: "头像上传失败。",
      avatarUpdated: "头像已更新。",
      avatarRemoved: "头像已移除。",
      footer: "平台设置"
    },

    // 账单与积分
    billing: {
      title: "账单与积分",
      subtitle: "查看积分余额、图片扣费规则与完整账单记录。",
      currentBalance: "当前余额",
      credits: "积分",
      balanceHint: "订阅积分每个计费周期清零，充值积分永久有效。",
      topUp: "充值",
      subscriptionPlan: "订阅套餐",
      proPlanPrice: "专业版 · 每月560积分（$49）",
      nextRenewal: "下次续费：",
      noPlan: "未订阅",
      noPlanHint: "暂无订阅",
      loading: "正在加载账单信息...",
      loadFailed: "账单信息加载失败。",
      emptyHistory: "暂无账单记录。",
      paymentSuccess: "订阅支付完成",
      paymentCanceled: "订阅支付已取消",
      rechargeSuccess: "充值支付完成",
      rechargeCanceled: "充值支付已取消",
      refresh: "刷新",
      syncing: "同步中...",
      summaryTotal: "总积分",
      summarySubscription: "订阅积分",
      summaryRecharge: "充值积分",
      summaryTrial: "试用积分",
      statusActive: "已生效",
      statusInactive: "未生效",
      badgeSubscription: "订阅",
      badgeRecharge: "充值",
      badgeAdjustment: "调整",
      badgeRefund: "退款",
      badgePromo: "赠送",
      badgeUsage: "用量",
      badgeOther: "其他",
      usageImageGenerate: "图片生成 · {count} 张",
      usageImageRefund: "图片生成退款 · {count} 张",
      managePlan: "管理套餐",
      billingHistory: "账单记录",
      date: "日期",
      description: "描述",
      amount: "金额",
      invoice: "发票",
      proSubscription: "专业版订阅（560 积分）",
      creditTopUp: "积分充值 (300 积分)",
      downloadAll: "导出账单记录",
      exportRow: "导出此行",
      exportSuccess: "账单记录已导出。",
      exporting: "导出中...",
      footer: "所有价格以美元计价，订阅积分每个计费周期清零，充值积分永久有效。",
      footerCopyright: "财务运营中心",
      pricingTitle: "计费规则",
      ruleImagePrice: "图片生成 {creditsPerImage} 积分/张（${usdPerImage}）。",
      ruleExchange: "1 积分 = ${usdPerCredit}。",
      ruleRollover: "订阅积分每个计费周期清零，充值积分永久有效。",
      ruleGift: "注册赠送 {registerGiftCredits} 积分，游客赠送 {guestTrialCredits} 积分。",
      imageUsageTitle: "图片模式消耗（近 {days} 天）",
      imageUsageHint: "仅统计扣费的图片生成记录；退款会在明细中单独展示。",
      imageUsageTotal: "总计",
      imageUsageUnknownHint: "其他：{images} 张 · {credits} 积分。",
      faqTitle: "常见问题",
      faq: {
        q1: "为什么只对图片收费？",
        a1: "图片生成是主要成本项，其它模块免费以保持定价透明。",
        q2: "订阅积分会结转吗？",
        a2: "不会，订阅积分每个计费周期清零；充值积分永久有效。",
        q3: "生成失败会退积分吗？",
        a3: "会，系统会根据失败记录自动退还积分。",
        q4: "订阅期间还能充值吗？",
        a4: "可以，充值积分叠加到订阅积分之上。",
      }
    },

    purchaseCredits: {
      title: "充值积分",
      subtitle: "选择充值包用于图片生成（{plan} · {creditsPerImage} 积分/张）。",
      priceHint: "1 积分 = ${usdPerCredit}，充值积分永久有效。",
      planSectionTitle: "图片生成模式",
      planThirdParty: "超值模式",
      planThirdPartyDesc: "合作渠道 · 适合日常批量生成，单张消耗更少积分。",
      planOfficial: "官方模式",
      planOfficialDesc: "适合关键任务，更稳定，单张消耗更高。",
      planRecommended: "推荐",
      planUnavailable: "该模式暂不可用，请联系管理员处理。",
      planUnavailableTag: "暂不可用",
      noChannelsHint: "当前未配置可用的 AI 渠道，请联系管理员在后台启用。",
      creditsUnitPerImage: "积分/张",
      planEstimateHint: "估算按每条 listing 约 {imagesPerListing} 张图（主图+详情图+场景/细节）计算，实际以你的配置为准。",
      bucketSubscription: "订阅赠送积分",
      bucketRecharge: "充值积分",
      bucketLegacy: "旧积分",
      creditsScopePrefix: "扣费范围：",
      creditsScopeSuffixThirdParty: "（扣费顺序：订阅 → 充值 → 旧积分）",
      creditsScopeSuffixOfficial: "（扣费顺序：充值优先；是否使用订阅/旧积分取决于系统设置）",
      summaryPlanLine: "当前模式：{plan} · {creditsPerImage} 积分/张",
      listingsLabel: "Listing",
      listingsUnit: "条 Listing",
      timeSaveHint: "预计可节省约 {hoursMin}-{hoursMax} 小时（仅供参考）。",
      laborHint: "人工制作一条 listing 通常需要 30–60 分钟；稳定批量一般需要专职人员（约 4,000–8,000 元/月）。",
      planCompareHint:
        "同样积分：超值模式≈{thirdPartyImages}张/{thirdPartyListings}条，官方模式≈{officialImages}张/{officialListings}条（按每条{imagesPerListing}张估算）。",
      planUpdateFailed: "更新图片生成模式失败。",
      stepPack: "选择套餐",
      stepPay: "选择支付",
      stepConfirm: "完成支付",
      creditsLabel: "积分",
      imagesLabel: "可生成",
      imagesUnit: "张",
      topUpLabel: "充值",
      bestValue: "最划算",
      saveLabel: "立省",
      summaryTitle: "当前选择",
      summaryPay: "支付方式",
      bonusLabel: "赠送",
      featureNeverExpire: "充值积分永久有效",
      featureBatch: "支持批量生成",
      featureNoBatch: "不支持批量生成",
      featurePriority: "优先支持",
      paymentTitle: "支付方式",
      purchaseButton: "完成支付",
      buttonProcessing: "支付处理中...",
      processing: "正在处理支付...",
      redirecting: "正在跳转到支付页面...",
      success: "支付成功，积分已到账。",
      paymentFailed: "发起支付失败，请稍后重试",
      payHint: "充值支持微信或支付宝，支付完成后积分即时到账。",
      acceptedMethods: "支持方式",
      visaLabel: "Visa 尾号 4242",
      visaExpire: "到期时间 12/26",
      wechat: "微信支付",
      wechatDesc: "快捷安全的移动支付",
      wechatPendingBadge: "待批准",
      wechatPendingTitle: "微信支付待 Stripe 审核",
      wechatPendingDesc: "Stripe 生产环境正在审核微信支付，暂不可用。请先使用支付宝完成充值。",
      wechatPendingToast: "微信支付暂不可用（待 Stripe 审核），请先使用支付宝。",
      alipay: "支付宝",
      alipayDesc: "全球支付解决方案",
      addNew: "添加新的支付方式",
      addNewDesc: "信用卡、微信支付或支付宝",
      security: "Stripe SSL 加密保障",
      total: "支付总额",
      payHintAlipayOnly: "当前仅支持支付宝充值；微信支付待 Stripe 审核，通过后将开放。",
      footer: "© {year} 一键大卖财务运营中心"
    },

    subscription: {
      title: "管理订阅套餐",
      subtitle: "选择符合图片生成需求的套餐。",
      currentPlanLabel: "当前套餐",
      currentPlanBadge: "当前套餐",
      activeTag: "生效中",
      inactiveTag: "未激活",
      noPlan: "未订阅",
      noPlanHint: "暂无订阅",
      nextBilling: "下次扣费日期：",
      nextBillingDate: "更新中...",
      activeCycle: "当前周期",
      monthly: "月付",
      yearly: "年付",
      yearlySave: "送 2 个月",
      billedMonthly: "按月计费",
      billedYearly: "按年计费（10个月价格）",
      rolloverHint: "订阅积分每个计费周期清零，充值积分永久有效。",
      creditsScopeHint: "当前图片模式：{plan} · {creditsPerImage} 积分/张。扣费范围：{scope}。",
      officialTopUpOnly: "默认不消耗订阅积分（需使用充值积分）",
      perMonth: "/月",
      perYear: "/年",
      plansEmpty: "暂无可用套餐。",
      planImagesPerMonth: "每月 {count} 张",
      planListingsPerMonth: "每月 {count} 条 Listing",
      planCreditsPerMonth: "每月 {count} 积分",
      planUnlimited: "不限量",
      planStarterDesc: "适合个人或刚起步团队。",
      planProDesc: "成长团队首选。",
      planBusinessDesc: "适合高频卖家与工作室。",
      featureStarter1: "基础批量生成",
      featureStarter2: "高清素材下载",
      featureStarter3: "标准支持（48 小时）",
      featurePro1: "高级批量生成",
      featurePro2: "高清 + 4K 素材下载",
      featurePro3: "优先支持（12 小时）",
      featurePro4: "专属增长经理",
      featureBusiness1: "高并发批量生成",
      featureBusiness2: "品牌素材包导出",
      featureBusiness3: "优先支持 + 成功经理",
      featureBusiness4: "流程自动化指导",
      downgrade: "降级套餐",
      downgradeSuccess: "已完成降级",
      upgrade: "升级套餐",
      manageInStripe: "前往 Stripe 管理",
      activePlan: "当前套餐",
      subscribe: "立即订阅",
      processing: "正在跳转到 Stripe 支付页...",
      paymentFailed: "创建支付失败",
      loading: "正在同步订阅状态...",
      buttonProcessing: "处理中...",
      cardHint: "订阅仅支持信用卡（Stripe）。",
      contactSales: "联系销售",
      customTitle: "需要定制方案？",
      customDesc: "我们可以根据你的产出与流程定制方案。",
      consultSales: "咨询销售",
      cancel: "取消订阅",
      cancelConfirm: "确定要取消订阅吗？",
      footer: "© {year} 一键大卖财务运营中心"
    },

    // 订阅方案设置
    subscriptionSettings: {
      title: "管理订阅方案",
      subtitle: "选择最适合你业务的套餐。",
      currentPlanLabel: "当前套餐：",
      currentPlan: "专业版",
      active: "已启用",
      nextBillingLabel: "下次扣费日期：",
      nextBillingDate: "更新中...",
      activeCycleLabel: "当前周期：",
      monthly: "月度",
      yearly: "年度",
      save20: "节省 20%",
      billedAnnually: "按年计费，享受最大优惠",
      billed: "按{cycle}计费",
      perMonth: "/月",
      plans: {
        starter: {
          title: "入门版",
          desc: "适合个人与小型创业团队。"
        },
        pro: {
          title: "专业版",
          desc: "最受成长型团队欢迎。"
        },
      },
      features: {
        starterCredits: "每月 500 积分",
        starterAgents: "基础 AI 代理权限",
        starterSupport: "邮件支持（48 小时内）",
        customIntegrations: "自定义集成",
        proCredits: "每月 2,000 积分",
        proAgents: "全部 AI 代理权限",
        proSupport: "优先支持（12 小时内）",
        proIntegrations: "最多 5 个集成",
      },
      buttons: {
        downgrade: "降级",
        activePlan: "当前方案",
        contactSales: "联系销售",
        consultSales: "咨询销售",
        cancelSubscription: "取消订阅"
      },
      currentBadge: "当前方案",
      custom: {
        title: "需要定制方案？",
        desc: "我们的销售专家可为高频业务量身定制解决方案。"
      },
      alerts: {
        downgrade: "正在降级到入门版...",
        contactSales: "正在联系销售团队...",
        openChat: "正在打开销售聊天...",
        cancelConfirm: "确定要取消吗？",
        cancelRequested: "已提交取消订阅请求。"
      }
    },

    // 安全设置
    securitySettings: {
      title: "安全",
      subtitle: "管理账号保护、认证方式与活跃会话，确保数据安全。",
      twoFactor: {
        title: "双重验证 (2FA)",
        desc: "启用后，新的设备登录需要验证码，提升账号安全。",
        comingSoon: "即将上线",
        available: "已开放"
      },
      password: {
        title: "密码管理",
        updated: "建议定期更新密码以确保账号安全。",
        reset: "重置密码"
      },
      sessions: {
        title: "活跃会话",
        subtitle: "以下是已登录设备。如不认识请撤销。",
        currentSession: "当前会话",
        lastLogin: "最近登录：",
        current: "当前",
        activeNow: "在线中",
        signOut: "退出登录",
        chromeDevice: "macOS 上的 Chrome",
        chromeLocation: "美国旧金山 • 192.168.1.1",
        iphoneDevice: "iPhone 15 Pro",
        iphoneLocation: "英国伦敦 • 2 天前"
      },
      cancel: "取消",
      saving: "保存中...",
      saved: "已保存",
      save: "保存更改",
      signOutConfirm: "确定要退出该设备吗？",
      signOutAlert: "正在退出该设备...",
      footer: "安全基础设施"
    },

    // 通知中心
    notificationsCenter: {
      title: "通知",
      subtitle: "这里会展示重要消息与任务结果。",
      tabInbox: "通知",
      tabSettings: "设置",
      unreadOnly: "只看未读",
      refresh: "刷新",
      markAllRead: "全部已读",
      loading: "正在加载通知...",
      loadingMore: "加载中...",
      loadMore: "加载更多",
      loadFailed: "加载通知失败。",
      emptyTitle: "暂无通知",
      emptyDesc: "当有重要进展（如发布成功/失败）时，会在这里提示。",
      delete: "删除",
      deleteConfirm: "确定要删除这条通知吗？",
      deleteFailed: "删除通知失败。",
      deleteDone: "已删除",
      markAllDone: "已全部标记为已读",
      markAllFailed: "标记已读失败。"
    },
    feedbackCenter: {
      title: "我的反馈",
      subtitle: "跟踪你提交的 Bug/需求在 AI 自动处理流程中的实时状态。",
      loading: "正在加载反馈...",
      refresh: "刷新",
      empty: "你还没有提交过反馈。",
      createdAt: "创建时间",
      updatedAt: "最近更新"
    },

    // 通知设置
    notificationsSettings: {
      title: "通知设置",
      subtitle: "管理系统更新和提醒的接收方式。",
      table: {
        type: "通知类型",
        email: "邮箱",
        push: "推送"
      },
      loading: "加载中...",
      loadFailed: "加载通知偏好失败。",
      saveFailed: "保存通知偏好失败。",
      pushComingSoon: "推送通知即将上线。",
      account: {
        title: "账号",
        desc: "账号变更与安全提醒"
      },
      payment: {
        title: "支付",
        desc: "支付回执与交易更新"
      },
      subscription: {
        title: "订阅",
        desc: "续费、账单与取消订阅通知"
      },
      quota: {
        title: "积分与额度",
        desc: "余额预警与用量提醒"
      },
      task: {
        title: "任务",
        desc: "导入、生成与发布进度提醒"
      },
      system: {
        title: "系统",
        desc: "平台公告、维护与安全通知"
      },
      listing: {
        title: "上架成功",
        desc: "商品发布时提醒"
      },
      credit: {
        title: "积分预警",
        desc: "余额低于 10% 时提醒"
      },
      updates: {
        title: "更新与发布",
        desc: "新功能与平台改进"
      },
      mute: {
        title: "静默全部通知",
        desc: "暂时关闭平台内所有提醒与邮件"
      },
      discard: "放弃更改",
      saving: "保存中...",
      saved: "偏好已保存",
      save: "保存偏好"
    },

    // 集成
    integrations: {
      title: "平台集成",
      subtitle: "连接您常用的工具和电商平台，优化工作流程。",
      loadFailed: "加载集成信息失败。",
      connected: "已连接",
      connect: "连接",
      shopify: "Shopify",
      shopifyDesc: "同步商品和库存数据。",
      amazon: "亚马逊卖家",
      amazonDesc: "导入商品列表并管理订单。",
      ozon: "Ozon",
      ozonDesc: "俄罗斯电商平台集成。",
      slack: "Slack",
      slackDesc: "在频道中接收实时通知。"
    },

    // 平台偏好
    platformPreferences: {
      title: "平台偏好设置",
      subtitle: "自定义默认的目标市场和 AI 智能体行为。",
      targetMarketplace: "默认目标市场",
      advancedHint: "需要更细的控制？请前往高级能力设置。",
      openAdvanced: "高级设置",
      loading: "加载中...",
      primaryPlatform: "主要平台",
      targetCountry: "目标国家",
      russia: "俄罗斯",
      us: "美国",
      unitedStates: "美国",
      uk: "英国",
      unitedKingdom: "英国",
      germany: "德国",
      france: "法国",
      italy: "意大利",
      spain: "西班牙",
      japan: "日本",
      brazil: "巴西",
      canada: "加拿大",
      mexico: "墨西哥",
      australia: "澳大利亚",
      singapore: "新加坡",
      india: "印度",
      turkey: "土耳其",
      uae: "阿联酋",
      saudi: "沙特阿拉伯",
      netherlands: "荷兰",
      sweden: "瑞典",
      poland: "波兰",
      belgium: "比利时",
      ireland: "爱尔兰",
      southAfrica: "南非",
      egypt: "埃及",
      global: "全球",
      aiPersona: "AI 写作风格",
      poweredBy: "由 LexiCopy 驱动",
      persuasive: "营销说服型",
      professional: "专业严谨型",
      minimalist: "简约精炼型",
      seoFocused: "SEO 优先型",
      personaHint: "此风格将默认应用于所有新的产品描述和广告文案。",
      brandProtection: "自动品牌保护",
      brandGuard: "品牌守护",
      brandGuardDesc: "生成时自动过滤品牌词，降低侵权风险",
      brandName: "默认品牌名称",
      brandPlaceholder: "输入用于水印的品牌名称...",
      autoWatermark: "自动添加 AI 水印",
      autoWatermarkDesc: "为所有新生成的内容添加品牌标识保护",
      imagePreferences: "图片处理偏好",
      deepEnhancement: "深度图像增强",
      deepEnhancementDesc: "AI 超分辨率和清晰度提升",
      autoBgRemoval: "自动去除背景",
      autoBgRemovalDesc: "生成电商平台规范的干净素材",
      autoSaveHint: "更改将自动保存到您的工作区配置。",
      saveHint: "更改将应用于新任务，保存后同步到工作区配置。",
      resetDefaults: "恢复默认设置",
      saveChanges: "保存更改",
      saving: "保存中...",
      savedChanges: "已保存",
      saveFailed: "保存偏好失败。",
      footer: "全球工作区"
    },

    // 资产管理
    assets: {
      nav: "资产库",
      title: "资产运营库",
      subtitle: "专注已完成商品与发布链接运营。任务调度请前往任务中心。",
      loading: "加载资产中...",
      loadFailed: "加载资产失败。",
      loginTitle: "请先登录查看资产库",
      loginDesc: "登录后可管理生成的商品信息与上传记录。",
      loginAction: "登录",
      emptyTitle: "暂无资产",
      emptyHint: "请先到任务中心查看进行中任务，完成后会进入资产库。",
      emptyAction: "前往任务中心",
      newOptimization: "导入新商品",
      goTaskCenter: "任务中心",
      focusTitle: "资产库仅展示可运营结果",
      focusHint: "发布链接、成品素材在此管理；进行中或失败任务请回到任务中心处理。",
      platformAll: "平台：全部",
      statusPublished: "状态：已发布",
      statusAll: "状态：全部",
      progressLabel: "进度",
      last30Days: "近 30 天",
      last7Days: "近 7 天",
      last90Days: "近 90 天",
      allPeriod: "全部时间",
      clearFilters: "清除筛选",
      score: "评分",
      published: "已发布",
      draft: "草稿",
      failed: "失败",
      retry: "重试",
      edit: "编辑",
      view: "查看",
      viewProgress: "查看进度",
      goToPublish: "去发布",
      fixError: "去修复",
      openListing: "打开商品",
      noPublishUrl: "暂无商品链接",
      moreActionsComingSoon: "更多操作正在开发中。",
      addNewListing: "添加新商品",
      addNewDesc: "上传原始图片进行 AI 增强。",
      assetsSelected: "已选资产",
      batchDownload: "批量下载",
      batchSync: "批量同步",
      delete: "删除",
      actions: {
        copyPublishLink: "复制发布链接",
        openPublishLink: "打开发布链接",
        deleteRecord: "删除记录",
        deleteTitle: "删除记录",
        deleteConfirmMessage: "确定要删除这条记录吗？此操作不可撤销。",
        deleted: "已删除。",
        deleteFailed: "删除失败。",
        publishLinkCopied: "发布链接已复制。",
        publishLinkCopyFailed: "复制失败，请重试。"
      },
      showingAssets: "显示第 {from} 至 {to} 项，共 {total} 项资产",
      searchPlaceholder: "搜索资产..."
    },

    // 导入预览
    importPreview: {
      nav: "商品预览",
      breadcrumb: "工作区",
      collectStatus: "采集状态",
      collectQueued: "排队中",
      collecting: "采集中",
      collectCompleted: "已完成",
      collectFailed: "采集失败",
      collectingHint: "插件正在后台采集数据。",
      collectProgressTitle: "采集进度",
      collectProgressHint: "正在与插件同步，请在此查看实时进度。",
      progressLabel: "进度",
      stepQueued: "等待插件接收",
      stepAssigned: "插件已接收",
      stepCollecting: "采集中",
      stepUploading: "上传中",
      stepCompleted: "采集完成",
      currentStage: "当前阶段",
      stage: {
        pending: "等待插件接收",
        assigned: "任务已分配",
        preparing: "准备页面",
        scrolling: "滚动页面",
        collecting: "采集中",
        saving: "保存数据",
        uploading: "上传资源",
        cached: "加载缓存结果",
        completed: "已完成"
      },
      authRequired: "请先登录后查看采集进度。",
      pollFailed: "获取采集进度失败。",
      previewFailed: "加载采集数据失败。",
      retryCollect: "重新采集",
      attributeList: "其他属性",
      detailHighlights: "详情要点",
      noHighlights: "暂无可用的详情要点。",
      skuStructure: "SKU 结构",
      skuStock: "库存",
      skuVariant: "规格",
      skuSyncing: "正在同步 SKU 选择...",
      skuAnchorSyncing: "正在同步 SKU 锚点图...",
      skuLockedByUser: "已手动锁定",
      skuGuideTitle: "SKU 选择指引",
      skuGuideStepSelect: "1. 先选择你要发布的 SKU 规格。",
      skuGuideStepAnchor: "2. 可选锁定 1 张锚点图，提升一致性。",
      skuGuideStepStart: "3. 确认后再开始 AI 优化。",
      skuSelectionNudgeTitle: "建议先锁定 SKU 再生成",
      skuSelectionNudgeDesc: "多 SKU 商品建议手动锁定 SKU，可显著降低参考图与成图不一致风险。",
      skuSelectionNudgeAction: "现在去选择 SKU",
      skuRecommendationTitle: "AI 推荐 SKU",
      skuRecommendationHint: "基于模型识别结果生成，请确认后再应用。",
      skuRecommendationLoading: "AI 正在分析 SKU 推荐...",
      skuRecommendationRefresh: "刷新推荐",
      skuRecommendationRefreshing: "刷新中...",
      skuRecommendationFailed: "SKU 推荐加载失败。",
      skuRecommendationEmpty: "模型未返回有效 SKU 推荐。",
      skuRecommendationApply: "应用推荐",
      skuRecommendationApplied: "已应用",
      skuRecommendationPendingApply: "AI 推荐已就绪，可一键应用。",
      skuRecommendationConfidence: "置信度 {confidence}%",
      skuRecommendationSourceLabel: "推荐来源：{source}",
      skuRecommendationSourceModel: "模型",
      skuRecommendationSourceCache: "缓存",
      skuRecommendationSourceFallback: "回退",
      skuRecommendationNoReason: "该推荐暂无模型理由说明。",
      skuSelectionSyncFailed: "SKU 选择同步失败，请重试。",
      skuAnchorSyncFailed: "SKU 锚点图同步失败，请重试。",
      skuAnchorMissingHint: "当前尚未锁定 SKU 锚点图，系统将回退到文字匹配。",
      skuAnchorLocked: "已锁定 SKU 锚点图：#{index}",
      skuAnchorSelectHint: "可手动选择 1 张 SKU 锚点图（推荐）：",
      skuAnchorOptionTitle: "锁定为 SKU 锚点图 #{index}",
      skuAnchorSourceLabel: "锚点来源：{source}",
      skuAnchorExpand: "编辑锚点",
      skuAnchorCollapse: "收起",
      manualSkuReuseDisabledHint: "已手动锁定 SKU，为保证参考图一致性将跳过历史复用。",
      noSkuData: "暂无 SKU 结构数据。",
      productDetails: "商品详情",
      detailSummary: "详情摘要",
      noDetailSummary: "暂无详情摘要。",
      noImages: "暂无图片",
      previewImage: "预览图片",
      dataOverview: "采集概览",
      overviewImages: "图片数量",
      overviewSkus: "SKU 数量",
      overviewAttributes: "属性数量",
      overviewSections: "区块数量",
      overviewRawText: "原始文本长度",
      characters: "字符",
      noOverview: "暂无采集概览数据。",
      flags: "采集标记",
      noFlags: "暂无采集标记。",
      flagEnabled: "是",
      flagDisabled: "否",
      dimensionsInfo: "尺寸与重量",
      noDimensions: "暂无尺寸信息。",
      imageTags: "图片标签",
      noImageTags: "暂无图片标签信息。",
      rawText: "原始文本",
      noRawText: "暂无原始文本。",
      rawSections: "原始区块",
      validationWarning: "数据校验问题",
      issuesDetected: "个问题可能影响 AI 优化效果",
      manualReview: "需要人工审核",
      allResolved: "所有问题已解决",
      source: "来源",
      marketPricing: "市场定价分析",
      supplierPrice: "供应商价格 (1688)",
      priceRange: "价格区间",
      invalid: "无效",
      aiSuggest: "AI 建议",
      marketAvg: "市场均价",
      suggestedAiPrice: "AI 建议价格",
      profitMargin: "利润率",
      marginDesc: "扣除常规运费后预估净利润率 72.4%。",
      aiStrategy: "AI 优化策略",
      seoEnhancements: "SEO 增强",
      seoDesc: "将添加「矫正支撑」和「透气网面」等关键词，预计提升搜索曝光 35%。",
      imageRefinement: "图片优化",
      imageDesc: "AI 将去除 1688 水印，并为主图生成场景化背景。",
      marketPositioning: "市场定位",
      positionDesc: "定位「轻奢品质」细分市场，主打城市耐用性。",
      skuAttributes: "SKU 属性",
      colorVariants: "颜色款式",
      sizeAvailability: "尺码可选",
      currentStock: "当前库存",
      shippingEst: "预计发货",
      shippingLocal: "3-5 天 (本地)",
      weight: "重量",
      dimensions: "尺寸",
      missing: "缺失",
      aiAutofill: "AI 自动补全缺失数据",
      dataFilled: "AI 已补全数据",
      viewMetadata: "查看完整原始数据",
      lowRes: "低分辨率",
      dataRequired: "需要数据校验",
      readyOptimize: "已就绪，可优化",
      agentsPaused: "优化智能体已暂停",
      aiFillAll: "AI 补全所有缺失数据",
      saveDraft: "保存草稿",
      saved: "已保存",
      startOptimization: "开始 AI 优化",
      optimized: "已优化",
      fixIssues: "修复问题以提升 AI 效果",
      exportToStore: "导出到店铺",
      exporting: "导出中...",
      exported: "已导出",
      exportSuccess: "商品已成功导出到店铺！"
    },

    review: {
      nav: "AI 数据审核",
      validating: "AI 正在根据产品类目建议值。",
      validated: "AI 优化成功。所有字段已验证。",
      pending: "需要人工审核",
      completed: "审核完成",
      marketPricing: "市场定价比较",
      supplierPrice: "供应商价格",
      invalid: "无效",
      marketAvg: "市场均价",
      suggestedPrice: "AI 建议价格",
      aiGenerated: "AI 生成",
      marginAnalysis: "利润率分析",
      marginDesc: "本地化运费后估计净利润率为 72.4%。",
      strategy: "AI 优化策略",
      seoEnhance: "SEO 增强",
      seoDesc: "将添加优化关键词以提高可见性。",
      imgRefine: "图像优化",
      imgDesc: "AI 将去除水印并生成生活场景背景。",
      marketPos: "市场定位",
      marketPosDesc: "针对耐用性定位于'经济高端'细分市场。",
      skuAttrs: "SKU 属性",
      originalData: "原始数据",
      colorVariants: "颜色变体",
      sizeAvail: "尺寸可用性",
      stock: "当前库存",
      shipping: "运费估算",
      weight: "重量",
      dimensions: "尺寸",
      missing: "缺失",
      confirmBtn: "确认所有 AI 建议",
      autofillBtn: "AI 自动填充缺失数据",
      saveDraft: "保存草稿",
      applyAi: "应用 AI 更改",
      startAi: "开始 AI 优化",
      exportBtn: "导出到店铺"
    }
  }
};
